import {
  Amount,
  failure,
  isOk,
  isPending,
  ok,
  pending,
  Result,
  ShareValuationInformation,
} from "@/common/common";
import {
  BooleanFieldModel,
  createEmailField,
  LocalDateFieldModel,
  NumberFieldModel,
  RadioFieldModel,
  StringFieldModel,
} from "@/common/commonForms";
import { lookupShareTypeName, ShareType, ShareTypeCode } from "@/common/share";
import { DataCardProps } from "@/components-v2/DataCard";
import { RowAction } from "@/components-v2/DataTable";
import { TagColor, TagProps } from "@/components-v2/Tag";
import { DropdownMenuItems } from "@/components-v2/Dropdown";
import { IconName } from "@/components-v2/iconMapping";
import { RadioButtonGroupProps } from "@/components-v2/RadioButton";
import { getDecisionActionLabel, getDecisionIcon } from "@/dbot/common/commonDomain";
import {
  DCEvent,
  EventDispatcher,
  httpDelete,
  httpGet,
  httpPost,
  navigate,
  showAlert,
  showError,
  showGeneralError,
  thunk,
} from "@/events/eventsDomain";
import {
  excelUploadConfig,
  FileUploadConfig,
  toFormData,
  UploadedAttachment,
} from "@/fileUpload/fileUploadDomain";
import { Language } from "@/i18n/languages";
import { TFunction, TMessage, TMessageParam } from "@/i18n/tt";
import { TMessageKey } from "@/i18n/types.gen";
import { addMonths } from "date-fns/addMonths";
import { compact, groupBy, isEqual } from "lodash";
import { CapTable, capTableApiClient } from "@/shareholderRegister/capTable/capTableApiClient";
import { Actor, ActorType, SelectedActor } from "@/types/SelectActors";
import { ShareholderDTO } from "@/types/Shareholder";
import { toggleElement } from "@/utils/arrays";
import { DateValue, toDate, today, toLocalDateISOString } from "@/utils/Dates";
import { differenceInCalendarDays } from "date-fns/differenceInCalendarDays";
import { newUuid } from "@/utils/uuid";
import {
  adminProject,
  adminShareholdersTransaction,
  adminShareholdersTransactions,
} from "@/utils/navigation";
import {
  commandFailureMessage,
  EditExerciseRequestCommand,
  ExerciseOptionsCommand,
  ExerciseOptionsResult,
  ExerciseReceiver,
  ExerciseShareIssue,
  HolderHoldingCompanies,
  instrumentCommandApiClient,
  instrumentHolderApiClient,
  optionApiClient,
  OwnershipSource,
  RequestExerciseCommand,
  ReverseExerciseCommand,
  UpdateExerciseRequestCommand,
} from "./instrumentsApiClient";
import {
  InstrumentHolder,
  loadPortfolioActors,
  NewHolderForm,
  newHolderForm,
  NewHolderSpec,
  newHolderSpec,
  portfolioHolderSpec,
  QuickAddSuggestion,
  quickAddHolderSpec,
  validateNewHolderForm,
  holderIcon,
} from "./instrumentHolders";
import {
  companyReceiver,
  ExerciseReceiverChoice,
  exerciseReceiverName,
  holdingCompaniesOf,
  isSplitTranche,
  receiverCompany,
} from "./exerciseReceivers";
import {
  ExerciseIssueDraft,
  exerciseIssueFailureMessage,
  ExerciseIssueForm,
  exerciseIssueUrl,
  exerciseShareIssue,
  IssueReceiver,
  ShareCapitalFigures,
  shareIssueSummary,
  ShareIssueSummaryRow,
  validateExerciseIssueForm,
} from "./shareIssue";
import {
  foldHistory,
  fromTo,
  HistoryRow,
  HolderHistoryEntry,
  shareTypeClause,
} from "./instrumentHistory";
import { DecisionType } from "@/common/decisions";
import { shareholderViewApiClient } from "@/views/Pages/ShareholderView/shareholderViewApiClient";
import { projectApi } from "@/dbot/common/projectApi";
import { OptionGrantLetter } from "@/dbot/optionGrantLetter/optionGrantLetterDomain";
import {
  HoldingCompanyReceiver,
  OptionExercise,
  ProgramExercise,
} from "@/dbot/optionExercise/optionExerciseDomain";
import { AiPromptSource } from "@/components-v2/AiPrompt";

export type VestingFrequency = "MONTHLY" | "QUARTERLY" | "ANNUAL";

export type VestingDateEntry = {
  vestingDate: string;
  optionsAmount: number;
};

export type MilestoneScope = "INDIVIDUAL" | "COMPANY_WIDE";

export type VestingMilestone = {
  scope: MilestoneScope;
  title: string;
  criterion?: string;
  optionsAmount: number;
};

export type VestingConfig =
  | {
      vestingType: "PERIODIC";
      periodMonths: number;
      cliffMonths: number;
      frequency: VestingFrequency;
    }
  | { vestingType: "SPECIFIC_DATES"; entries: VestingDateEntry[] }
  | { vestingType: "PERFORMANCE"; milestones: VestingMilestone[] };

export type CancellationReason =
  | "RESIGNATION_GOOD_LEAVER"
  | "DISMISSAL_BAD_LEAVER"
  | "REDUNDANCY"
  | "RETIREMENT"
  | "DEATH_OR_DISABILITY"
  | "MUTUAL_AGREEMENT"
  | "FAILED_MILESTONE"
  | "OTHER";

// A cancellation folded from the ledger, mirroring the backend TrancheCancellation.
export type TrancheCancellation = {
  reason: CancellationReason;
  effectiveDate: string;
  vestedCancelled: number;
  unvestedCancelled: number;
  remarks?: string;
  attachments?: UploadedAttachment[];
};

// An acceleration folded from the ledger, mirroring the backend TrancheAcceleration.
export type TrancheAcceleration = {
  effectiveDate: string;
  unvestedAccelerated: number;
  remarks?: string;
  attachments?: UploadedAttachment[];
};

export type OptionTranche = {
  optionTrancheId: string;
  grantId: string;
  name?: string;
  shareTypeId?: number;
  totalOptions: number;
  strikePrice: number;
  grantDate: string;
  expirationDate: string;
  holderBearsPayrollTax: boolean;
  vesting: VestingConfig;
  remarks?: string;
  attachments?: UploadedAttachment[];
  cancellations?: TrancheCancellation[];
  accelerations?: TrancheAcceleration[];
  exercises?: TrancheExercise[];
  exerciseRequests?: TrancheExerciseRequest[];
};

export type ExerciseRequestNotification = {
  recipients: string[];
  subject: string;
  body: string;
};

export type ExerciseRequestStatusChange = {
  status: ExerciseRequestStatus;
  changedAt: string;
  changedBy?: string;
  changedByName?: string;
  remarks?: string;
  notification?: ExerciseRequestNotification;
};

export type TrancheExerciseRequest = {
  requestId: string;
  eventId: string;
  requestDate: string;
  count: number;
  status: ExerciseRequestStatus;
  exerciseId?: string;
  remarks?: string;
  history: ExerciseRequestStatusChange[];
  isHolderRequest?: boolean;
};

export type ReceiverOwnership = {
  organisationNumber: string;
  source: OwnershipSource;
  asOf?: string;
};

export type TrancheExercise = {
  exerciseId: string;
  exerciseDate: string;
  count: number;
  strikePrice: number;
  shareTypeId: number;
  receiverShareholderId: number;
  receiverName?: string;
  ownership?: ReceiverOwnership;
  isEmployee: boolean;
  isReversed: boolean;
  remarks?: string;
  attachments?: UploadedAttachment[];
  issue?: ExerciseIssue;
};

export type ExerciseIssue = {
  transactionLogId: number;
  issueDate: string;
  isPending: boolean;
};

export type OptionGrant = {
  optionGrantId: string;
  holderId: string;
  planId: string;
  registeredDate: string;
};

// Mirrors the backend instrument_event ledger. The grant's whole audit trail
// rides along with its tranches so the holder history can read it directly.
export type InstrumentEventPayload =
  | {
      payloadType: "GRANT_REGISTERED";
      planId: string;
      shareTypeId?: number;
      totalOptions: number;
      strikePrice: number;
      grantDate: string;
      expirationDate: string;
      vesting: VestingConfig;
    }
  | {
      payloadType: "GRANT_MODIFIED";
      planId: string;
      shareTypeId?: number;
      totalOptions: number;
      strikePrice: number;
      grantDate: string;
      expirationDate: string;
    }
  | { payloadType: "VESTING_MODIFIED"; vesting: VestingConfig }
  | {
      payloadType: "CANCELLATION_REGISTERED";
      reason: CancellationReason;
      vestedCancelled: number;
      unvestedCancelled: number;
      attachmentReference?: string;
    }
  | {
      payloadType: "ACCELERATION_REGISTERED";
      unvestedAccelerated: number;
      attachmentReference?: string;
    }
  | {
      payloadType: "EXERCISE_REGISTERED";
      exerciseId: string;
      count: number;
      strikePrice: number;
      shareTypeId: number;
      receiverShareholderId: number;
      receiverName?: string;
      ownership?: ReceiverOwnership;
      isEmployee: boolean;
      attachmentReference?: string;
      requestId?: string;
    }
  | { payloadType: "EXERCISE_REVERSED"; exerciseId: string }
  | { payloadType: "EXERCISE_REQUESTED"; requestId: string; count: number }
  | {
      payloadType: "EXERCISE_REQUEST_MODIFIED";
      requestId: string;
      requestDate: string;
      count: number;
    }
  | {
      payloadType: "EXERCISE_REQUEST_STATUS_CHANGED";
      requestId: string;
      status: ExerciseRequestStatus;
    };

export type ExerciseRequestStatus =
  | "REQUESTED"
  | "ACKNOWLEDGED"
  | "REJECTED"
  | "WITHDRAWN"
  | "DELETED"
  | "EXERCISED";

export type InstrumentEvent = {
  instrumentEventId: string;
  trancheId?: string;
  effectiveDate: string;
  recordedAt: string;
  rationale?: string;
  payload: InstrumentEventPayload;
};

export type OptionGrantDetails = {
  grant: OptionGrant;
  tranches: OptionTranche[];
  events?: InstrumentEvent[];
};

export type OptionPoolChange = {
  eventId: string;
  planId: string;
  effectiveDate: string;
  poolSize: number;
  comment?: string;
  recordedAt: string;
};

export type ExerciseWindow = {
  fromDate: string;
  toDate: string;
};

export type ExerciseRequestPolicy = "DISABLED" | "ANY_TIME" | "WINDOWS_ONLY";

export type ExerciseRequestTiming = Exclude<ExerciseRequestPolicy, "DISABLED">;

export type OptionPlan = {
  optionPlanId: string;
  name: string;
  termsReference?: string;
  description?: string;
  vestingTemplate?: VestingConfig;
  expirationDate?: string;
  exerciseWindows?: ExerciseWindow[];
  exerciseRequestPolicy?: ExerciseRequestPolicy;
  poolChanges?: OptionPoolChange[];
  attachments?: UploadedAttachment[];
};

export type ShareholderOptionsView = {
  holder: InstrumentHolder;
  plans: OptionPlan[];
  grants: OptionGrantDetails[];
};

export type RegisterGrantStep = "holder-details" | "grant" | "vesting-schedule";

// Shared by every holder-selection path (new holder, quick add, portfolio):
// once a holder is known, the wizard continues with this form.
export type GrantForm = {
  plan: RadioFieldModel<OptionPlan | undefined>;
  shareType: RadioFieldModel<ShareType | undefined>;
  numberOfOptions: NumberFieldModel;
  strikePrice: NumberFieldModel;
  grantDate: LocalDateFieldModel;
  expirationDate: LocalDateFieldModel;
  remarks: StringFieldModel;
};

export type VestingType = "TIME_VESTED" | "PERFORMANCE";

// "SPECIFIC_DATES" lives in the frequency select but switches the whole
// schedule from computed to hand-entered dates.
export type FrequencyChoice = VestingFrequency | "SPECIFIC_DATES";

export type SpecificDateRow = {
  id: string;
  date: LocalDateFieldModel;
  options: NumberFieldModel;
};

// Scope, title and criterion are fixed when a milestone is added; only the
// option allocation stays editable in the list, so just it is a field model.
export type MilestoneRow = {
  id: string;
  scope: MilestoneScope;
  title: string;
  criterion?: string;
  options: NumberFieldModel;
};

export type DurationUnit = "MONTHS" | "YEARS";

export type DurationFields = {
  amount: NumberFieldModel;
  unit: RadioFieldModel<DurationUnit>;
};

export type VestingForm = {
  vestingType: RadioFieldModel<VestingType>;
  period: DurationFields;
  frequency: RadioFieldModel<FrequencyChoice>;
  cliff: DurationFields;
  specificDates: SpecificDateRow[];
  milestones: MilestoneRow[];
  // The "add milestone" sub-form; committed into `milestones` by addMilestone.
  showAddMilestone: boolean;
  addMilestoneScope: RadioFieldModel<MilestoneScope>;
  addMilestoneTitle: StringFieldModel;
  addMilestoneCriterion: StringFieldModel;
};

// Nothing persists until Save: the wizard carries the holder either as an
// existing holderId (quick add), a pendingHolder spec (portfolio pick) or a
// holderForm to fill in (new holder) — the holder-details step only shows for
// the last of these. Save sends holder, grant and vesting in one command.
export type RegisterGrantState =
  | { view: "quick-add" }
  | {
      view: "wizard";
      step: RegisterGrantStep;
      holderForm?: NewHolderForm;
      grantForm: GrantForm;
      vestingForm: VestingForm;
      holderId?: string;
      pendingHolder?: NewHolderSpec;
      holderName?: string;
      holderStepCompleted?: boolean;
      grantStepCompleted?: boolean;
      showAllVestingEntries?: boolean;
      submitResult?: Result<TMessage, TMessage>;
    };

export type GrantWizard = Extract<RegisterGrantState, { view: "wizard" }>;

// Editing reuses the wizard's grant form against an already-registered grant;
// the backend appends a modification event to the ledger on save.
export type EditGrantState = {
  grantId: string;
  trancheId: string;
  holderName: string;
  grantForm: GrantForm;
  submitResult?: Result<TMessage, TMessage>;
};

// Same idea for the vesting schedule: the wizard's vesting step against a
// registered tranche. The grant form carries the registered terms read-only —
// the schedule computation needs them (total options, grant date, plan).
export type EditVestingState = {
  grantId: string;
  trancheId: string;
  holderName: string;
  grantForm: GrantForm;
  vestingForm: VestingForm;
  showAllVestingEntries?: boolean;
  submitResult?: Result<TMessage, TMessage>;
};

// The grant a delete is about to wipe, held while the confirmation is open. The
// terms are copied in so the dialog can name what is being deleted after the row
// itself is gone from the reloaded register.
export type DeleteGrantState = {
  grantId: string;
  trancheId: string;
  holderName: string;
  program: TMessage;
  totalOptions: number;
  submitResult?: Result<TMessage, TMessage>;
};

export type CancellationGrantOption = {
  trancheId: string;
  grantId: string;
  holderName: string;
  planName: string;
  vestedAvailable: number;
  unvestedAvailable: number;
  isPerformance: boolean;
};

export type CancellationState = {
  grantOptions: CancellationGrantOption[];
  selectedTrancheId: string;
  activeTab: "cancellation" | "schedule";
  attachmentReference: string;
  reason: RadioFieldModel<CancellationReason>;
  effectiveDate: LocalDateFieldModel;
  vestedToCancel: NumberFieldModel;
  unvestedToCancel: NumberFieldModel;
  remarks: StringFieldModel;
  stagedFiles: StagedFile[];
  submitResult?: Result<TMessage, TMessage>;
};

export type AccelerationGrantOption = {
  trancheId: string;
  grantId: string;
  holderName: string;
  planName: string;
  unvestedAvailable: number;
};

export type AccelerationState = {
  grantOptions: AccelerationGrantOption[];
  selectedTrancheId: string;
  // Opened from a grant's own menu — the grant is fixed and shown in the title,
  // so the picker is hidden. Opened company-wide, the admin picks it here.
  preselected: boolean;
  activeTab: "acceleration" | "schedule";
  attachmentReference: string;
  effectiveDate: LocalDateFieldModel;
  unvestedToAccelerate: NumberFieldModel;
  remarks: StringFieldModel;
  stagedFiles: StagedFile[];
  submitResult?: Result<TMessage, TMessage>;
};

// A dialog so the first program can be created without leaving the
// grant registration.
export type ExerciseWindowRow = {
  id: string;
  fromDate: LocalDateFieldModel;
  toDate: LocalDateFieldModel;
  hasStarted: boolean;
};

export type CreatePlanState = {
  name: StringFieldModel;
  description: StringFieldModel;
  expirationDate: LocalDateFieldModel;
  poolSize: NumberFieldModel;
  poolEffectiveDate: LocalDateFieldModel;
  allowExerciseRequests: BooleanFieldModel;
  exerciseRequestTiming: RadioFieldModel<ExerciseRequestTiming>;
  exerciseWindows: ExerciseWindowRow[];
  exerciseWindowsError?: TMessage;
  stagedFiles: StagedFile[];
  submitResult?: Result<TMessage, TMessage>;
};

export type EditPlanState = {
  plan: OptionPlan;
  name: StringFieldModel;
  description: StringFieldModel;
  expirationDate: LocalDateFieldModel;
  poolSize: NumberFieldModel;
  poolEffectiveDate: LocalDateFieldModel;
  poolComment: StringFieldModel;
  allowExerciseRequests: BooleanFieldModel;
  exerciseRequestTiming: RadioFieldModel<ExerciseRequestTiming>;
  exerciseWindows: ExerciseWindowRow[];
  exerciseWindowsError?: TMessage;
  submitResult?: Result<TMessage, TMessage>;
};

export type OptionsTab = "holders" | "programs" | "exercises";

export type OptionsPageState = {
  orgNo?: string;
  holdersResult?: Result<InstrumentHolder[]>;
  plansResult?: Result<OptionPlan[]>;
  grantsResult?: Result<OptionGrantDetails[]>;
  searchModel?: StringFieldModel;
  exercisesFilter?: ExercisesFilter;
  expandedHolderIds?: string[];
  registerGrant?: RegisterGrantState;
  editGrant?: EditGrantState;
  editVesting?: EditVestingState;
  editPlanVesting?: EditPlanVestingState;
  cancellation?: CancellationState;
  acceleration?: AccelerationState;
  isCreatingGrantLetter?: boolean;
  exercise?: ExerciseState;
  exerciseIssue?: ExerciseIssueDraft;
  reverseExercise?: ReverseExerciseState;
  exerciseRequestForm?: ExerciseRequestFormState;
  exerciseRequestReview?: ExerciseRequestReviewState;
  deleteExerciseRequest?: DeleteExerciseRequestState;
  sentNotification?: SentNotificationState;
  holderRequestIndex?: Record<string, number>;
  holderRequestsCollapsed?: boolean;
  deleteGrant?: DeleteGrantState;
  createPlan?: CreatePlanState;
  editPlan?: EditPlanState;
  capTableResult?: Result<CapTable>;
  attachmentPreview?: { filename: string; url: string };
  quickAddActors?: Actor[];
};

export const updateOptionsState = (
  f: (state?: OptionsPageState) => Partial<OptionsPageState>,
): DCEvent => ({
  type: "update-store",
  data: {
    optionsState: (state) => ({
      ...state,
      ...f(state),
    }),
  },
});

const updateOptionsStateFor = (
  orgNo: string,
  f: (state: OptionsPageState) => Partial<OptionsPageState>,
): DCEvent => ({
  type: "update-store",
  data: {
    optionsState: (state) => (state?.orgNo === orgNo ? { ...state, ...f(state) } : state),
  },
});

export const clearOptionsState = (): DCEvent => ({
  type: "update-store",
  data: {
    optionsState: (state) =>
      state?.exerciseIssue === undefined ? undefined : { exerciseIssue: state.exerciseIssue },
  },
});

const loadHolders = (orgNo: string): DCEvent =>
  httpGet({
    url: `/instruments/${orgNo}/holders`,
    onSuccess: (res) => [updateOptionsStateFor(orgNo, () => ({ holdersResult: ok(res.data) }))],
    onFailure: () => [
      showGeneralError(),
      updateOptionsStateFor(orgNo, () => ({ holdersResult: failure() })),
    ],
  });

const loadGrants = (orgNo: string): DCEvent =>
  httpGet({
    url: `/instruments/${orgNo}/options/grants`,
    onSuccess: (res, rootState) => [
      updateOptionsStateFor(orgNo, () => ({ grantsResult: ok(res.data) })),
      ...(isOk(rootState.optionsState?.capTableResult) ? loadOptionsCapTable(orgNo) : []),
    ],
    onFailure: () => [
      showGeneralError(),
      updateOptionsStateFor(orgNo, () => ({ grantsResult: failure() })),
    ],
  });

export const loadOptionsCapTable = (orgNo: string): DCEvent[] => [
  capTableApiClient.getCapTable(
    orgNo,
    { includePool: true },
    {
      onSuccess: (res) => [updateOptionsStateFor(orgNo, () => ({ capTableResult: ok(res.data) }))],
      onFailure: () => [updateOptionsStateFor(orgNo, () => ({ capTableResult: failure() }))],
    },
  ),
];

export type FullyDilutedBasis = {
  total: number;
  sharesHeld: number;
};

export const fullyDilutedBasisFor = (
  state: OptionsPageState | undefined,
  shareholderId: number,
): FullyDilutedBasis | undefined => {
  const result = state?.capTableResult;
  if (!isOk(result) || result.data.totals.fullyDiluted <= 0) {
    return undefined;
  }
  return {
    total: result.data.totals.fullyDiluted,
    sharesHeld:
      result.data.rows.find((row) => row.stakeholder.shareholderId === shareholderId)?.shares ?? 0,
  };
};

const loadPlans = (orgNo: string): DCEvent =>
  httpGet({
    url: `/instruments/${orgNo}/options/plans`,
    onSuccess: (res) => [updateOptionsStateFor(orgNo, () => ({ plansResult: ok(res.data) }))],
    onFailure: () => [
      showGeneralError(),
      updateOptionsStateFor(orgNo, () => ({ plansResult: failure() })),
    ],
  });

export const initOptionsPage = (orgNo: string): DCEvent[] => [
  {
    type: "update-store",
    data: {
      optionsState: (state) => ({
        exerciseIssue: state?.exerciseIssue,
        orgNo,
        holdersResult: pending(),
        plansResult: pending(),
        grantsResult: pending(),
        searchModel: new StringFieldModel(),
        exercisesFilter: newExercisesFilter(),
        expandedHolderIds: [],
      }),
    },
  },
  loadHolders(orgNo),
  loadPlans(orgNo),
  loadGrants(orgNo),
];

// A grant built from several rows (a performance schedule and its milestone
// rows) reports the whole span, so lastRowNumber can be past rowNumber.
export type ImportRowError = {
  rowNumber: number;
  lastRowNumber: number;
  reason: string;
};

// The backend imports all rows or none, so errors always come back with the
// counts at zero.
export type ImportOptionHoldersResponse = {
  grantsRegistered: number;
  holdersCreated: number;
  plansCreated: number;
  errors: ImportRowError[];
};

// Reasons come from the backend in English; they name the cell the admin has to
// fix, so they ride along untranslated rather than being dropped.
export const importErrorLine = (error: ImportRowError): TMessage =>
  error.lastRowNumber > error.rowNumber
    ? {
        key: "options.import-error-rows",
        params: { from: error.rowNumber, to: error.lastRowNumber, reason: error.reason },
      }
    : { key: "options.import-error-row", params: { row: error.rowNumber, reason: error.reason } };

export const importResultAlert = (result: ImportOptionHoldersResponse): DCEvent => {
  if (result.errors.length > 0) {
    return showAlert(
      {
        key: "options.import-rejected",
        params: { rows: { type: "md-list", value: result.errors.map(importErrorLine) } },
      },
      "error",
      { autoHide: false },
    );
  }
  if (result.grantsRegistered === 0) {
    return showAlert({ key: "options.import-empty" }, "warning", { autoHide: false });
  }
  return showAlert(
    { key: "options.import-success", params: { count: result.grantsRegistered } },
    "success",
  );
};

const uploadOptionHoldersExcel = (orgNo: string, file: File): DCEvent => {
  const data = new FormData();
  data.append("file", file);
  return httpPost({
    url: `/instruments/${orgNo}/import-excel`,
    data,
    onSuccess: (res) => [
      importResultAlert(res.data),
      loadHolders(orgNo),
      loadPlans(orgNo),
      loadGrants(orgNo),
    ],
    onFailure: () => [showError({ key: "options.import-failed" }, { autoHide: false })],
  });
};

export const importOptionHoldersFromExcel = (orgNo: string, files: File[]): DCEvent[] => {
  const file = files[0];
  if (file === undefined) {
    return [];
  }
  return [
    {
      type: "validate-files",
      files: [file],
      fileUploadConfig: excelUploadConfig,
      onSuccess: [uploadOptionHoldersExcel(orgNo, file)],
      onFailure: (message) => [showError(message, { autoHide: false })],
    },
  ];
};

export const toggleHolderExpanded = (holderId: string): DCEvent =>
  updateOptionsState((state) => ({
    expandedHolderIds: toggleElement(state?.expandedHolderIds ?? [], holderId),
  }));

const appendQuickAddActors = (actors: Actor[]): DCEvent =>
  updateOptionsState((state) => ({
    quickAddActors: [...(state?.quickAddActors ?? []), ...actors],
  }));

// The dialog suggests the company's board members and CEO as quick-add chips.
export const openRegisterGrantDialog = (orgNo: string): DCEvent[] => [
  updateOptionsState(() => ({ registerGrant: { view: "quick-add" }, quickAddActors: [] })),
  ...[ActorType.BOARD_MEMBER, ActorType.CEO].flatMap((type) =>
    loadPortfolioActors(
      orgNo,
      type,
      (actors) => [appendQuickAddActors(actors)],
      () => [],
    ),
  ),
];

export const closeRegisterGrantDialog = (): DCEvent =>
  updateOptionsState(() => ({ registerGrant: undefined }));

export const newGrantForm = (
  plans: OptionPlan[],
  shareTypes: ShareType[],
  lang: Language,
): GrantForm => {
  const preselectedPlan = plans.length === 1 ? plans[0] : undefined;
  return {
    plan: new RadioFieldModel<OptionPlan | undefined>({
      required: true,
      value: preselectedPlan,
      options: plans,
      idFn: (plan) => plan?.optionPlanId ?? "",
      labelFn: (plan) => ({ translated: plan?.name ?? "" }),
    }),
    shareType: new RadioFieldModel<ShareType | undefined>({
      required: true,
      value: shareTypes.length === 1 ? shareTypes[0] : undefined,
      options: shareTypes,
      idFn: (shareType) => String(shareType?.sharetypeId ?? ""),
      labelFn: (shareType) => ({
        translated: shareType ? lookupShareTypeName(shareType.sharetypeId, shareTypes, lang) : "",
      }),
    }),
    numberOfOptions: new NumberFieldModel({ required: true, isInteger: true, isPositive: true }),
    strikePrice: new NumberFieldModel({ required: true, isPositiveOrZero: true }),
    grantDate: new LocalDateFieldModel({ required: true, value: toLocalDateISOString(today()) }),
    expirationDate: new LocalDateFieldModel({
      required: true,
      value: preselectedPlan?.expirationDate,
    }),
    remarks: new StringFieldModel(),
  };
};

const followPlanExpiration = (form: GrantForm, plan: OptionPlan): void => {
  const current = form.expirationDate.inputValue.trim();
  const next = plan.expirationDate ?? "";
  if (current === next || (current !== "" && current !== form.plan.value?.expirationDate)) {
    return;
  }
  if (next === "") {
    form.expirationDate.clear();
  } else {
    form.expirationDate.setAndCommitInputValue(next);
  }
};

const frequencyLabelKeys: Record<FrequencyChoice, TMessageKey> = {
  MONTHLY: "options.frequency-monthly",
  QUARTERLY: "options.frequency-quarterly",
  ANNUAL: "options.frequency-annual",
  SPECIFIC_DATES: "options.frequency-specific-dates",
};

const vestingTypeLabelKeys: Record<VestingType, TMessageKey> = {
  TIME_VESTED: "options.time-vested",
  PERFORMANCE: "options.performance-vested",
};

export const milestoneScopeLabelKeys: Record<MilestoneScope, TMessageKey> = {
  INDIVIDUAL: "options.milestone-individual",
  COMPANY_WIDE: "options.milestone-company-wide",
};

export const cancellationReasonLabelKeys: Record<CancellationReason, TMessageKey> = {
  RESIGNATION_GOOD_LEAVER: "options.cancel-reason-resignation",
  DISMISSAL_BAD_LEAVER: "options.cancel-reason-dismissal",
  REDUNDANCY: "options.cancel-reason-redundancy",
  RETIREMENT: "options.cancel-reason-retirement",
  DEATH_OR_DISABILITY: "options.cancel-reason-death-disability",
  MUTUAL_AGREEMENT: "options.cancel-reason-mutual-agreement",
  FAILED_MILESTONE: "options.cancel-reason-failed-milestone",
  OTHER: "options.cancel-reason-other",
};

export const cancellationReasonOptions = (isPerformance: boolean): CancellationReason[] =>
  (
    [
      "RESIGNATION_GOOD_LEAVER",
      "DISMISSAL_BAD_LEAVER",
      "REDUNDANCY",
      "RETIREMENT",
      "DEATH_OR_DISABILITY",
      "MUTUAL_AGREEMENT",
      "FAILED_MILESTONE",
      "OTHER",
    ] as const
  ).filter((reason) => reason !== "FAILED_MILESTONE" || isPerformance);

const durationUnitLabelKeys: Record<DurationUnit, TMessageKey> = {
  MONTHS: "options.unit-months",
  YEARS: "options.unit-years",
};

const newDurationFields = (
  amount: number,
  unit: DurationUnit,
  { allowZero = false }: { allowZero?: boolean } = {},
): DurationFields => ({
  amount: new NumberFieldModel({
    required: true,
    isInteger: true,
    isPositive: !allowZero,
    isPositiveOrZero: allowZero,
    value: amount,
  }),
  unit: new RadioFieldModel<DurationUnit>({
    required: true,
    value: unit,
    options: ["MONTHS", "YEARS"],
    idFn: (u) => u,
    labelFn: (u) => ({ key: durationUnitLabelKeys[u] }),
  }),
});

export const durationMonths = (duration: DurationFields): number | undefined => {
  const amount = duration.amount.toNumber();
  if (amount === undefined) {
    return undefined;
  }
  return duration.unit.value === "YEARS" ? amount * 12 : amount;
};

export const newSpecificDateRow = (): SpecificDateRow => ({
  id: newUuid(),
  date: new LocalDateFieldModel({ required: true }),
  options: new NumberFieldModel({ required: true, isInteger: true, isPositive: true }),
});

export const newMilestoneRow = (milestone: VestingMilestone): MilestoneRow => ({
  id: newUuid(),
  scope: milestone.scope,
  title: milestone.title,
  criterion: milestone.criterion,
  options: new NumberFieldModel({
    required: true,
    isInteger: true,
    isPositive: true,
    value: milestone.optionsAmount,
  }),
});

export const newVestingForm = (): VestingForm => ({
  vestingType: new RadioFieldModel<VestingType>({
    required: true,
    value: "TIME_VESTED",
    options: ["TIME_VESTED", "PERFORMANCE"],
    idFn: (type) => type,
    labelFn: (type) => ({ key: vestingTypeLabelKeys[type] }),
  }),
  period: newDurationFields(4, "YEARS"),
  frequency: new RadioFieldModel<FrequencyChoice>({
    required: true,
    value: "MONTHLY",
    options: ["MONTHLY", "QUARTERLY", "ANNUAL", "SPECIFIC_DATES"],
    idFn: (frequency) => frequency,
    labelFn: (frequency) => ({ key: frequencyLabelKeys[frequency] }),
  }),
  cliff: newDurationFields(12, "MONTHS", { allowZero: true }),
  specificDates: [newSpecificDateRow()],
  milestones: [],
  showAddMilestone: true,
  addMilestoneScope: new RadioFieldModel<MilestoneScope>({
    required: true,
    value: "INDIVIDUAL",
    options: ["INDIVIDUAL", "COMPANY_WIDE"],
    idFn: (scope) => scope,
    labelFn: (scope) => ({ key: milestoneScopeLabelKeys[scope] }),
  }),
  addMilestoneTitle: new StringFieldModel({ required: true }),
  addMilestoneCriterion: new StringFieldModel(),
});

export const planVestingForm = (plan: OptionPlan | undefined): VestingForm =>
  plan?.vestingTemplate === undefined
    ? newVestingForm()
    : vestingFormFromConfig(plan.vestingTemplate);

const newWizardForms = (
  plans: OptionPlan[],
  shareTypes: ShareType[],
  lang: Language,
): { grantForm: GrantForm; vestingForm: VestingForm } => {
  const grantForm = newGrantForm(plans, shareTypes, lang);
  return { grantForm, vestingForm: planVestingForm(grantForm.plan.value) };
};

export const startAddNewHolder = (
  lang: Language,
  plans: OptionPlan[],
  shareTypes: ShareType[],
  shareholders: ShareholderDTO[],
): DCEvent =>
  updateOptionsState(() => ({
    registerGrant: {
      view: "wizard",
      step: "holder-details",
      holderForm: newHolderForm(lang, shareholders),
      ...newWizardForms(plans, shareTypes, lang),
    },
  }));

const updateWizard = (f: (wizard: GrantWizard) => Partial<GrantWizard>): DCEvent =>
  updateOptionsState((state) =>
    state?.registerGrant?.view === "wizard"
      ? { registerGrant: { ...state.registerGrant, ...f(state.registerGrant) } }
      : {},
  );

export const setWizardStep = (step: RegisterGrantStep): DCEvent => updateWizard(() => ({ step }));

// The grant step renders in both the register wizard and the edit-grant
// dialog — changes apply to whichever of them is open.
const activeGrantForm = (state?: OptionsPageState): GrantForm | undefined =>
  state?.registerGrant?.view === "wizard"
    ? state.registerGrant.grantForm
    : state?.editGrant?.grantForm;

export const isGrantFormOpen = (state?: OptionsPageState): boolean =>
  activeGrantForm(state) !== undefined;

export const selectGrantPlan = (plan: OptionPlan): DCEvent =>
  updateOptionsState((state) => {
    const grantForm = activeGrantForm(state);
    const changed = grantForm?.plan.value?.optionPlanId !== plan.optionPlanId;
    if (state?.registerGrant?.view === "wizard") {
      followPlanExpiration(state.registerGrant.grantForm, plan);
    }
    grantForm?.plan.setValue(plan);
    return changed && state?.registerGrant?.view === "wizard"
      ? {
          registerGrant: {
            ...state.registerGrant,
            vestingForm: planVestingForm(plan),
            showAllVestingEntries: false,
          },
        }
      : {};
  });

const setWizardSubmitResult = (submitResult: Result<TMessage, TMessage>): DCEvent =>
  updateWizard(() => ({ submitResult }));

const commandFailure = (err: unknown): DCEvent =>
  setWizardSubmitResult(failure(commandFailureMessage(err)));

const validateExpirationAfterPoolDate = (
  plan: Pick<CreatePlanState, "expirationDate" | "poolSize" | "poolEffectiveDate">,
): boolean => {
  const poolDate = plan.poolEffectiveDate.validatedValue;
  const expirationDate = plan.expirationDate.validatedValue;
  if (
    plan.poolSize.isEmpty ||
    !plan.expirationDate.isValid ||
    !poolDate ||
    !expirationDate ||
    expirationDate > poolDate
  ) {
    return true;
  }
  plan.expirationDate.setError({ key: "options.expiration-after-pool-date" });
  return false;
};

export const newExerciseWindowRow = (): ExerciseWindowRow => ({
  id: newUuid(),
  fromDate: new LocalDateFieldModel({ required: true }),
  toDate: new LocalDateFieldModel({ required: true }),
  hasStarted: false,
});

const exerciseWindowRow = (exerciseWindow: ExerciseWindow, today: string): ExerciseWindowRow => ({
  id: newUuid(),
  fromDate: new LocalDateFieldModel({ required: true, value: exerciseWindow.fromDate }),
  toDate: new LocalDateFieldModel({ required: true, value: exerciseWindow.toDate }),
  hasStarted: exerciseWindow.fromDate <= today,
});

const exerciseWindowsOf = (rows: ExerciseWindowRow[]): ExerciseWindow[] =>
  rows
    .flatMap((row) => {
      const fromDate = row.fromDate.validatedValue;
      const toDate = row.toDate.validatedValue;
      return fromDate === undefined || toDate === undefined ? [] : [{ fromDate, toDate }];
    })
    .sort((a, b) => a.fromDate.localeCompare(b.fromDate));

type PlanRequestFields = Pick<
  CreatePlanState,
  "allowExerciseRequests" | "exerciseRequestTiming" | "exerciseWindows"
>;

const exerciseRequestTimingKeys: Record<
  ExerciseRequestTiming,
  { label: TMessageKey; description: TMessageKey }
> = {
  ANY_TIME: {
    label: "options.request-exercise-any-time",
    description: "options.request-exercise-any-time-description",
  },
  WINDOWS_ONLY: {
    label: "options.request-exercise-windows-only",
    description: "options.request-exercise-windows-only-description",
  },
};

export const exerciseRequestTimingLabel = (timing: ExerciseRequestTiming): TMessage => ({
  key: exerciseRequestTimingKeys[timing].label,
});

export const exerciseRequestTimingDescription = (timing: ExerciseRequestTiming): TMessage => ({
  key: exerciseRequestTimingKeys[timing].description,
});

const exerciseRequestFields = (
  policy: ExerciseRequestPolicy,
): Pick<CreatePlanState, "allowExerciseRequests" | "exerciseRequestTiming"> => ({
  allowExerciseRequests: new BooleanFieldModel({ value: policy !== "DISABLED" }),
  exerciseRequestTiming: new RadioFieldModel<ExerciseRequestTiming>({
    required: true,
    value: policy === "WINDOWS_ONLY" ? "WINDOWS_ONLY" : "ANY_TIME",
    options: ["ANY_TIME", "WINDOWS_ONLY"],
    idFn: (timing) => timing,
    labelFn: exerciseRequestTimingLabel,
    descriptionFn: exerciseRequestTimingDescription,
  }),
});

export const planRequestPolicy = (
  plan: Pick<CreatePlanState, "allowExerciseRequests" | "exerciseRequestTiming">,
): ExerciseRequestPolicy =>
  plan.allowExerciseRequests.value ? plan.exerciseRequestTiming.value : "DISABLED";

const sentRequestPolicy = (
  plan: Pick<CreatePlanState, "allowExerciseRequests" | "exerciseRequestTiming">,
): ExerciseRequestTiming | undefined => {
  const policy = planRequestPolicy(plan);
  return policy === "DISABLED" ? undefined : policy;
};

const missingExerciseWindow = (plan: PlanRequestFields): TMessage | undefined =>
  planRequestPolicy(plan) === "WINDOWS_ONLY" && plan.exerciseWindows.length === 0
    ? { key: "options.exercise-window-required" }
    : undefined;

const editedWindowRows = (plan: PlanRequestFields): ExerciseWindowRow[] =>
  planRequestPolicy(plan) === "WINDOWS_ONLY" ? plan.exerciseWindows : [];

const savedExerciseWindows = (plan: PlanRequestFields): ExerciseWindow[] =>
  exerciseWindowsOf(
    planRequestPolicy(plan) === "WINDOWS_ONLY"
      ? plan.exerciseWindows
      : plan.exerciseWindows.filter((row) => row.hasStarted),
  );

export const activeExerciseWindows = (plan: OptionPlan | undefined): ExerciseWindow[] =>
  plan?.exerciseRequestPolicy === "WINDOWS_ONLY" ? (plan.exerciseWindows ?? []) : [];

const fromOf = (row: ExerciseWindowRow): string => row.fromDate.validatedValue ?? "";

const toOf = (row: ExerciseWindowRow): string => row.toDate.validatedValue ?? "";

const validateExerciseWindows = (rows: ExerciseWindowRow[]): boolean => {
  const fieldsValid = rows
    .flatMap((row) => [row.fromDate.validate(), row.toDate.validate()])
    .every(Boolean);
  if (!fieldsValid) {
    return false;
  }
  const reversed = rows.filter((row) => toOf(row) < fromOf(row));
  reversed.forEach((row) =>
    row.toDate.setError({ key: "options.exercise-window-ends-before-start" }),
  );
  const sorted = [...rows].sort((a, b) => fromOf(a).localeCompare(fromOf(b)));
  const overlapping = sorted.flatMap((row, index) => {
    const previous = sorted[index - 1];
    return previous !== undefined && fromOf(row) <= toOf(previous)
      ? [row.hasStarted ? previous : row]
      : [];
  });
  overlapping.forEach((row) => row.fromDate.setError({ key: "options.exercise-windows-overlap" }));
  return reversed.length === 0 && overlapping.length === 0;
};

const programStartOf = (
  plan: Pick<CreatePlanState, "poolSize" | "poolEffectiveDate">,
  poolChanges: OptionPoolChange[],
): string | undefined => {
  const enteredPoolDate = plan.poolSize.isEmpty ? undefined : plan.poolEffectiveDate.validatedValue;
  return [
    ...poolChanges.map((change) => change.effectiveDate),
    ...compact([enteredPoolDate]),
  ].sort()[0];
};

const validExpirationOf = (plan: Pick<CreatePlanState, "expirationDate">): string | undefined =>
  plan.expirationDate.isValid ? plan.expirationDate.validatedValue : undefined;

const validateWindowsWithinProgram = (
  rows: ExerciseWindowRow[],
  programStart: string | undefined,
  expirationDate: string | undefined,
): boolean => {
  const early = rows.filter(
    (row) => programStart !== undefined && row.fromDate.isValid && fromOf(row) < programStart,
  );
  early.forEach((row) =>
    row.fromDate.setError({ key: "options.exercise-window-before-program-start" }),
  );
  const late = rows.filter(
    (row) => expirationDate !== undefined && row.toDate.isValid && toOf(row) > expirationDate,
  );
  late.forEach((row) => row.toDate.setError({ key: "options.exercise-window-after-expiration" }));
  return early.length === 0 && late.length === 0;
};

export const openCreatePlanDialog = (): DCEvent =>
  updateOptionsState(() => ({
    createPlan: {
      name: new StringFieldModel({ required: true }),
      description: new StringFieldModel(),
      expirationDate: new LocalDateFieldModel({ required: true }),
      poolSize: new NumberFieldModel({ isInteger: true, isPositiveOrZero: true }),
      poolEffectiveDate: new LocalDateFieldModel({
        required: true,
        value: toLocalDateISOString(today()),
      }),
      ...exerciseRequestFields("DISABLED"),
      exerciseWindows: [],
      stagedFiles: [],
    },
  }));

export const closeCreatePlanDialog = (): DCEvent =>
  updateOptionsState(() => ({ createPlan: undefined }));

const updateCreatePlan = (f: (create: CreatePlanState) => Partial<CreatePlanState>): DCEvent =>
  updateOptionsState((state) =>
    state?.createPlan === undefined
      ? {}
      : { createPlan: { ...state.createPlan, ...f(state.createPlan) } },
  );

export const stageCreatePlanFiles = (files: File[]): DCEvent =>
  updateCreatePlan((state) => ({
    stagedFiles: [...state.stagedFiles, ...files.map((file) => ({ id: newUuid(), file }))],
  }));

export const removeStagedCreatePlanFile = (id: string): DCEvent =>
  updateCreatePlan((state) => ({
    stagedFiles: state.stagedFiles.filter((staged) => staged.id !== id),
  }));

// The new program joins the plan list and is preselected in whichever grant
// form the dialog was opened from.
export const planCreated = (plan: OptionPlan): DCEvent =>
  updateOptionsState((state) => {
    const previous = state?.plansResult;
    const plans = [...(isOk(previous) ? previous.data : []), plan];
    const grantForm = activeGrantForm(state);
    if (state?.registerGrant?.view === "wizard") {
      followPlanExpiration(state.registerGrant.grantForm, plan);
    }
    grantForm?.plan.updateOptions(plans);
    grantForm?.plan.setValue(plan);
    return { plansResult: ok(plans), createPlan: undefined };
  });

export const createPlanCommand = (create: CreatePlanState) => {
  const poolSize = create.poolSize.toNumber();
  const effectiveDate = create.poolEffectiveDate.validatedValue;
  const exerciseWindows = savedExerciseWindows(create);
  return {
    commandType: "CREATE_PLAN" as const,
    name: create.name.inputValue.trim(),
    description: create.description.inputValue.trim() || undefined,
    expirationDate: create.expirationDate.validatedValue,
    exerciseWindows: exerciseWindows.length > 0 ? exerciseWindows : undefined,
    exerciseRequestPolicy: sentRequestPolicy(create),
    initialPool:
      poolSize === undefined || effectiveDate === undefined
        ? undefined
        : { effectiveDate, poolSize },
  };
};

export const submitCreatePlan = (orgNo: string, create: CreatePlanState): DCEvent[] => {
  const windowsError = missingExerciseWindow(create);
  const valid = [
    create.name.validate(),
    create.expirationDate.validate(),
    create.poolSize.validate(),
    create.poolSize.isEmpty || create.poolEffectiveDate.validate(),
    validateExpirationAfterPoolDate(create),
    validateExerciseWindows(editedWindowRows(create)),
    validateWindowsWithinProgram(
      editedWindowRows(create),
      programStartOf(create, []),
      validExpirationOf(create),
    ),
  ].every(Boolean);
  if (windowsError !== undefined) {
    return [updateCreatePlan(() => ({ exerciseWindowsError: windowsError }))];
  }
  if (!valid) {
    return [];
  }
  return [
    updateCreatePlan(() => ({ submitResult: pending() })),
    instrumentCommandApiClient.send<{ plan: OptionPlan }>(orgNo, createPlanCommand(create), {
      onSuccess: (res) => [
        planCreated(res.data.plan),
        ...(create.stagedFiles.length > 0
          ? [
              uploadPlanAttachments(
                orgNo,
                res.data.plan.optionPlanId,
                create.stagedFiles.map((staged) => staged.file),
              ),
            ]
          : []),
      ],
      onFailure: (err) => [
        updateCreatePlan(() => ({ submitResult: failure(commandFailureMessage(err)) })),
      ],
    }),
  ];
};

export const openEditPlanDialog = (plan: OptionPlan, today: string): DCEvent => {
  const currentPool = poolChangeOn(plan, today);
  return updateOptionsState(() => ({
    editPlan: {
      plan,
      name: new StringFieldModel({ required: true, value: plan.name }),
      description: new StringFieldModel({ value: plan.description ?? "" }),
      expirationDate: new LocalDateFieldModel({ required: true, value: plan.expirationDate }),
      poolSize: new NumberFieldModel({
        required: (plan.poolChanges ?? []).length > 0,
        isInteger: true,
        isPositiveOrZero: true,
        value: currentPool?.poolSize,
      }),
      poolEffectiveDate: new LocalDateFieldModel({
        required: true,
        value: currentPool?.effectiveDate,
      }),
      poolComment: new StringFieldModel(),
      ...exerciseRequestFields(plan.exerciseRequestPolicy ?? "DISABLED"),
      exerciseWindows: (plan.exerciseWindows ?? []).map((exerciseWindow) =>
        exerciseWindowRow(exerciseWindow, today),
      ),
    },
  }));
};

export const closeEditPlanDialog = (): DCEvent =>
  updateOptionsState(() => ({ editPlan: undefined }));

const updateEditPlan = (f: (state: EditPlanState) => Partial<EditPlanState>): DCEvent =>
  updateOptionsState((state) =>
    state?.editPlan === undefined ? {} : { editPlan: { ...state.editPlan, ...f(state.editPlan) } },
  );

export type PlanDialog = "create" | "edit";

const updateExerciseWindowRows = (
  dialog: PlanDialog,
  f: (rows: ExerciseWindowRow[]) => ExerciseWindowRow[],
): DCEvent =>
  dialog === "create"
    ? updateCreatePlan((state) => ({
        exerciseWindows: f(state.exerciseWindows),
        exerciseWindowsError: undefined,
      }))
    : updateEditPlan((state) => ({
        exerciseWindows: f(state.exerciseWindows),
        exerciseWindowsError: undefined,
      }));

export const addExerciseWindow = (dialog: PlanDialog): DCEvent =>
  updateExerciseWindowRows(dialog, (rows) => [...rows, newExerciseWindowRow()]);

export const removeExerciseWindow = (dialog: PlanDialog, id: string): DCEvent =>
  updateExerciseWindowRows(dialog, (rows) => rows.filter((row) => row.id !== id || row.hasStarted));

export const updatePlanCommand = (state: EditPlanState) => {
  const name = state.name.inputValue.trim();
  const description = state.description.inputValue.trim();
  const expirationDate = state.expirationDate.validatedValue;
  const exerciseWindows = savedExerciseWindows(state);
  const exerciseRequestPolicy = planRequestPolicy(state);
  const poolSize = state.poolSize.toNumber();
  const effectiveDate = state.poolEffectiveDate.validatedValue;
  const poolChange =
    poolSize === undefined ||
    effectiveDate === undefined ||
    poolSize === poolSizeOn(state.plan, effectiveDate)
      ? undefined
      : { effectiveDate, poolSize, comment: state.poolComment.inputValue.trim() || undefined };
  if (
    name === state.plan.name &&
    description === (state.plan.description ?? "") &&
    expirationDate === state.plan.expirationDate &&
    isEqual(exerciseWindows, state.plan.exerciseWindows ?? []) &&
    exerciseRequestPolicy === (state.plan.exerciseRequestPolicy ?? "DISABLED") &&
    poolChange === undefined
  ) {
    return undefined;
  }
  return {
    commandType: "UPDATE_PLAN" as const,
    planId: state.plan.optionPlanId,
    name,
    description: description || undefined,
    expirationDate,
    exerciseWindows: exerciseWindows.length > 0 ? exerciseWindows : undefined,
    exerciseRequestPolicy: sentRequestPolicy(state),
    poolChange,
  };
};

export const submitEditPlan = (orgNo: string, state: EditPlanState): DCEvent[] => {
  const windowsError = missingExerciseWindow(state);
  const valid = [
    state.name.validate(),
    state.expirationDate.validate(),
    state.poolSize.validate(),
    state.poolSize.isEmpty || state.poolEffectiveDate.validate(),
    validateExpirationAfterPoolDate(state),
    validateExerciseWindows(editedWindowRows(state)),
    validateWindowsWithinProgram(
      editedWindowRows(state),
      programStartOf(state, state.plan.poolChanges ?? []),
      validExpirationOf(state),
    ),
  ].every(Boolean);
  if (windowsError !== undefined) {
    return [updateEditPlan(() => ({ exerciseWindowsError: windowsError }))];
  }
  if (!valid) {
    return [];
  }
  const command = updatePlanCommand(state);
  if (command === undefined) {
    return [closeEditPlanDialog()];
  }
  return [
    updateEditPlan(() => ({ submitResult: pending() })),
    instrumentCommandApiClient.send(orgNo, command, {
      onSuccess: () => [closeEditPlanDialog(), loadPlans(orgNo)],
      onFailure: (err) => [
        updateEditPlan(() => ({ submitResult: failure(commandFailureMessage(err)) })),
      ],
    }),
  ];
};

export const submitHolderStep = (form: NewHolderForm): DCEvent[] =>
  validateNewHolderForm(form)
    ? [updateWizard(() => ({ step: "grant", holderStepCompleted: true }))]
    : [];

const startGrantForPendingHolder = (
  pendingHolder: NewHolderSpec,
  lang: Language,
  plans: OptionPlan[],
  shareTypes: ShareType[],
): DCEvent =>
  updateOptionsState(() => ({
    registerGrant: {
      view: "wizard",
      step: "grant",
      pendingHolder,
      holderName: pendingHolder.name,
      ...newWizardForms(plans, shareTypes, lang),
    },
  }));

export const startGrantForPortfolioActor = (
  actor: SelectedActor,
  lang: Language,
  plans: OptionPlan[],
  shareTypes: ShareType[],
): DCEvent => startGrantForPendingHolder(portfolioHolderSpec(actor), lang, plans, shareTypes);

const startGrantForExistingHolder = (
  holderId: string,
  holderName: string,
  lang: Language,
  plans: OptionPlan[],
  shareTypes: ShareType[],
): DCEvent =>
  updateOptionsState(() => ({
    registerGrant: {
      view: "wizard",
      step: "grant",
      holderId,
      holderName,
      ...newWizardForms(plans, shareTypes, lang),
    },
  }));

export const startGrantForHolder = (
  holder: InstrumentHolder,
  lang: Language,
  plans: OptionPlan[],
  shareTypes: ShareType[],
): DCEvent =>
  startGrantForExistingHolder(holder.instrumentHolderId, holder.name, lang, plans, shareTypes);

export const startGrantForQuickAddActor = (
  suggestion: QuickAddSuggestion,
  orgNo: string,
  lang: Language,
  plans: OptionPlan[],
  shareTypes: ShareType[],
): DCEvent => {
  if (suggestion.holderId !== undefined) {
    return startGrantForExistingHolder(
      suggestion.holderId,
      suggestion.holderName ?? suggestion.actor.name,
      lang,
      plans,
      shareTypes,
    );
  }
  return startGrantForPendingHolder(
    quickAddHolderSpec(suggestion.actor, orgNo),
    lang,
    plans,
    shareTypes,
  );
};

const validateExpirationAfterGrantDate = (form: GrantForm): boolean => {
  const grantDate = form.grantDate.validatedValue;
  const expirationDate = form.expirationDate.validatedValue;
  if (!grantDate || !expirationDate || expirationDate > grantDate) {
    return true;
  }
  form.expirationDate.setError({ key: "options.expiration-after-grant-date" });
  return false;
};

export const validateGrantForm = (form: GrantForm): boolean => {
  const fieldsValid = [
    form.plan.validate(),
    form.shareType.validate(),
    form.numberOfOptions.validate(),
    form.strikePrice.validate(),
    form.grantDate.validate(),
    form.expirationDate.validate(),
  ].every(Boolean);
  const datesOrdered = validateExpirationAfterGrantDate(form);
  return fieldsValid && datesOrdered;
};

export const submitGrantStep = (form: GrantForm): DCEvent[] =>
  validateGrantForm(form)
    ? [updateWizard(() => ({ step: "vesting-schedule", grantStepCompleted: true }))]
    : [];

// The vesting step renders in both the register wizard and the edit-vesting
// dialog — these events apply to whichever of them is open.
const updateActiveVestingForm = (
  f: (grantForm: GrantForm, vestingForm: VestingForm) => VestingForm,
): DCEvent =>
  updateOptionsState((state) => {
    if (state?.registerGrant?.view === "wizard") {
      const wizard = state.registerGrant;
      return {
        registerGrant: { ...wizard, vestingForm: f(wizard.grantForm, wizard.vestingForm) },
      };
    }
    if (state?.editVesting !== undefined) {
      const edit = state.editVesting;
      return { editVesting: { ...edit, vestingForm: f(edit.grantForm, edit.vestingForm) } };
    }
    if (state?.editPlanVesting !== undefined) {
      const edit = state.editPlanVesting;
      return { editPlanVesting: { ...edit, vestingForm: f(edit.grantForm, edit.vestingForm) } };
    }
    return {};
  });

export const setShowAllVestingEntries = (showAllVestingEntries: boolean): DCEvent =>
  updateOptionsState((state) => {
    if (state?.registerGrant?.view === "wizard") {
      return { registerGrant: { ...state.registerGrant, showAllVestingEntries } };
    }
    if (state?.editVesting !== undefined) {
      return { editVesting: { ...state.editVesting, showAllVestingEntries } };
    }
    if (state?.editPlanVesting !== undefined) {
      return { editPlanVesting: { ...state.editPlanVesting, showAllVestingEntries } };
    }
    return {};
  });

export const addVestingDateRow = (): DCEvent =>
  updateActiveVestingForm((_, vestingForm) => ({
    ...vestingForm,
    specificDates: [...vestingForm.specificDates, newSpecificDateRow()],
  }));

export const removeVestingDateRow = (id: string): DCEvent =>
  updateActiveVestingForm((_, vestingForm) => ({
    ...vestingForm,
    specificDates: vestingForm.specificDates.filter((row) => row.id !== id),
  }));

export const isPerformanceVesting = (vestingForm: VestingForm): boolean =>
  vestingForm.vestingType.value === "PERFORMANCE";

export const toggleAddMilestone = (): DCEvent =>
  updateActiveVestingForm((_, vestingForm) => ({
    ...vestingForm,
    showAddMilestone: !vestingForm.showAddMilestone,
  }));

export const removeMilestone = (id: string): DCEvent =>
  updateActiveVestingForm((_, vestingForm) => ({
    ...vestingForm,
    milestones: vestingForm.milestones.filter((row) => row.id !== id),
  }));

// A new milestone defaults to the still-unallocated options, so adding the last
// one closes the gap and unlocks Save without further typing.
export const addMilestone = (): DCEvent =>
  updateActiveVestingForm((grantForm, vestingForm) => {
    if (!vestingForm.addMilestoneTitle.validate()) {
      return vestingForm;
    }
    const row = newMilestoneRow({
      scope: vestingForm.addMilestoneScope.value,
      title: vestingForm.addMilestoneTitle.inputValue.trim(),
      criterion: vestingForm.addMilestoneCriterion.inputValue.trim() || undefined,
      optionsAmount: Math.max(unallocatedMilestoneOptions(grantForm, vestingForm), 0),
    });
    vestingForm.addMilestoneScope.setValue("INDIVIDUAL");
    vestingForm.addMilestoneTitle.setAndCommitInputValue("");
    vestingForm.addMilestoneCriterion.setAndCommitInputValue("");
    return {
      ...vestingForm,
      milestones: [...vestingForm.milestones, row],
      showAddMilestone: false,
    };
  });

export const isSpecificDatesVesting = (vestingForm: VestingForm): boolean =>
  vestingForm.frequency.value === "SPECIFIC_DATES";

const specificDateRowFromEntry = (entry: VestingScheduleEntry): SpecificDateRow => {
  const row = newSpecificDateRow();
  row.date.setAndCommitInputValue(entry.date);
  row.options.setAndCommitInputValue(String(entry.options));
  return row;
};

// Switching to specific dates starts from the schedule the periodic settings
// produced, so the user edits dates instead of re-entering them.
export const vestingFormWithFrequency = (
  grantForm: GrantForm,
  vestingForm: VestingForm,
  choice: FrequencyChoice,
): VestingForm => {
  const seed =
    choice === "SPECIFIC_DATES" && !isSpecificDatesVesting(vestingForm)
      ? vestingSchedule(grantForm, vestingForm)
      : [];
  vestingForm.frequency.setValue(choice);
  return seed.length > 0
    ? { ...vestingForm, specificDates: seed.map(specificDateRowFromEntry) }
    : vestingForm;
};

export const setVestingFrequency = (choice: FrequencyChoice): DCEvent =>
  updateActiveVestingForm((grantForm, vestingForm) =>
    vestingFormWithFrequency(grantForm, vestingForm, choice),
  );

const specificDateEntries = (vestingForm: VestingForm): VestingDateEntry[] =>
  vestingForm.specificDates.flatMap((row) => {
    const vestingDate = row.date.validatedValue;
    const optionsAmount = row.options.toNumber();
    return vestingDate !== undefined && optionsAmount !== undefined && optionsAmount > 0
      ? [{ vestingDate, optionsAmount }]
      : [];
  });

export const unscheduledOptions = (grantForm: GrantForm, vestingForm: VestingForm): number => {
  const totalOptions = grantForm.numberOfOptions.toNumber() ?? 0;
  const scheduled = vestingForm.specificDates.reduce(
    (acc, row) => acc + (row.options.toNumber() ?? 0),
    0,
  );
  return totalOptions - scheduled;
};

export const unallocatedMilestoneOptions = (
  grantForm: GrantForm,
  vestingForm: VestingForm,
): number => {
  const totalOptions = grantForm.numberOfOptions.toNumber() ?? 0;
  const allocated = vestingForm.milestones.reduce(
    (acc, row) => acc + (row.options.toNumber() ?? 0),
    0,
  );
  return totalOptions - allocated;
};

// Mirrors the backend checks except duplicate dates, which the backend reports
// with a translated message instead.
export const validateVestingForm = (grantForm: GrantForm, vestingForm: VestingForm): boolean => {
  if (isPerformanceVesting(vestingForm)) {
    if (vestingForm.milestones.length === 0) {
      return false;
    }
    const rowsValid = vestingForm.milestones.map((row) => row.options.validate()).every(Boolean);
    return rowsValid && unallocatedMilestoneOptions(grantForm, vestingForm) === 0;
  }
  if (!isSpecificDatesVesting(vestingForm)) {
    return [vestingForm.period.amount.validate(), vestingForm.cliff.amount.validate()].every(
      Boolean,
    );
  }
  const rowsValid = vestingForm.specificDates
    .map((row) => [row.date.validate(), row.options.validate()].every(Boolean))
    .every(Boolean);
  return rowsValid && unscheduledOptions(grantForm, vestingForm) === 0;
};

// Pure predicate for gating the Save button: a performance grant needs at least
// one milestone with a positive amount and every option allocated.
// validateVestingForm mutates field error state, so it can't run during render.
export const performanceMilestonesComplete = (
  grantForm: GrantForm,
  vestingForm: VestingForm,
): boolean =>
  vestingForm.milestones.length > 0 &&
  vestingForm.milestones.every((row) => (row.options.toNumber() ?? 0) > 0) &&
  unallocatedMilestoneOptions(grantForm, vestingForm) === 0;

export type VestingScheduleEntry = {
  date: string;
  options: number;
  fractionOfTotal: number;
};

const plusVestingMonths = (date: DateValue, months: number): string =>
  toLocalDateISOString(addMonths(toDate(date), months));

const frequencyMonths: Record<VestingFrequency, number> = {
  MONTHLY: 1,
  QUARTERLY: 3,
  ANNUAL: 12,
};

// Pre-cliff accruals vest in one batch on the cliff date (e.g. 48 months,
// 12-month cliff, monthly: 25% on the cliff, then monthly thereafter).
export const periodicVestingSchedule = ({
  totalOptions,
  grantDate,
  periodMonths,
  cliffMonths,
  frequency,
}: {
  totalOptions: number;
  grantDate: string;
  periodMonths: number;
  cliffMonths: number;
  frequency: VestingFrequency;
}): VestingScheduleEntry[] => {
  const interval = frequencyMonths[frequency];
  const eventCount = Math.floor(periodMonths / interval);
  if (eventCount <= 0 || totalOptions <= 0) {
    return [];
  }
  const base = Math.floor(totalOptions / eventCount);
  const remainder = totalOptions - base * eventCount;
  const events = Array.from({ length: eventCount }, (_, index) => ({
    monthOffset: (index + 1) * interval,
    options: base + (index < remainder ? 1 : 0),
  }));
  const [atCliff, afterCliff] = [
    events.filter((event) => event.monthOffset <= cliffMonths),
    events.filter((event) => event.monthOffset > cliffMonths),
  ];
  const cliffEvent =
    atCliff.length > 0
      ? [
          {
            monthOffset: cliffMonths,
            options: atCliff.reduce((acc, event) => acc + event.options, 0),
          },
        ]
      : [];
  return [...cliffEvent, ...afterCliff].map((event) => ({
    date: plusVestingMonths(grantDate, event.monthOffset),
    options: event.options,
    fractionOfTotal: event.options / totalOptions,
  }));
};

export const vestingSchedule = (
  grantForm: GrantForm,
  vestingForm: VestingForm,
): VestingScheduleEntry[] => {
  const totalOptions = grantForm.numberOfOptions.toNumber();
  const grantDate = grantForm.grantDate.validatedValue;
  if (!totalOptions || !grantDate) {
    return [];
  }
  const frequency = vestingForm.frequency.value;
  if (frequency === "SPECIFIC_DATES") {
    return specificDateEntries(vestingForm)
      .map((entry) => ({
        date: entry.vestingDate,
        options: entry.optionsAmount,
        fractionOfTotal: entry.optionsAmount / totalOptions,
      }))
      .sort((a, b) => a.date.localeCompare(b.date));
  }
  const periodMonths = durationMonths(vestingForm.period);
  if (periodMonths === undefined) {
    return [];
  }
  return periodicVestingSchedule({
    totalOptions,
    grantDate,
    periodMonths,
    cliffMonths: durationMonths(vestingForm.cliff) ?? 0,
    frequency,
  });
};

export const vestingCliffDate = (
  grantForm: GrantForm,
  vestingForm: VestingForm,
): string | undefined => {
  const grantDate = grantForm.grantDate.validatedValue;
  const cliffMonths = durationMonths(vestingForm.cliff) ?? 0;
  if (!grantDate || cliffMonths <= 0) {
    return undefined;
  }
  return plusVestingMonths(grantDate, cliffMonths);
};

export type GrantHolderRef = { holderId: string } | { newHolder: NewHolderSpec };

export const wizardHolderRef = (wizard: GrantWizard): GrantHolderRef | undefined => {
  if (wizard.holderId !== undefined) {
    return { holderId: wizard.holderId };
  }
  if (wizard.holderForm !== undefined) {
    return { newHolder: newHolderSpec(wizard.holderForm) };
  }
  if (wizard.pendingHolder !== undefined) {
    return { newHolder: wizard.pendingHolder };
  }
  return undefined;
};

export const grantStepEnabled = (wizard: GrantWizard): boolean =>
  wizard.holderForm === undefined || wizard.holderStepCompleted === true;

export const wizardHolderName = (wizard: GrantWizard): string | undefined => {
  if (wizard.holderName !== undefined) {
    return wizard.holderName;
  }
  if (wizard.holderForm === undefined) {
    return undefined;
  }
  const name = newHolderSpec(wizard.holderForm).name;
  return name === "" ? undefined : name;
};

export type RegisterGrantCommand = {
  commandType: "REGISTER_GRANT";
  holderId?: string;
  newHolder?: NewHolderSpec;
  planId: string;
  registeredDate: string;
  tranches: Array<{
    shareTypeId: number;
    totalOptions: number;
    strikePrice: number;
    grantDate: string;
    expirationDate: string;
    holderBearsPayrollTax: boolean;
    remarks: string;
    vesting:
      | {
          vestingType: "PERIODIC";
          periodMonths: number;
          cliffMonths: number;
          frequency: VestingFrequency;
        }
      | { vestingType: "SPECIFIC_DATES"; entries: VestingDateEntry[] }
      | { vestingType: "PERFORMANCE"; milestones: VestingMilestone[] };
  }>;
};

const milestonesFromForm = (vestingForm: VestingForm): VestingMilestone[] =>
  vestingForm.milestones.flatMap((row) => {
    const optionsAmount = row.options.toNumber();
    return optionsAmount !== undefined && optionsAmount > 0
      ? [{ scope: row.scope, title: row.title, criterion: row.criterion, optionsAmount }]
      : [];
  });

const trancheVesting = (
  vestingForm: VestingForm,
): RegisterGrantCommand["tranches"][number]["vesting"] | undefined => {
  if (isPerformanceVesting(vestingForm)) {
    return { vestingType: "PERFORMANCE", milestones: milestonesFromForm(vestingForm) };
  }
  const frequency = vestingForm.frequency.value;
  if (frequency === "SPECIFIC_DATES") {
    return { vestingType: "SPECIFIC_DATES", entries: specificDateEntries(vestingForm) };
  }
  const periodMonths = durationMonths(vestingForm.period);
  const cliffMonths = durationMonths(vestingForm.cliff);
  return periodMonths === undefined || cliffMonths === undefined
    ? undefined
    : { vestingType: "PERIODIC", periodMonths, cliffMonths, frequency };
};

export const registerGrantCommand = (
  holderRef: GrantHolderRef,
  grantForm: GrantForm,
  vestingForm: VestingForm,
): RegisterGrantCommand | undefined => {
  const plan = grantForm.plan.value;
  const shareType = grantForm.shareType.value;
  const totalOptions = grantForm.numberOfOptions.toNumber();
  const strikePrice = grantForm.strikePrice.toNumber();
  const grantDate = grantForm.grantDate.validatedValue;
  const expirationDate = grantForm.expirationDate.validatedValue;
  const vesting = trancheVesting(vestingForm);
  if (
    !plan ||
    !shareType ||
    !totalOptions ||
    strikePrice === undefined ||
    !grantDate ||
    !expirationDate ||
    vesting === undefined
  ) {
    return undefined;
  }
  return {
    commandType: "REGISTER_GRANT",
    ...holderRef,
    planId: plan.optionPlanId,
    registeredDate: toLocalDateISOString(today()),
    tranches: [
      {
        shareTypeId: shareType.sharetypeId,
        totalOptions,
        strikePrice,
        grantDate,
        expirationDate,
        holderBearsPayrollTax: false,
        remarks: grantForm.remarks.inputValue.trim(),
        vesting,
      },
    ],
  };
};

// Everything-or-nothing: the holder (when new) travels inside the grant command
// and is persisted by the backend in the same transaction as the grant.
export const submitRegisterGrant = (orgNo: string, wizard: GrantWizard): DCEvent[] => {
  if (wizard.holderForm !== undefined && !validateNewHolderForm(wizard.holderForm)) {
    return [setWizardStep("holder-details")];
  }
  if (!validateGrantForm(wizard.grantForm)) {
    return [setWizardStep("grant")];
  }
  if (!validateVestingForm(wizard.grantForm, wizard.vestingForm)) {
    return [setWizardStep("vesting-schedule")];
  }
  const holderRef = wizardHolderRef(wizard);
  const command =
    holderRef === undefined
      ? undefined
      : registerGrantCommand(holderRef, wizard.grantForm, wizard.vestingForm);
  if (command === undefined) {
    return [];
  }
  return [
    setWizardSubmitResult(pending()),
    instrumentCommandApiClient.send(orgNo, command, {
      onSuccess: () => [
        updateOptionsState(() => ({ registerGrant: undefined })),
        showAlert({ key: "options.grant-registered" }, "success"),
        loadHolders(orgNo),
        loadGrants(orgNo),
      ],
      onFailure: (err) => [commandFailure(err)],
    }),
  ];
};

// The template is a schedule as a grant stores it. The grant form only gives the
// vesting step a number of options and a grant date to show the schedule with.
export type EditPlanVestingState = {
  plan: OptionPlan;
  grantForm: GrantForm;
  vestingForm: VestingForm;
  showAllVestingEntries?: boolean;
  submitResult?: Result<TMessage, TMessage>;
};

// The registered terms loaded into a grant form — the base for editing the
// grant, and the read-only context for the vesting schedule preview.
const grantFormFromTranche = (
  tranche: OptionTranche,
  grants: OptionGrantDetails[],
  plans: OptionPlan[],
  shareTypes: ShareType[],
  lang: Language,
): GrantForm => {
  const planId = grants.find((d) => d.grant.optionGrantId === tranche.grantId)?.grant.planId;
  const grantForm = newGrantForm(plans, shareTypes, lang);
  grantForm.plan.setValue(plans.find((p) => p.optionPlanId === planId));
  grantForm.shareType.setValue(shareTypes.find((s) => s.sharetypeId === tranche.shareTypeId));
  grantForm.numberOfOptions.setAndCommitInputValue(tranche.totalOptions);
  grantForm.strikePrice.setAndCommitInputValue(tranche.strikePrice);
  grantForm.grantDate.setAndCommitInputValue(tranche.grantDate);
  grantForm.expirationDate.setAndCommitInputValue(tranche.expirationDate);
  grantForm.remarks.setAndCommitInputValue(tranche.remarks ?? "");
  return grantForm;
};

const setDurationMonths = (duration: DurationFields, months: number): void => {
  const wholeYears = months > 0 && months % 12 === 0;
  duration.unit.setValue(wholeYears ? "YEARS" : "MONTHS");
  duration.amount.setAndCommitInputValue(wholeYears ? months / 12 : months);
};

export const vestingFormFromConfig = (vesting: VestingConfig): VestingForm => {
  const form = newVestingForm();
  switch (vesting.vestingType) {
    case "PERIODIC":
      setDurationMonths(form.period, vesting.periodMonths);
      setDurationMonths(form.cliff, vesting.cliffMonths);
      form.frequency.setValue(vesting.frequency);
      return form;
    case "SPECIFIC_DATES":
      form.frequency.setValue("SPECIFIC_DATES");
      return {
        ...form,
        specificDates: vesting.entries.map((entry) => {
          const row = newSpecificDateRow();
          row.date.setAndCommitInputValue(entry.vestingDate);
          row.options.setAndCommitInputValue(entry.optionsAmount);
          return row;
        }),
      };
    case "PERFORMANCE":
      form.vestingType.setValue("PERFORMANCE");
      return {
        ...form,
        milestones: vesting.milestones.map(newMilestoneRow),
        // Existing milestones show the list with a "+ Add milestone" link; an
        // empty set (e.g. legacy data) opens the add form straight away.
        showAddMilestone: vesting.milestones.length === 0,
      };
  }
};

export const startEditGrant = (
  tranche: OptionTranche,
  holderName: string,
  grants: OptionGrantDetails[],
  plans: OptionPlan[],
  shareTypes: ShareType[],
  lang: Language,
): DCEvent =>
  updateOptionsState(() => ({
    editGrant: {
      grantId: tranche.grantId,
      trancheId: tranche.optionTrancheId,
      holderName,
      grantForm: grantFormFromTranche(tranche, grants, plans, shareTypes, lang),
    },
  }));

export const closeEditGrantDialog = (): DCEvent =>
  updateOptionsState(() => ({ editGrant: undefined }));

const updateEditGrant = (f: (edit: EditGrantState) => Partial<EditGrantState>): DCEvent =>
  updateOptionsState((state) =>
    state?.editGrant === undefined
      ? {}
      : { editGrant: { ...state.editGrant, ...f(state.editGrant) } },
  );

export type UpdateGrantCommand = {
  commandType: "UPDATE_GRANT";
  grantId: string;
  trancheId: string;
  planId: string;
  shareTypeId?: number;
  totalOptions: number;
  strikePrice: number;
  grantDate: string;
  expirationDate: string;
  remarks: string;
};

export const updateGrantCommand = (edit: EditGrantState): UpdateGrantCommand | undefined => {
  const { grantForm } = edit;
  const plan = grantForm.plan.value;
  const shareType = grantForm.shareType.value;
  const totalOptions = grantForm.numberOfOptions.toNumber();
  const strikePrice = grantForm.strikePrice.toNumber();
  const grantDate = grantForm.grantDate.validatedValue;
  const expirationDate = grantForm.expirationDate.validatedValue;
  if (
    !plan ||
    !shareType ||
    !totalOptions ||
    strikePrice === undefined ||
    !grantDate ||
    !expirationDate
  ) {
    return undefined;
  }
  return {
    commandType: "UPDATE_GRANT",
    grantId: edit.grantId,
    trancheId: edit.trancheId,
    planId: plan.optionPlanId,
    shareTypeId: shareType.sharetypeId,
    totalOptions,
    strikePrice,
    grantDate,
    expirationDate,
    remarks: grantForm.remarks.inputValue.trim(),
  };
};

export const submitEditGrant = (orgNo: string, edit: EditGrantState): DCEvent[] => {
  if (!validateGrantForm(edit.grantForm)) {
    return [];
  }
  const command = updateGrantCommand(edit);
  if (command === undefined) {
    return [];
  }
  return [
    updateEditGrant(() => ({ submitResult: pending() })),
    instrumentCommandApiClient.send(orgNo, command, {
      onSuccess: () => [
        closeEditGrantDialog(),
        showAlert({ key: "options.grant-updated" }, "success"),
        loadGrants(orgNo),
      ],
      onFailure: (err) => [
        updateEditGrant(() => ({ submitResult: failure(commandFailureMessage(err)) })),
      ],
    }),
  ];
};

export const startEditVesting = (
  tranche: OptionTranche,
  holderName: string,
  grants: OptionGrantDetails[],
  plans: OptionPlan[],
  shareTypes: ShareType[],
  lang: Language,
): DCEvent =>
  updateOptionsState(() => ({
    editVesting: {
      grantId: tranche.grantId,
      trancheId: tranche.optionTrancheId,
      holderName,
      grantForm: grantFormFromTranche(tranche, grants, plans, shareTypes, lang),
      vestingForm: vestingFormFromConfig(tranche.vesting),
    },
  }));

export const closeEditVestingDialog = (): DCEvent =>
  updateOptionsState(() => ({ editVesting: undefined }));

const updateEditVesting = (f: (edit: EditVestingState) => Partial<EditVestingState>): DCEvent =>
  updateOptionsState((state) =>
    state?.editVesting === undefined
      ? {}
      : { editVesting: { ...state.editVesting, ...f(state.editVesting) } },
  );

export type UpdateVestingCommand = {
  commandType: "UPDATE_VESTING";
  grantId: string;
  trancheId: string;
  vesting: RegisterGrantCommand["tranches"][number]["vesting"];
};

export const updateVestingCommand = (edit: EditVestingState): UpdateVestingCommand | undefined => {
  const vesting = trancheVesting(edit.vestingForm);
  return vesting === undefined
    ? undefined
    : {
        commandType: "UPDATE_VESTING",
        grantId: edit.grantId,
        trancheId: edit.trancheId,
        vesting,
      };
};

export const submitEditVesting = (orgNo: string, edit: EditVestingState): DCEvent[] => {
  if (!validateVestingForm(edit.grantForm, edit.vestingForm)) {
    return [];
  }
  const command = updateVestingCommand(edit);
  if (command === undefined) {
    return [];
  }
  return [
    updateEditVesting(() => ({ submitResult: pending() })),
    instrumentCommandApiClient.send(orgNo, command, {
      onSuccess: () => [
        closeEditVestingDialog(),
        showAlert({ key: "options.vesting-updated" }, "success"),
        loadGrants(orgNo),
      ],
      onFailure: (err) => [
        updateEditVesting(() => ({ submitResult: failure(commandFailureMessage(err)) })),
      ],
    }),
  ];
};

const vestingTotal = (vesting: VestingConfig): number | undefined => {
  switch (vesting.vestingType) {
    case "PERIODIC":
      return undefined;
    case "SPECIFIC_DATES":
      return vesting.entries.reduce((acc, entry) => acc + entry.optionsAmount, 0);
    case "PERFORMANCE":
      return vesting.milestones.reduce((acc, milestone) => acc + milestone.optionsAmount, 0);
  }
};

export const startEditPlanVesting = (plan: OptionPlan, today: string, lang: Language): DCEvent => {
  const grantForm = newGrantForm([plan], [], lang);
  const numberOfOptions =
    (plan.vestingTemplate === undefined ? undefined : vestingTotal(plan.vestingTemplate)) ??
    poolSizeOn(plan, today);
  if (numberOfOptions !== undefined) {
    grantForm.numberOfOptions.setAndCommitInputValue(numberOfOptions);
  }
  grantForm.grantDate.setAndCommitInputValue(today);
  return updateOptionsState(() => ({
    editPlanVesting: { plan, grantForm, vestingForm: planVestingForm(plan) },
  }));
};

export const closeEditPlanVestingDialog = (): DCEvent =>
  updateOptionsState(() => ({ editPlanVesting: undefined }));

const updateEditPlanVesting = (
  f: (edit: EditPlanVestingState) => Partial<EditPlanVestingState>,
): DCEvent =>
  updateOptionsState((state) =>
    state?.editPlanVesting === undefined
      ? {}
      : { editPlanVesting: { ...state.editPlanVesting, ...f(state.editPlanVesting) } },
  );

export const updatePlanVestingCommand = (edit: EditPlanVestingState) => {
  const vestingTemplate = trancheVesting(edit.vestingForm);
  return vestingTemplate === undefined
    ? undefined
    : {
        commandType: "UPDATE_PLAN_VESTING" as const,
        planId: edit.plan.optionPlanId,
        vestingTemplate,
      };
};

export const submitEditPlanVesting = (orgNo: string, edit: EditPlanVestingState): DCEvent[] => {
  if (!validateVestingForm(edit.grantForm, edit.vestingForm)) {
    return [];
  }
  const command = updatePlanVestingCommand(edit);
  if (command === undefined) {
    return [];
  }
  return [
    updateEditPlanVesting(() => ({ submitResult: pending() })),
    instrumentCommandApiClient.send(orgNo, command, {
      onSuccess: () => [
        closeEditPlanVestingDialog(),
        showAlert({ key: "options.vesting-template-updated" }, "success"),
        loadPlans(orgNo),
      ],
      onFailure: (err) => [
        updateEditPlanVesting(() => ({ submitResult: failure(commandFailureMessage(err)) })),
      ],
    }),
  ];
};

export type StagedFile = { id: string; file: File };

const toCancellationOption = (
  twp: TrancheWithPlan,
  holderName: string,
  today: string,
): CancellationGrantOption => {
  const numbers = trancheNumbers(twp.tranche, today);
  return {
    trancheId: twp.tranche.optionTrancheId,
    grantId: twp.tranche.grantId,
    holderName,
    planName: twp.planName,
    vestedAvailable: numbers.vested,
    unvestedAvailable: numbers.outstanding - numbers.vested,
    isPerformance: twp.tranche.vesting.vestingType === "PERFORMANCE",
  };
};

const buildCancellationReason = (
  isPerformance: boolean,
  value: CancellationReason = "RESIGNATION_GOOD_LEAVER",
): RadioFieldModel<CancellationReason> =>
  new RadioFieldModel<CancellationReason>({
    required: true,
    value,
    options: cancellationReasonOptions(isPerformance),
    idFn: (reason) => reason,
    labelFn: (reason) => ({ key: cancellationReasonLabelKeys[reason] }),
  });

const buildCancelAmountField = (available: number): NumberFieldModel =>
  new NumberFieldModel({
    required: true,
    isInteger: true,
    isPositiveOrZero: true,
    value: 0,
    validator: (value) =>
      value !== undefined && value > available
        ? {
            key: "options.cancel-exceeds-available",
            params: { available: { type: "number", value: available } },
          }
        : undefined,
  });

// Every holder × program pair in the company, so the picker can retarget the
// cancellation to any grant, sorted by holder then program.
const cancellationOptions = (
  grants: OptionGrantDetails[],
  plans: OptionPlan[],
  holders: InstrumentHolder[],
  today: string,
): CancellationGrantOption[] => {
  const planNameById = new Map(plans.map((p) => [p.optionPlanId, p.name]));
  const holderNameById = new Map(holders.map((h) => [h.instrumentHolderId, h.name]));
  return grants
    .flatMap((details) =>
      details.tranches.map((t) =>
        toCancellationOption(
          { tranche: t, planName: planNameById.get(details.grant.planId) ?? "–" },
          holderNameById.get(details.grant.holderId) ?? "",
          today,
        ),
      ),
    )
    .sort(
      (a, b) => a.holderName.localeCompare(b.holderName) || a.planName.localeCompare(b.planName),
    );
};

// `selected` undefined opens the dialog with nothing chosen — the picker shows a
// placeholder and the fields stay hidden until a grant is selected. The amount
// caps and reason options are (re)built from the selection in setCancellationGrant.
const newCancellationState = (
  options: CancellationGrantOption[],
  selected: CancellationGrantOption | undefined,
  today: string,
): CancellationState => ({
  grantOptions: options,
  selectedTrancheId: selected?.trancheId ?? "",
  activeTab: "cancellation",
  attachmentReference: newUuid(),
  reason: buildCancellationReason(selected?.isPerformance ?? false),
  effectiveDate: new LocalDateFieldModel({ required: true, value: today }),
  vestedToCancel: buildCancelAmountField(selected?.vestedAvailable ?? 0),
  unvestedToCancel: buildCancelAmountField(selected?.unvestedAvailable ?? 0),
  remarks: new StringFieldModel(),
  stagedFiles: [],
});

// Opened from a specific grant's menu — that grant is preselected.
export const startCancellation = (
  tranche: OptionTranche,
  grants: OptionGrantDetails[],
  plans: OptionPlan[],
  holders: InstrumentHolder[],
  today: string,
): DCEvent => {
  const options = cancellationOptions(grants, plans, holders, today);
  const selected = options.find((o) => o.trancheId === tranche.optionTrancheId) ?? options[0];
  if (selected === undefined) {
    return updateOptionsState(() => ({}));
  }
  return updateOptionsState(() => ({
    cancellation: newCancellationState(options, selected, today),
  }));
};

// Opened from the options page — no grant preselected, so the admin picks the
// holder × program pair in the dialog first.
export const startCancellationForCompany = (
  grants: OptionGrantDetails[],
  plans: OptionPlan[],
  holders: InstrumentHolder[],
  today: string,
): DCEvent => {
  const options = cancellationOptions(grants, plans, holders, today);
  if (options.length === 0) {
    return updateOptionsState(() => ({}));
  }
  return updateOptionsState(() => ({
    cancellation: newCancellationState(options, undefined, today),
  }));
};

export const closeCancellationDialog = (): DCEvent =>
  updateOptionsState(() => ({ cancellation: undefined }));

const updateCancellation = (f: (state: CancellationState) => Partial<CancellationState>): DCEvent =>
  updateOptionsState((state) =>
    state?.cancellation === undefined
      ? {}
      : { cancellation: { ...state.cancellation, ...f(state.cancellation) } },
  );

export const setCancellationTab = (tab: CancellationState["activeTab"]): DCEvent =>
  updateCancellation(() => ({ activeTab: tab }));

// Switching grant rebuilds the amount fields (new caps and defaults) and the
// reason options (a non-performance grant drops "failed milestone").
export const setCancellationGrant = (trancheId: string): DCEvent =>
  updateCancellation((state) => {
    const option = state.grantOptions.find((o) => o.trancheId === trancheId);
    if (option === undefined) {
      return {};
    }
    const reason = cancellationReasonOptions(option.isPerformance).includes(state.reason.value)
      ? state.reason.value
      : undefined;
    return {
      selectedTrancheId: trancheId,
      reason: buildCancellationReason(option.isPerformance, reason),
      vestedToCancel: buildCancelAmountField(option.vestedAvailable),
      unvestedToCancel: buildCancelAmountField(option.unvestedAvailable),
    };
  });

export const stageCancellationFiles = (files: File[]): DCEvent =>
  updateCancellation((state) => ({
    stagedFiles: [...state.stagedFiles, ...files.map((file) => ({ id: newUuid(), file }))],
  }));

export const removeStagedCancellationFile = (id: string): DCEvent =>
  updateCancellation((state) => ({
    stagedFiles: state.stagedFiles.filter((staged) => staged.id !== id),
  }));

export const selectedCancellationOption = (
  state: CancellationState,
): CancellationGrantOption | undefined =>
  state.grantOptions.find((o) => o.trancheId === state.selectedTrancheId);

export const cancellationComplete = (state: CancellationState): boolean => {
  const option = selectedCancellationOption(state);
  const vested = state.vestedToCancel.toNumber();
  const unvested = state.unvestedToCancel.toNumber();
  return (
    option !== undefined &&
    state.effectiveDate.validatedValue !== undefined &&
    vested !== undefined &&
    unvested !== undefined &&
    vested >= 0 &&
    unvested >= 0 &&
    vested <= option.vestedAvailable &&
    unvested <= option.unvestedAvailable &&
    vested + unvested > 0
  );
};

const validateCancellation = (state: CancellationState): boolean => {
  const okReason = state.reason.validate();
  const okDate = state.effectiveDate.validate();
  const okVested = state.vestedToCancel.validate();
  const okUnvested = state.unvestedToCancel.validate();
  return okReason && okDate && okVested && okUnvested && cancellationComplete(state);
};

export type CancelGrantCommand = {
  commandType: "CANCEL_GRANT";
  grantId: string;
  trancheId: string;
  reason: CancellationReason;
  effectiveDate: string;
  vestedCancelled: number;
  unvestedCancelled: number;
  remarks?: string;
  attachmentReference: string;
};

export const cancelGrantCommand = (state: CancellationState): CancelGrantCommand | undefined => {
  const option = selectedCancellationOption(state);
  const effectiveDate = state.effectiveDate.validatedValue;
  const vestedCancelled = state.vestedToCancel.toNumber();
  const unvestedCancelled = state.unvestedToCancel.toNumber();
  if (
    option === undefined ||
    effectiveDate === undefined ||
    vestedCancelled === undefined ||
    unvestedCancelled === undefined
  ) {
    return undefined;
  }
  return {
    commandType: "CANCEL_GRANT",
    grantId: option.grantId,
    trancheId: option.trancheId,
    reason: state.reason.value,
    effectiveDate,
    vestedCancelled,
    unvestedCancelled,
    remarks: state.remarks.inputValue.trim() || undefined,
    attachmentReference: state.attachmentReference,
  };
};

export const uploadCancellationAttachments = (
  orgNo: string,
  reference: string,
  files: File[],
): DCEvent =>
  httpPost({
    url: `/instruments/${orgNo}/options/cancellations/${reference}/attachments`,
    data: toFormData(files),
    onSuccess: () => [loadGrants(orgNo)],
    onFailure: () => [showGeneralError()],
  });

export const submitCancellation = (orgNo: string, state: CancellationState): DCEvent[] => {
  if (!validateCancellation(state)) {
    return [];
  }
  const command = cancelGrantCommand(state);
  if (command === undefined) {
    return [];
  }
  const files = state.stagedFiles.map((staged) => staged.file);
  return [
    updateCancellation(() => ({ submitResult: pending() })),
    instrumentCommandApiClient.send(orgNo, command, {
      onSuccess: () => [
        ...(files.length > 0
          ? [uploadCancellationAttachments(orgNo, command.attachmentReference, files)]
          : []),
        closeCancellationDialog(),
        showAlert({ key: "options.cancellation-registered" }, "success"),
        loadGrants(orgNo),
      ],
      onFailure: (err) => [
        updateCancellation(() => ({ submitResult: failure(commandFailureMessage(err)) })),
      ],
    }),
  ];
};

const toAccelerationOption = (
  twp: TrancheWithPlan,
  holderName: string,
  today: string,
): AccelerationGrantOption => {
  const numbers = trancheNumbers(twp.tranche, today);
  return {
    trancheId: twp.tranche.optionTrancheId,
    grantId: twp.tranche.grantId,
    holderName,
    planName: twp.planName,
    unvestedAvailable: numbers.outstanding - numbers.vested,
  };
};

const buildAccelerateAmountField = (available: number): NumberFieldModel =>
  new NumberFieldModel({
    required: true,
    isInteger: true,
    isPositiveOrZero: true,
    value: available,
    validator: (value) =>
      value !== undefined && value > available
        ? {
            key: "options.accelerate-exceeds-available",
            params: { available: { type: "number", value: available } },
          }
        : undefined,
  });

const accelerationOptions = (
  grants: OptionGrantDetails[],
  plans: OptionPlan[],
  holders: InstrumentHolder[],
  today: string,
): AccelerationGrantOption[] => {
  const planNameById = new Map(plans.map((p) => [p.optionPlanId, p.name]));
  const holderNameById = new Map(holders.map((h) => [h.instrumentHolderId, h.name]));
  return grants
    .flatMap((details) =>
      details.tranches.map((t) =>
        toAccelerationOption(
          { tranche: t, planName: planNameById.get(details.grant.planId) ?? "–" },
          holderNameById.get(details.grant.holderId) ?? "",
          today,
        ),
      ),
    )
    .sort(
      (a, b) => a.holderName.localeCompare(b.holderName) || a.planName.localeCompare(b.planName),
    );
};

const newAccelerationState = (
  options: AccelerationGrantOption[],
  selected: AccelerationGrantOption | undefined,
  preselected: boolean,
  today: string,
): AccelerationState => ({
  grantOptions: options,
  selectedTrancheId: selected?.trancheId ?? "",
  preselected,
  activeTab: "acceleration",
  attachmentReference: newUuid(),
  effectiveDate: new LocalDateFieldModel({ required: true, value: today }),
  unvestedToAccelerate: buildAccelerateAmountField(selected?.unvestedAvailable ?? 0),
  remarks: new StringFieldModel(),
  stagedFiles: [],
});

export const startAcceleration = (
  tranche: OptionTranche,
  grants: OptionGrantDetails[],
  plans: OptionPlan[],
  holders: InstrumentHolder[],
  today: string,
): DCEvent => {
  const options = accelerationOptions(grants, plans, holders, today);
  const selected = options.find((o) => o.trancheId === tranche.optionTrancheId) ?? options[0];
  if (selected === undefined) {
    return updateOptionsState(() => ({}));
  }
  return updateOptionsState(() => ({
    acceleration: newAccelerationState(options, selected, true, today),
  }));
};

export const startAccelerationForCompany = (
  grants: OptionGrantDetails[],
  plans: OptionPlan[],
  holders: InstrumentHolder[],
  today: string,
): DCEvent => {
  const options = accelerationOptions(grants, plans, holders, today);
  if (options.length === 0) {
    return updateOptionsState(() => ({}));
  }
  return updateOptionsState(() => ({
    acceleration: newAccelerationState(options, undefined, false, today),
  }));
};

export const closeAccelerationDialog = (): DCEvent =>
  updateOptionsState(() => ({ acceleration: undefined }));

const updateAcceleration = (f: (state: AccelerationState) => Partial<AccelerationState>): DCEvent =>
  updateOptionsState((state) =>
    state?.acceleration === undefined
      ? {}
      : { acceleration: { ...state.acceleration, ...f(state.acceleration) } },
  );

export const setAccelerationTab = (tab: AccelerationState["activeTab"]): DCEvent =>
  updateAcceleration(() => ({ activeTab: tab }));

// Switching grant rebuilds the amount field with the new cap and default.
export const setAccelerationGrant = (trancheId: string): DCEvent =>
  updateAcceleration((state) => {
    const option = state.grantOptions.find((o) => o.trancheId === trancheId);
    if (option === undefined) {
      return {};
    }
    return {
      selectedTrancheId: trancheId,
      unvestedToAccelerate: buildAccelerateAmountField(option.unvestedAvailable),
    };
  });

export const stageAccelerationFiles = (files: File[]): DCEvent =>
  updateAcceleration((state) => ({
    stagedFiles: [...state.stagedFiles, ...files.map((file) => ({ id: newUuid(), file }))],
  }));

export const removeStagedAccelerationFile = (id: string): DCEvent =>
  updateAcceleration((state) => ({
    stagedFiles: state.stagedFiles.filter((staged) => staged.id !== id),
  }));

export const selectedAccelerationOption = (
  state: AccelerationState,
): AccelerationGrantOption | undefined =>
  state.grantOptions.find((o) => o.trancheId === state.selectedTrancheId);

export const accelerationComplete = (state: AccelerationState): boolean => {
  const option = selectedAccelerationOption(state);
  const unvested = state.unvestedToAccelerate.toNumber();
  return (
    option !== undefined &&
    state.effectiveDate.validatedValue !== undefined &&
    unvested !== undefined &&
    unvested > 0 &&
    unvested <= option.unvestedAvailable
  );
};

const validateAcceleration = (state: AccelerationState): boolean => {
  const okDate = state.effectiveDate.validate();
  const okUnvested = state.unvestedToAccelerate.validate();
  return okDate && okUnvested && accelerationComplete(state);
};

export type AccelerateGrantCommand = {
  commandType: "ACCELERATE_GRANT";
  grantId: string;
  trancheId: string;
  effectiveDate: string;
  unvestedAccelerated: number;
  remarks?: string;
  attachmentReference: string;
};

export const accelerateGrantCommand = (
  state: AccelerationState,
): AccelerateGrantCommand | undefined => {
  const option = selectedAccelerationOption(state);
  const effectiveDate = state.effectiveDate.validatedValue;
  const unvestedAccelerated = state.unvestedToAccelerate.toNumber();
  if (option === undefined || effectiveDate === undefined || unvestedAccelerated === undefined) {
    return undefined;
  }
  return {
    commandType: "ACCELERATE_GRANT",
    grantId: option.grantId,
    trancheId: option.trancheId,
    effectiveDate,
    unvestedAccelerated,
    remarks: state.remarks.inputValue.trim() || undefined,
    attachmentReference: state.attachmentReference,
  };
};

export const uploadAccelerationAttachments = (
  orgNo: string,
  reference: string,
  files: File[],
): DCEvent =>
  httpPost({
    url: `/instruments/${orgNo}/options/accelerations/${reference}/attachments`,
    data: toFormData(files),
    onSuccess: () => [loadGrants(orgNo)],
    onFailure: () => [showGeneralError()],
  });

export const submitAcceleration = (orgNo: string, state: AccelerationState): DCEvent[] => {
  if (!validateAcceleration(state)) {
    return [];
  }
  const command = accelerateGrantCommand(state);
  if (command === undefined) {
    return [];
  }
  const files = state.stagedFiles.map((staged) => staged.file);
  return [
    updateAcceleration(() => ({ submitResult: pending() })),
    instrumentCommandApiClient.send(orgNo, command, {
      onSuccess: () => [
        ...(files.length > 0
          ? [uploadAccelerationAttachments(orgNo, command.attachmentReference, files)]
          : []),
        closeAccelerationDialog(),
        showAlert({ key: "options.acceleration-registered" }, "success"),
        loadGrants(orgNo),
      ],
      onFailure: (err) => [
        updateAcceleration(() => ({ submitResult: failure(commandFailureMessage(err)) })),
      ],
    }),
  ];
};

export type ExerciseQuantity = "VESTED" | "GRANT" | "MANUAL";

export type ExerciseRowState = {
  key: string;
  trancheId: string;
  grantId: string;
  planId: string;
  planName: string;
  holderId: string;
  holderName: string;
  shareholderId?: number;
  program: TMessage;
  strikePrice: number;
  needsShareClass: boolean;
  isSplit: boolean;
  isSelected: boolean;
  quantity: ExerciseQuantity;
  vestedRemaining: number;
  grantRemaining: number;
  count: NumberFieldModel;
  shareTypeId?: number;
  receiver: ExerciseReceiver;
  isEmployee: boolean;
  requestId?: string;
  requestedCount?: number;
};

export type ExerciseStep = "exercise" | "issue";

export type ExerciseMethod = "documented" | "direct";

export type ExerciseState = {
  isPreselected: boolean;
  step: ExerciseStep;
  method: ExerciseMethod;
  planId?: string;
  search: StringFieldModel;
  isAcknowledgedOnly: boolean;
  exerciseDate: LocalDateFieldModel;
  rows: ExerciseRowState[];
  holdingCompaniesResult?: Result<HolderHoldingCompanies[]>;
  attachmentReference: string;
  remarks: StringFieldModel;
  stagedFiles: StagedFile[];
  projectName?: string;
  isCreatingProject?: boolean;
};

export type ProgramFilter = { planId?: string; label: TMessage };

const exerciseCountField = (value?: number): NumberFieldModel =>
  new NumberFieldModel({ required: true, isInteger: true, isPositive: true, value });

const withExerciseCount = (row: ExerciseRowState, count: number): ExerciseRowState => ({
  ...row,
  quantity: count === row.vestedRemaining ? "VESTED" : "MANUAL",
  count: exerciseCountField(count),
});

const acknowledgedRequestOf = (tranche: OptionTranche): TrancheExerciseRequest | undefined =>
  (tranche.exerciseRequests ?? [])
    .filter((request) => request.status === "ACKNOWLEDGED")
    .sort((a, b) => a.requestDate.localeCompare(b.requestDate))[0];

const exerciseRows = (
  grants: OptionGrantDetails[],
  plans: OptionPlan[],
  holders: InstrumentHolder[],
  today: string,
  trancheId?: string,
): ExerciseRowState[] => {
  const planNameById = new Map(plans.map((p) => [p.optionPlanId, p.name]));
  const holderById = new Map(holders.map((h) => [h.instrumentHolderId, h]));
  return grants
    .flatMap((details) =>
      details.tranches
        .filter((tranche) => trancheId === undefined || tranche.optionTrancheId === trancheId)
        .map((tranche) => ({
          details,
          tranche,
          planName: planNameById.get(details.grant.planId) ?? "–",
          holder: holderById.get(details.grant.holderId),
          exercisable: exercisableOn(tranche, today),
        })),
    )
    .filter(({ exercisable }) => exercisable > 0)
    .sort(
      (a, b) =>
        (a.holder?.name ?? "").localeCompare(b.holder?.name ?? "") ||
        a.planName.localeCompare(b.planName),
    )
    .map(({ details, tranche, planName, holder, exercisable }) => {
      const request = acknowledgedRequestOf(tranche);
      const row: ExerciseRowState = {
        key: newUuid(),
        trancheId: tranche.optionTrancheId,
        grantId: details.grant.optionGrantId,
        planId: details.grant.planId,
        planName,
        holderId: details.grant.holderId,
        holderName: holder?.name ?? "",
        shareholderId: holder?.shareholderId,
        program: programLabel(planName, tranche.name),
        strikePrice: tranche.strikePrice,
        needsShareClass: tranche.shareTypeId === undefined,
        shareTypeId: tranche.shareTypeId,
        isSplit: false,
        isSelected: request !== undefined,
        quantity: "VESTED",
        vestedRemaining: exercisable,
        grantRemaining: trancheNumbers(tranche, today).outstanding,
        count: exerciseCountField(exercisable),
        receiver: { type: "HOLDER" },
        isEmployee: false,
        requestId: request?.requestId,
        requestedCount: request?.count,
      };
      return request === undefined
        ? row
        : withExerciseCount(row, Math.min(request.count, exercisable));
    });
};

export const hasExercisableOptions = (grants: OptionGrantDetails[], today: string): boolean =>
  grants.some((details) => details.tranches.some((tranche) => exercisableOn(tranche, today) > 0));

const newExerciseState = (
  rows: ExerciseRowState[],
  isPreselected: boolean,
  today: string,
): ExerciseState => ({
  isPreselected,
  step: "exercise",
  method: "documented",
  search: new StringFieldModel(),
  isAcknowledgedOnly: false,
  exerciseDate: new LocalDateFieldModel({ required: true, value: today }),
  rows,
  holdingCompaniesResult: pending(),
  attachmentReference: newUuid(),
  remarks: new StringFieldModel(),
  stagedFiles: [],
});

const updateExercise = (f: (state: ExerciseState) => Partial<ExerciseState>): DCEvent =>
  updateOptionsState((state) =>
    state?.exercise === undefined ? {} : { exercise: { ...state.exercise, ...f(state.exercise) } },
  );

const loadExerciseHoldingCompanies = (orgNo: string, holderIds?: string[]): DCEvent =>
  instrumentHolderApiClient.getHoldingCompanies(
    orgNo,
    { holderIds },
    {
      onSuccess: (res) => [updateExercise(() => ({ holdingCompaniesResult: ok(res.data) }))],
      onFailure: () => [updateExercise(() => ({ holdingCompaniesResult: failure() }))],
    },
  );

export const startExercise = (
  orgNo: string,
  tranche: OptionTranche,
  grants: OptionGrantDetails[],
  plans: OptionPlan[],
  holders: InstrumentHolder[],
  today: string,
): DCEvent[] => {
  const rows = exerciseRows(grants, plans, holders, today, tranche.optionTrancheId).map((row) => ({
    ...row,
    isSelected: true,
  }));
  if (rows.length === 0) {
    return [];
  }
  return [
    updateOptionsState(() => ({ exercise: newExerciseState(rows, true, today) })),
    loadExerciseHoldingCompanies(orgNo, [rows[0].holderId]),
  ];
};

export const startExerciseForCompany = (
  orgNo: string,
  grants: OptionGrantDetails[],
  plans: OptionPlan[],
  holders: InstrumentHolder[],
  today: string,
): DCEvent[] => {
  const rows = exerciseRows(grants, plans, holders, today);
  if (rows.length === 0) {
    return [];
  }
  return [
    updateOptionsState(() => ({ exercise: newExerciseState(rows, false, today) })),
    loadExerciseHoldingCompanies(orgNo),
  ];
};

export const closeExerciseDialog = (): DCEvent =>
  updateOptionsState(() => ({ exercise: undefined }));

const updateExerciseRows = (f: (row: ExerciseRowState) => ExerciseRowState | undefined): DCEvent =>
  updateExercise((state) => ({
    rows: state.rows.map(f).filter((row): row is ExerciseRowState => row !== undefined),
  }));

export const setExerciseProgram = (planId: string | undefined): DCEvent =>
  updateExercise(() => ({ planId }));

export const setExerciseAcknowledgedOnly = (isAcknowledgedOnly: boolean): DCEvent =>
  updateExercise(() => ({ isAcknowledgedOnly }));

export const exerciseProgramFilters = (plans: OptionPlan[]): ProgramFilter[] => [
  { label: { key: "options.all-programs" } },
  ...plans.map((plan) => ({ planId: plan.optionPlanId, label: { translated: plan.name } })),
];

export const hasAcknowledgedExerciseRows = (state: ExerciseState): boolean =>
  state.rows.some((row) => row.requestId !== undefined);

const matchesExerciseSearch = (row: ExerciseRowState, query: string): boolean =>
  query === "" ||
  row.holderName.toLowerCase().includes(query) ||
  row.planName.toLowerCase().includes(query);

export const visibleExerciseRows = (state: ExerciseState): ExerciseRowState[] => {
  const query = state.search.inputValue.trim().toLowerCase();
  return state.rows.filter(
    (row) =>
      (state.planId === undefined || row.planId === state.planId) &&
      (!state.isAcknowledgedOnly || row.requestId !== undefined) &&
      matchesExerciseSearch(row, query),
  );
};

export const selectedExerciseRows = (state: ExerciseState): ExerciseRowState[] =>
  state.rows.filter((row) => row.isSelected);

export const setExerciseRowSelected = (trancheId: string, isSelected: boolean): DCEvent =>
  updateExerciseRows((row) =>
    row.trancheId !== trancheId
      ? row
      : row.isSplit && !isSelected
        ? undefined
        : { ...row, isSelected },
  );

export const setAllExerciseRowsSelected = (state: ExerciseState, isSelected: boolean): DCEvent => {
  const visibleTrancheIds = new Set(visibleExerciseRows(state).map((row) => row.trancheId));
  return updateExerciseRows((row) =>
    !visibleTrancheIds.has(row.trancheId)
      ? row
      : row.isSplit && !isSelected
        ? undefined
        : { ...row, isSelected },
  );
};

export const exerciseQuantities: ExerciseQuantity[] = ["VESTED", "GRANT", "MANUAL"];

export const exerciseQuantityLabel = (quantity: ExerciseQuantity): TMessage => {
  switch (quantity) {
    case "VESTED":
      return { key: "options.exercise-quantity-vested" };
    case "GRANT":
      return { key: "options.exercise-quantity-grant" };
    case "MANUAL":
      return { key: "options.exercise-quantity-manual" };
  }
};

export const exerciseQuantityIcon = (quantity: ExerciseQuantity): IconName => {
  switch (quantity) {
    case "VESTED":
      return "steps";
    case "GRANT":
      return "seal-check";
    case "MANUAL":
      return "text-box";
  }
};

export const setExerciseQuantity = (key: string, quantity: ExerciseQuantity): DCEvent =>
  updateExerciseRows((row) =>
    row.key !== key
      ? row
      : quantity === "MANUAL"
        ? { ...row, quantity }
        : {
            ...row,
            quantity,
            count: exerciseCountField(
              quantity === "VESTED" ? row.vestedRemaining : row.grantRemaining,
            ),
          },
  );

export const setExerciseEmployee = (key: string, isEmployee: boolean): DCEvent =>
  updateExerciseRows((row) => (row.key === key ? { ...row, isEmployee } : row));

export const setExerciseShareType = (key: string, shareTypeId: number): DCEvent =>
  updateExerciseRows((row) => (row.key === key ? { ...row, shareTypeId } : row));

const splitRowsOf = (state: ExerciseState, key: string): ExerciseRowState[] => {
  const row = state.rows.find((candidate) => candidate.key === key);
  const company = row && holdingCompaniesOf(state, row.holderId)[0];
  if (row === undefined || company === undefined || isSplitTranche(state, row.trancheId)) {
    return state.rows;
  }
  const split: ExerciseRowState = {
    ...row,
    key: newUuid(),
    isSplit: true,
    isSelected: true,
    quantity: "MANUAL",
    count: exerciseCountField(),
    receiver: companyReceiver(company),
    isEmployee: false,
    requestId: undefined,
    requestedCount: undefined,
  };
  return state.rows.flatMap((other) =>
    other.key === key
      ? [
          {
            ...other,
            isSelected: true,
            quantity: "MANUAL" as const,
            receiver: { type: "HOLDER" as const },
          },
          split,
        ]
      : [other],
  );
};

export const chooseExerciseReceiver = (key: string, choice: ExerciseReceiverChoice): DCEvent =>
  updateExercise((state) => {
    const row = state.rows.find((candidate) => candidate.key === key);
    if (row === undefined) {
      return {};
    }
    if (choice.type === "SELF_AND_HOLDING") {
      return { rows: splitRowsOf(state, key) };
    }
    const receiver: ExerciseReceiver =
      choice.type === "COMPANY" ? companyReceiver(choice.company) : { type: "HOLDER" };
    return {
      rows: state.rows
        .filter((other) => row.isSplit || !(other.isSplit && other.trancheId === row.trancheId))
        .map((other) => (other.key === key ? { ...other, receiver } : other)),
    };
  });

export const exercisableForRow = (
  row: ExerciseRowState,
  grants: OptionGrantDetails[],
  date: string | undefined,
): number => {
  const tranche = findTranche(grants, row.trancheId);
  return tranche === undefined || date === undefined ? 0 : exercisableOn(tranche, date);
};

export const exerciseAmount = (row: ExerciseRowState): number =>
  (row.count.parseInputValue() ?? 0) * row.strikePrice;

export const exerciseIssues = (
  state: ExerciseState,
  grants: OptionGrantDetails[],
  today: string,
): TMessage[] => {
  const date = state.exerciseDate.validatedValue;
  if (date === undefined) {
    return [];
  }
  if (date > today) {
    return [{ key: "options.exercise-date-in-future" }];
  }
  return Object.values(groupBy(selectedExerciseRows(state), (row) => row.trancheId)).flatMap(
    (rows) => {
      const requested = rows.reduce((acc, row) => acc + (row.count.parseInputValue() ?? 0), 0);
      const available = exercisableForRow(rows[0], grants, date);
      return requested > available
        ? [
            {
              key: "options.exercise-above-exercisable",
              params: {
                holder: rows[0].holderName,
                program: rows[0].program,
                requested: { type: "number", value: requested },
                available: { type: "number", value: available },
              },
            } satisfies TMessage,
          ]
        : [];
    },
  );
};

export const exerciseRequestDifferences = (state: ExerciseState): TMessage[] =>
  Object.values(groupBy(selectedExerciseRows(state), (row) => row.trancheId)).flatMap((rows) => {
    const requested = rows.find((row) => row.requestedCount !== undefined)?.requestedCount;
    const exercised = rows.reduce((acc, row) => acc + (row.count.parseInputValue() ?? 0), 0);
    return requested === undefined || exercised === requested
      ? []
      : [
          {
            key: "options.exercise-differs-from-request",
            params: {
              holder: rows[0].holderName,
              program: rows[0].program,
              requested: { type: "number", value: requested },
              exercised: { type: "number", value: exercised },
            },
          } satisfies TMessage,
        ];
  });

export const exerciseComplete = (
  state: ExerciseState,
  grants: OptionGrantDetails[],
  today: string,
): boolean => {
  const rows = selectedExerciseRows(state);
  return (
    rows.length > 0 &&
    state.exerciseDate.validatedValue !== undefined &&
    rows.every((row) => {
      const count = row.count.parseInputValue();
      return (
        count !== undefined &&
        Number.isInteger(count) &&
        count > 0 &&
        (!row.needsShareClass || row.shareTypeId !== undefined)
      );
    }) &&
    exerciseIssues(state, grants, today).length === 0
  );
};

export const exerciseCommand = (state: ExerciseState): ExerciseOptionsCommand | undefined => {
  const exerciseDate = state.exerciseDate.validatedValue;
  if (exerciseDate === undefined) {
    return undefined;
  }
  return {
    commandType: "EXERCISE_OPTIONS",
    exerciseDate,
    exercises: selectedExerciseRows(state).map((row, index, rows) => ({
      exerciseId: row.key,
      grantId: row.grantId,
      trancheId: row.trancheId,
      count: row.count.toNumber() ?? 0,
      shareTypeId: row.needsShareClass ? row.shareTypeId : undefined,
      receiver: row.receiver,
      isEmployee: row.isEmployee,
      requestId:
        rows.findIndex((other) => other.requestId === row.requestId) === index
          ? row.requestId
          : undefined,
    })),
    remarks: state.remarks.inputValue.trim() || undefined,
    attachmentReference: state.attachmentReference,
  };
};

export const stageExerciseFiles = (files: File[]): DCEvent =>
  updateExercise((state) => ({
    stagedFiles: [...state.stagedFiles, ...files.map((file) => ({ id: newUuid(), file }))],
  }));

export const removeStagedExerciseFile = (id: string): DCEvent =>
  updateExercise((state) => ({
    stagedFiles: state.stagedFiles.filter((staged) => staged.id !== id),
  }));

export const continueToShareIssue = (
  orgNo: string,
  state: ExerciseState,
  grants: OptionGrantDetails[],
  today: string,
): DCEvent[] => {
  const command = exerciseCommand(state);
  if (command === undefined || !exerciseComplete(state, grants, today)) {
    return [];
  }
  const rows = selectedExerciseRows(state).map((row) => ({
    key: row.key,
    holderName: row.holderName,
    program: row.program,
    receiverName: exerciseReceiverName(state, row),
    shareTypeId: row.shareTypeId,
    count: row.count.toNumber() ?? 0,
    strikePrice: row.strikePrice,
    isEmployee: row.isEmployee,
  }));
  return [
    updateOptionsState(() => ({
      exercise: undefined,
      exerciseIssue: {
        orgNo,
        command,
        rows,
        files: state.stagedFiles.map((staged) => staged.file),
      },
    })),
    navigate(exerciseIssueUrl(orgNo)),
  ];
};

export const setExerciseStep = (step: ExerciseStep): DCEvent =>
  updateExercise((state) => ({
    step,
    method:
      step === "issue" && documentedExerciseBlocker(state) !== undefined ? "direct" : state.method,
  }));

export const setExerciseMethod = (method: ExerciseMethod): DCEvent =>
  updateExercise(() => ({ method }));

export const setExerciseProjectName = (projectName: string): DCEvent =>
  updateExercise(() => ({ projectName }));

export const documentedExerciseBlocker = (state: ExerciseState): TMessage | undefined => {
  const names = [
    ...new Set(
      selectedExerciseRows(state)
        .filter((row) => row.shareholderId === undefined)
        .map((row) => row.holderName),
    ),
  ];
  return names.length === 0
    ? undefined
    : {
        key: "options.exercise-documented-no-shareholder",
        params: { names: { type: "list", value: names }, count: names.length },
      };
};

const exerciseMethodLabel = (method: ExerciseMethod): TMessage => {
  switch (method) {
    case "documented":
      return { key: "header.dbot" };
    case "direct":
      return { key: "options.exercise-direct" };
  }
};

const exerciseMethodDescription = (method: ExerciseMethod): TMessage => {
  switch (method) {
    case "documented":
      return { key: "options.exercise-documented-description" };
    case "direct":
      return { key: "options.exercise-direct-description" };
  }
};

export const prepExerciseMethodRadios = (
  state: ExerciseState,
  dispatch: EventDispatcher,
): RadioButtonGroupProps<ExerciseMethod> => {
  const isBlocked = documentedExerciseBlocker(state) !== undefined;
  return {
    name: "exercise-method",
    options: ["documented", "direct"],
    selected: state.method,
    idFn: (method) => method,
    labelFn: exerciseMethodLabel,
    descriptionFn: exerciseMethodDescription,
    disabled: state.isCreatingProject,
    disabledFn: (method) => method === "documented" && isBlocked,
    onChange: (_, method) => dispatch([setExerciseMethod(method)]),
  };
};

const issueReceiver = (state: ExerciseState, row: ExerciseRowState): IssueReceiver => {
  const shareholderId =
    row.receiver.type === "HOLDER" ? row.shareholderId : receiverCompany(state, row)?.shareholderId;
  if (shareholderId !== undefined) {
    return { type: "SHAREHOLDER", shareholderId };
  }
  return {
    type: "NOT_SHAREHOLDER",
    key:
      row.receiver.type === "HOLDER"
        ? `holder:${row.holderId}`
        : `company:${row.receiver.organisationNumber}`,
  };
};

export const exerciseIssueSummary = (
  state: ExerciseState,
  figures: ShareCapitalFigures,
  currency: string,
): ShareIssueSummaryRow[] =>
  shareIssueSummary(
    selectedExerciseRows(state).map((row) => ({
      count: row.count.toNumber() ?? 0,
      pricePerShare: row.strikePrice,
      receiver: issueReceiver(state, row),
    })),
    figures,
    currency,
  );

export const exerciseProjectName = (state: ExerciseState, t: TFunction): string => {
  if (state.projectName !== undefined) {
    return state.projectName;
  }
  const names = [...new Set(selectedExerciseRows(state).map((row) => row.holderName))];
  const name = `${state.exerciseDate.validatedValue ?? ""} ${t("decisionType.optionExercise.action")}`;
  return names.length === 1 ? `${name} – ${names[0]}` : name;
};

export const exerciseProjectSources = (): AiPromptSource[] =>
  [DecisionType.optionExercise, DecisionType.increaseOfShareCapital].map((decisionType) => ({
    id: decisionType,
    label: getDecisionActionLabel(decisionType),
    icon: getDecisionIcon(decisionType),
    bg: "alt1",
  }));

const documentedReceiver = (
  state: ExerciseState,
  row: ExerciseRowState,
): HoldingCompanyReceiver | undefined => {
  const company = receiverCompany(state, row);
  return (
    company && {
      organisationNumber: company.organisationNumber,
      name: company.name,
      shareholderId: company.shareholderId,
    }
  );
};

const programExercise = (
  state: ExerciseState,
  row: ExerciseRowState,
  shareTypes: ShareType[],
): ProgramExercise[] => {
  const shareType = shareTypes.find((candidate) => candidate.sharetypeId === row.shareTypeId);
  const count = row.count.toNumber();
  if (row.shareholderId === undefined || shareType === undefined || count === undefined) {
    return [];
  }
  return [
    {
      grantId: row.grantId,
      trancheId: row.trancheId,
      holderId: row.holderId,
      holderName: row.holderName,
      shareholderId: row.shareholderId,
      shareTypeId: shareType.sharetypeId,
      shareTypeCode: shareType.shareTypeCode,
      strikePrice: row.strikePrice,
      count,
      isEmployee: row.isEmployee,
      receiver: documentedReceiver(state, row),
    },
  ];
};

export const documentedExercise = (
  state: ExerciseState,
  shareTypes: ShareType[],
): OptionExercise => ({
  type: DecisionType.optionExercise,
  exerciseDate: state.exerciseDate.validatedValue,
  programs: Object.values(groupBy(selectedExerciseRows(state), (row) => row.planId)).map(
    (rows) => ({
      planId: rows[0].planId,
      programName: rows[0].planName,
      exercises: rows.flatMap((row) => programExercise(state, row, shareTypes)),
    }),
  ),
});

export const submitDocumentedExercise = ({
  orgNo,
  state,
  grants,
  shareTypes,
  today,
  t,
}: {
  orgNo: string;
  state: ExerciseState;
  grants: OptionGrantDetails[];
  shareTypes: ShareType[];
  today: string;
  t: TFunction;
}): DCEvent[] => {
  if (!exerciseComplete(state, grants, today) || documentedExerciseBlocker(state) !== undefined) {
    return [];
  }
  return [
    updateExercise(() => ({ isCreatingProject: true })),
    createOptionsProject(
      orgNo,
      exerciseProjectName(state, t),
      documentedExercise(state, shareTypes),
      {
        isGeneratedName: state.projectName === undefined,
        onSuccess: [closeExerciseDialog()],
        onFailure: [updateExercise(() => ({ isCreatingProject: false }))],
      },
    ),
  ];
};

const updateExerciseIssue = (
  f: (draft: ExerciseIssueDraft) => Partial<ExerciseIssueDraft>,
): DCEvent =>
  updateOptionsState((state) =>
    state?.exerciseIssue === undefined
      ? {}
      : { exerciseIssue: { ...state.exerciseIssue, ...f(state.exerciseIssue) } },
  );

export const submitExerciseIssue = (
  draft: ExerciseIssueDraft,
  form: ExerciseIssueForm,
): DCEvent[] =>
  validateExerciseIssueForm(form) ? sendExerciseIssue(draft, exerciseShareIssue(form)) : [];

const sendExerciseIssue = (draft: ExerciseIssueDraft, issue: ExerciseShareIssue): DCEvent[] => [
  updateExerciseIssue(() => ({ submitResult: pending() })),
  instrumentCommandApiClient.send<ExerciseOptionsResult>(
    draft.orgNo,
    { ...draft.command, issue },
    {
      onSuccess: (res) => [
        ...(draft.files.length > 0
          ? [
              optionApiClient.uploadExerciseAttachments(
                draft.orgNo,
                draft.command.attachmentReference,
                draft.files,
                { onSuccess: () => [], onFailure: () => [showGeneralError()] },
              ),
            ]
          : []),
        updateOptionsState(() => ({ exerciseIssue: undefined })),
        showAlert({ key: "options.exercise-issue-registered" }, "success"),
        navigate(
          res.data.transactionLogId === undefined
            ? adminShareholdersTransactions(draft.orgNo)
            : adminShareholdersTransaction(draft.orgNo, res.data.transactionLogId),
        ),
      ],
      onFailure: (err) => [
        updateExerciseIssue(() => ({ submitResult: failure(exerciseIssueFailureMessage(err)) })),
      ],
    },
  ),
];

export const startDeleteGrant = (
  tranche: OptionTranche,
  holderName: string,
  program: TMessage,
): DCEvent =>
  updateOptionsState(() => ({
    deleteGrant: {
      grantId: tranche.grantId,
      trancheId: tranche.optionTrancheId,
      holderName,
      program,
      totalOptions: tranche.totalOptions,
    },
  }));

export const closeDeleteGrantDialog = (): DCEvent =>
  updateOptionsState(() => ({ deleteGrant: undefined }));

export type DeleteGrantCommand = {
  commandType: "DELETE_GRANT";
  grantId: string;
  trancheId: string;
};

export const deleteGrantCommand = (state: DeleteGrantState): DeleteGrantCommand => ({
  commandType: "DELETE_GRANT",
  grantId: state.grantId,
  trancheId: state.trancheId,
});

export const submitDeleteGrant = (orgNo: string, state: DeleteGrantState): DCEvent[] => [
  updateOptionsState((prev) =>
    prev?.deleteGrant === undefined
      ? {}
      : { deleteGrant: { ...prev.deleteGrant, submitResult: pending() } },
  ),
  instrumentCommandApiClient.send(orgNo, deleteGrantCommand(state), {
    onSuccess: () => [
      closeDeleteGrantDialog(),
      showAlert({ key: "options.grant-deleted" }, "success"),
      loadHolders(orgNo),
      loadPlans(orgNo),
      loadGrants(orgNo),
    ],
    onFailure: (err) => [
      updateOptionsState((prev) =>
        prev?.deleteGrant === undefined
          ? {}
          : {
              deleteGrant: {
                ...prev.deleteGrant,
                submitResult: failure(commandFailureMessage(err)),
              },
            },
      ),
    ],
  }),
];

// Backend re-validates magic bytes; this only gates the file picker client-side.
export const optionAttachmentUploadConfig: FileUploadConfig = {
  supportedFileTypes: [
    {
      extensions: [
        "pdf",
        "doc",
        "docx",
        "xls",
        "xlsx",
        "ppt",
        "pptx",
        "png",
        "jpg",
        "jpeg",
        "gif",
        "txt",
        "csv",
      ],
      magicBytes: [],
    },
  ],
  maxFileSize: 5120 * 5120,
};

export const planAttachments = (plans: OptionPlan[], planId: string): UploadedAttachment[] =>
  plans.find((plan) => plan.optionPlanId === planId)?.attachments ?? [];

export const planAttachmentUrl = (orgNo: string, planId: string, attachmentId: string): string =>
  `/api/instruments/${orgNo}/options/plans/${planId}/attachments/${attachmentId}`;

export const uploadPlanAttachments = (orgNo: string, planId: string, files: File[]): DCEvent =>
  optionApiClient.uploadPlanAttachments(orgNo, planId, files, {
    onSuccess: () => [loadPlans(orgNo)],
    onFailure: () => [showGeneralError()],
  });

export const deletePlanAttachment = (
  orgNo: string,
  planId: string,
  attachmentId: string,
): DCEvent =>
  optionApiClient.deletePlanAttachment(orgNo, planId, attachmentId, {
    onSuccess: () => [loadPlans(orgNo)],
    onFailure: () => [showGeneralError()],
  });

export const trancheAttachmentUrl = (
  orgNo: string,
  trancheId: string,
  attachmentId: string,
): string => `/api/instruments/${orgNo}/options/tranches/${trancheId}/attachments/${attachmentId}`;

export const uploadTrancheAttachments = (
  orgNo: string,
  trancheId: string,
  files: File[],
): DCEvent =>
  httpPost({
    url: `/instruments/${orgNo}/options/tranches/${trancheId}/attachments`,
    data: toFormData(files),
    onSuccess: () => [loadGrants(orgNo)],
    onFailure: () => [showGeneralError()],
  });

export const deleteTrancheAttachment = (
  orgNo: string,
  trancheId: string,
  attachmentId: string,
): DCEvent =>
  httpDelete({
    url: `/instruments/${orgNo}/options/tranches/${trancheId}/attachments/${attachmentId}`,
    onSuccess: () => [loadGrants(orgNo)],
    onFailure: () => [showGeneralError()],
  });

export const openAttachmentPreview = (filename: string, url: string): DCEvent =>
  updateOptionsState(() => ({ attachmentPreview: { filename, url } }));

export const closeAttachmentPreview = (): DCEvent =>
  updateOptionsState(() => ({ attachmentPreview: undefined }));

const trancheVestingEntries = (tranche: OptionTranche): VestingScheduleEntry[] => {
  switch (tranche.vesting.vestingType) {
    case "PERIODIC":
      return periodicVestingSchedule({
        totalOptions: tranche.totalOptions,
        grantDate: tranche.grantDate,
        periodMonths: tranche.vesting.periodMonths,
        cliffMonths: tranche.vesting.cliffMonths,
        frequency: tranche.vesting.frequency,
      });
    case "SPECIFIC_DATES":
      return tranche.vesting.entries.map((entry) => ({
        date: entry.vestingDate,
        options: entry.optionsAmount,
        fractionOfTotal: tranche.totalOptions > 0 ? entry.optionsAmount / tranche.totalOptions : 0,
      }));
    case "PERFORMANCE":
      return [];
  }
};

// Moves `amount` options from the schedule dates strictly after `effectiveDate`
// into a step at the effective date, consuming the latest dates first — the
// vesting tail collapses forward. The cumulative total is preserved, so the
// curve still ends at the granted amount.
const pullVestingForward = (
  entries: VestingScheduleEntry[],
  effectiveDate: string,
  amount: number,
): VestingScheduleEntry[] => {
  const future = entries
    .filter((entry) => entry.date > effectiveDate)
    .sort((a, b) => b.date.localeCompare(a.date));
  const consumed = new Map<string, number>();
  let remaining = amount;
  for (const entry of future) {
    if (remaining <= 0) break;
    const take = Math.min(entry.options, remaining);
    consumed.set(entry.date, take);
    remaining -= take;
  }
  const pulled = amount - remaining;
  const reduced = entries
    .map((entry) => ({ ...entry, options: entry.options - (consumed.get(entry.date) ?? 0) }))
    .filter((entry) => entry.options > 0);
  const stepped = reduced.some((entry) => entry.date === effectiveDate)
    ? reduced.map((entry) =>
        entry.date === effectiveDate ? { ...entry, options: entry.options + pulled } : entry,
      )
    : pulled > 0
      ? [...reduced, { date: effectiveDate, options: pulled, fractionOfTotal: 0 }]
      : reduced;
  return stepped.sort((a, b) => a.date.localeCompare(b.date));
};

// Reshapes scheduled vesting for the chart so accelerated options vest at the
// acceleration's effective date instead of their scheduled future dates. Only
// the chart uses this; the vested count folds acceleration in separately (see
// trancheNumbers), so trancheVestingEntries itself stays the raw schedule.
export const accelerateVestingEntries = (
  entries: VestingScheduleEntry[],
  accelerations: TrancheAcceleration[],
): VestingScheduleEntry[] => {
  const total = entries.reduce((acc, entry) => acc + entry.options, 0);
  if (total === 0 || accelerations.length === 0) {
    return entries;
  }
  return [...accelerations]
    .sort((a, b) => a.effectiveDate.localeCompare(b.effectiveDate))
    .reduce(
      (current, accel) =>
        pullVestingForward(current, accel.effectiveDate, accel.unvestedAccelerated),
      entries,
    )
    .map((entry) => ({ ...entry, fractionOfTotal: entry.options / total }));
};

// Reshapes the schedule for a single cancellation: unvested-cancelled options
// are dropped from the scheduled future dates (latest first, mirroring
// pullVestingForward), so the curve tops out lower; vested-cancelled options are
// removed as a downward step at the effective date, so the cumulative curve dips
// there.
const applyCancellation = (
  entries: VestingScheduleEntry[],
  effectiveDate: string,
  vestedCancelled: number,
  unvestedCancelled: number,
): VestingScheduleEntry[] => {
  const future = entries
    .filter((entry) => entry.date > effectiveDate)
    .sort((a, b) => b.date.localeCompare(a.date));
  const consumed = new Map<string, number>();
  let remaining = unvestedCancelled;
  for (const entry of future) {
    if (remaining <= 0) break;
    const take = Math.min(entry.options, remaining);
    consumed.set(entry.date, take);
    remaining -= take;
  }
  const reduced = entries
    .map((entry) => ({ ...entry, options: entry.options - (consumed.get(entry.date) ?? 0) }))
    .filter((entry) => entry.options > 0);
  const stepped =
    vestedCancelled <= 0
      ? reduced
      : reduced.some((entry) => entry.date === effectiveDate)
        ? reduced.map((entry) =>
            entry.date === effectiveDate
              ? { ...entry, options: entry.options - vestedCancelled }
              : entry,
          )
        : [...reduced, { date: effectiveDate, options: -vestedCancelled, fractionOfTotal: 0 }];
  return stepped.sort((a, b) => a.date.localeCompare(b.date));
};

// Reshapes scheduled vesting for the chart so cancellations show on the curve.
// Only the chart uses this; the outstanding/vested counts fold cancellation in
// separately (see trancheNumbers), so trancheVestingEntries stays the raw
// schedule.
export const cancelVestingEntries = (
  entries: VestingScheduleEntry[],
  cancellations: TrancheCancellation[],
): VestingScheduleEntry[] => {
  const total = entries.reduce((acc, entry) => acc + entry.options, 0);
  if (total === 0 || cancellations.length === 0) {
    return entries;
  }
  return [...cancellations]
    .sort((a, b) => a.effectiveDate.localeCompare(b.effectiveDate))
    .reduce(
      (current, cancellation) =>
        applyCancellation(
          current,
          cancellation.effectiveDate,
          cancellation.vestedCancelled,
          cancellation.unvestedCancelled,
        ),
      entries,
    )
    .map((entry) => ({ ...entry, fractionOfTotal: entry.options / total }));
};

// Time-based vesting is pure math over the vesting config; performance vesting
// has no schedule and counts as unvested until its flow is specified.
export const vestedToDate = (tranche: OptionTranche, today: string): number =>
  trancheVestingEntries(tranche)
    .filter((entry) => entry.date <= today)
    .reduce((acc, entry) => acc + entry.options, 0);

export type OptionsRowNumbers = {
  granted: number;
  exercised: number;
  cancelled: number;
  expired: number;
  outstanding: number;
  vested: number;
};

export type OptionsTableRow = OptionsRowNumbers & {
  id: string;
  kind: "holder" | "tranche";
  holderId: string;
  holderName?: string;
  holderIcon?: "buildings" | "user-square";
  shareholderId?: number;
  expandable: boolean;
  expanded: boolean;
  program: TMessage;
  isMultipleProgram: boolean;
  shareTypeId?: number;
  strike?: number;
  grantDate?: string;
  expiryDate?: string;
  // Set only on rows that stand for one tranche, so the row actions know what to edit.
  trancheId?: string;
};

const activeExercises = (tranche: OptionTranche): TrancheExercise[] =>
  (tranche.exercises ?? []).filter((exercise) => !exercise.isReversed);

export const trancheNumbers = (tranche: OptionTranche, today: string): OptionsRowNumbers => {
  const granted = tranche.totalOptions;
  const effective = (tranche.cancellations ?? []).filter((c) => c.effectiveDate <= today);
  const cancelledVested = effective.reduce((acc, c) => acc + c.vestedCancelled, 0);
  const cancelled = effective.reduce((acc, c) => acc + c.vestedCancelled + c.unvestedCancelled, 0);
  const accelerated = (tranche.accelerations ?? [])
    .filter((a) => a.effectiveDate <= today)
    .reduce((acc, a) => acc + a.unvestedAccelerated, 0);
  const exercised = activeExercises(tranche)
    .filter((exercise) => exercise.exerciseDate <= today)
    .reduce((acc, exercise) => acc + exercise.count, 0);
  const remaining = granted - exercised - cancelled;
  const expired = tranche.expirationDate < today ? remaining : 0;
  const outstanding = remaining - expired;
  const vested = Math.max(
    0,
    Math.min(vestedToDate(tranche, today) + accelerated - exercised - cancelledVested, outstanding),
  );
  return { granted, exercised, cancelled, expired, outstanding, vested };
};

export const exercisableOn = (tranche: OptionTranche, date: string): number => {
  const laterDates = [
    ...activeExercises(tranche).map((exercise) => exercise.exerciseDate),
    ...(tranche.cancellations ?? []).map((cancellation) => cancellation.effectiveDate),
  ].filter((later) => later > date && later <= tranche.expirationDate);
  return Math.min(...[date, ...laterDates].map((on) => trancheNumbers(tranche, on).vested));
};

export const awaitingShares = (tranche: OptionTranche, today: string): number =>
  activeExercises(tranche)
    .filter(
      (exercise) =>
        exercise.exerciseDate <= today &&
        (exercise.issue === undefined ||
          exercise.issue.isPending ||
          exercise.issue.issueDate > today),
    )
    .reduce((acc, exercise) => acc + exercise.count, 0);

export type ExerciseStatus =
  | "AWAITING_SHARES"
  | "PENDING_REGISTRATION"
  | "SHARES_ISSUED"
  | "REVERSED";

export type ExerciseStage = "REQUESTED" | "ACKNOWLEDGED" | "COMPLETED" | "DECLINED" | "REVERSED";

export type ExerciseTab = "ALL" | "REQUESTED" | "ACKNOWLEDGED" | "COMPLETED";

export type ExerciseRequestView = {
  request: TrancheExerciseRequest;
  grantId: string;
  trancheId: string;
  holderId: string;
  holderName: string;
  holderEmail?: string;
  planId: string;
  planName: string;
  program: TMessage;
  shareTypeId?: number;
  strikePrice: number;
  window?: ExerciseWindow;
  registeredBy?: string;
  decision?: ExerciseRequestStatusChange;
  exercise?: TrancheExercise;
};

type ExerciseListRowBase = {
  id: string;
  holderName: string;
  planId: string;
  planName: string;
  program: TMessage;
  stage: ExerciseStage;
  shareTypeId?: number;
  request?: ExerciseRequestView;
};

export type ExerciseListRow = ExerciseListRowBase &
  (
    | { kind: "exercise"; exercise: TrancheExercise; status: ExerciseStatus }
    | { kind: "request"; request: ExerciseRequestView }
  );

export type ExercisesFilter = {
  tab: ExerciseTab;
  search: StringFieldModel;
  planId?: string;
};

export const exerciseStatus = (exercise: TrancheExercise): ExerciseStatus => {
  if (exercise.isReversed) {
    return "REVERSED";
  }
  if (exercise.issue === undefined) {
    return "AWAITING_SHARES";
  }
  return exercise.issue.isPending ? "PENDING_REGISTRATION" : "SHARES_ISSUED";
};

export const exerciseStage = (status: ExerciseStatus): ExerciseStage => {
  switch (status) {
    case "AWAITING_SHARES":
    case "PENDING_REGISTRATION":
      return "ACKNOWLEDGED";
    case "SHARES_ISSUED":
      return "COMPLETED";
    case "REVERSED":
      return "REVERSED";
  }
};

const listedRequestStatuses = new Set<ExerciseRequestStatus>([
  "REQUESTED",
  "ACKNOWLEDGED",
  "REJECTED",
  "EXERCISED",
]);

const requestStages: Partial<Record<ExerciseRequestStatus, ExerciseStage>> = {
  REQUESTED: "REQUESTED",
  ACKNOWLEDGED: "ACKNOWLEDGED",
  REJECTED: "DECLINED",
};

export const exerciseRequestStage = (status: ExerciseRequestStatus): ExerciseStage | undefined =>
  requestStages[status];

export const exerciseWindowOn = (
  plan: OptionPlan | undefined,
  date: string,
): ExerciseWindow | undefined =>
  activeExerciseWindows(plan).find((window) => window.fromDate <= date && date <= window.toDate);

export const administratorName = (name: string | undefined): TMessage | undefined =>
  name === undefined ? undefined : { key: "options.name-is-administrator", params: { name } };

export const requesterName = (view: ExerciseRequestView): TMessage | undefined =>
  view.request.isHolderRequest && view.registeredBy !== undefined
    ? { translated: view.registeredBy }
    : administratorName(view.registeredBy);

export const exerciseRequestViews = (
  grants: OptionGrantDetails[],
  plans: OptionPlan[],
  holders: InstrumentHolder[],
): ExerciseRequestView[] => {
  const planById = new Map(plans.map((p) => [p.optionPlanId, p]));
  const holderById = new Map(holders.map((h) => [h.instrumentHolderId, h]));
  return grants.flatMap(({ grant, tranches }) =>
    tranches.flatMap((tranche) =>
      (tranche.exerciseRequests ?? [])
        .filter((request) => listedRequestStatuses.has(request.status))
        .map((request) => {
          const plan = planById.get(grant.planId);
          const holder = holderById.get(grant.holderId);
          return {
            request,
            grantId: grant.optionGrantId,
            trancheId: tranche.optionTrancheId,
            holderId: grant.holderId,
            holderName: holder?.name ?? "",
            holderEmail: holder?.email,
            planId: grant.planId,
            planName: plan?.name ?? "–",
            program: programLabel(plan?.name ?? "–", tranche.name),
            shareTypeId: tranche.shareTypeId,
            strikePrice: tranche.strikePrice,
            window: exerciseWindowOn(plan, request.requestDate),
            registeredBy: request.history[0]?.changedByName,
            decision: request.history.findLast(
              (change) => change.status === "ACKNOWLEDGED" || change.status === "REJECTED",
            ),
            exercise: (tranche.exercises ?? []).find(
              (exercise) => exercise.exerciseId === request.exerciseId,
            ),
          };
        }),
    ),
  );
};

const exerciseRowDate = (row: ExerciseListRow): string =>
  row.kind === "exercise" ? row.exercise.exerciseDate : row.request.request.requestDate;

export const exerciseListRows = (
  grants: OptionGrantDetails[],
  plans: OptionPlan[],
  holders: InstrumentHolder[],
): ExerciseListRow[] => {
  const requests = exerciseRequestViews(grants, plans, holders);
  const requestByExerciseId = new Map(
    requests.flatMap((view) =>
      view.request.exerciseId === undefined ? [] : [[view.request.exerciseId, view] as const],
    ),
  );
  const planNameById = new Map(plans.map((p) => [p.optionPlanId, p.name]));
  const holderNameById = new Map(holders.map((h) => [h.instrumentHolderId, h.name]));
  const exerciseRows = grants.flatMap(({ grant, tranches }) =>
    tranches.flatMap((tranche) =>
      (tranche.exercises ?? []).map(
        (exercise): ExerciseListRow => ({
          kind: "exercise",
          id: exercise.exerciseId,
          exercise,
          holderName: holderNameById.get(grant.holderId) ?? "",
          planId: grant.planId,
          planName: planNameById.get(grant.planId) ?? "–",
          program: programLabel(planNameById.get(grant.planId) ?? "–", tranche.name),
          shareTypeId: exercise.shareTypeId,
          status: exerciseStatus(exercise),
          stage: exerciseStage(exerciseStatus(exercise)),
          request: requestByExerciseId.get(exercise.exerciseId),
        }),
      ),
    ),
  );
  const requestRows = requests.flatMap((view): ExerciseListRow[] => {
    const stage = exerciseRequestStage(view.request.status);
    return stage === undefined
      ? []
      : [
          {
            kind: "request",
            id: view.request.requestId,
            request: view,
            holderName: view.holderName,
            planId: view.planId,
            planName: view.planName,
            program: view.program,
            shareTypeId: view.shareTypeId,
            stage,
          },
        ];
  });
  return [...requestRows, ...exerciseRows].sort(
    (a, b) =>
      exerciseRowDate(b).localeCompare(exerciseRowDate(a)) ||
      a.holderName.localeCompare(b.holderName),
  );
};

export const exerciseTabLabelKeys: Record<Exclude<ExerciseTab, "ALL">, TMessageKey> = {
  REQUESTED: "options.exercise-status-requested",
  ACKNOWLEDGED: "options.exercise-status-acknowledged",
  COMPLETED: "options.exercise-status-completed",
};

export const exerciseStageLabelKeys: Record<ExerciseStage, TMessageKey> = {
  REQUESTED: "options.exercise-status-pending-acknowledgement",
  ACKNOWLEDGED: "options.exercise-status-acknowledged",
  COMPLETED: "options.exercise-status-completed",
  DECLINED: "options.exercise-status-declined",
  REVERSED: "options.exercise-status-reversed",
};

const exerciseStageColors: Record<ExerciseStage, TagColor> = {
  REQUESTED: "warning",
  ACKNOWLEDGED: "info",
  COMPLETED: "success",
  DECLINED: "error",
  REVERSED: "neutral",
};

export const exerciseStageTag = (stage: ExerciseStage): TagProps => ({
  label: { key: exerciseStageLabelKeys[stage] },
  color: exerciseStageColors[stage],
  compact: true,
});

export const newExercisesFilter = (): ExercisesFilter => ({
  tab: "ALL",
  search: new StringFieldModel(),
});

export const filterExerciseRows = (
  rows: ExerciseListRow[],
  filter: ExercisesFilter,
): ExerciseListRow[] => {
  const query = filter.search.inputValue.trim().toLowerCase();
  return rows.filter(
    (row) =>
      (filter.tab === "ALL" || row.stage === filter.tab) &&
      (filter.planId === undefined || row.planId === filter.planId) &&
      (query === "" ||
        row.holderName.toLowerCase().includes(query) ||
        row.planName.toLowerCase().includes(query)),
  );
};

export const exerciseStageCount = (rows: ExerciseListRow[], stage: ExerciseStage): number =>
  rows.filter((row) => row.stage === stage).length;

const updateExercisesFilter = (f: (filter: ExercisesFilter) => Partial<ExercisesFilter>): DCEvent =>
  updateOptionsState((state) => {
    const filter = state?.exercisesFilter ?? newExercisesFilter();
    return { exercisesFilter: { ...filter, ...f(filter) } };
  });

export const setExercisesTab = (tab: ExerciseTab): DCEvent =>
  updateExercisesFilter(() => ({ tab }));

export const setExercisesPlan = (planId: string | undefined): DCEvent =>
  updateExercisesFilter(() => ({ planId }));

export type ExerciseActionScope = {
  orgNo: string;
  grants: OptionGrantDetails[];
  plans: OptionPlan[];
  holders: InstrumentHolder[];
  today: string;
};

const viewNotificationItem = (view: ExerciseRequestView | undefined): DropdownMenuItems => {
  const notification = view?.decision?.notification;
  return notification === undefined || view?.decision === undefined
    ? []
    : [
        {
          type: "item",
          icon: "envelope",
          label: { key: "options.view-sent-notification" },
          onSelect: [openSentNotification(notification, view.decision.changedAt)],
        },
      ];
};

export const exerciseRequestMenuItems = (
  view: ExerciseRequestView,
  scope: ExerciseActionScope,
): DropdownMenuItems => {
  const { status } = view.request;
  const items: (DropdownMenuItems[number] | false)[] = [
    status === "REQUESTED" && {
      type: "item",
      icon: "edit",
      label: { key: "options.edit-exercise-request" },
      onSelect: [startEditExerciseRequest(view)],
    },
    status === "ACKNOWLEDGED" && {
      type: "item",
      icon: "check-circle",
      label: { key: "options.register-exercise" },
      onSelect: startExerciseForRequest(view, scope),
    },
    ...viewNotificationItem(view),
    (status === "REQUESTED" || status === "ACKNOWLEDGED") && {
      type: "item",
      icon: "trash",
      color: "error",
      label: { key: "options.delete-exercise-request" },
      onSelect: [startDeleteExerciseRequest(view)],
    },
  ];
  return compact(items);
};

export const exerciseRowActions = (
  row: ExerciseListRow,
  scope: ExerciseActionScope,
): RowAction[] => {
  if (row.kind === "request") {
    const items = exerciseRequestMenuItems(row.request, scope);
    return compact<RowAction>([
      row.request.request.status === "REQUESTED" && [
        "button",
        {
          variant: "primary",
          size: "sm",
          label: { key: "manage" },
          onClick: [startExerciseRequestReview(row.request)],
        },
      ],
      items.length > 0 && ["context-menu", items],
    ]);
  }
  const { issue } = row.exercise;
  const candidates: (DropdownMenuItems[number] | false)[] = [
    issue !== undefined && {
      type: "item",
      icon: "file-plus",
      label: { key: "options.open-share-issue" },
      onSelect: [navigate(adminShareholdersTransaction(scope.orgNo, issue.transactionLogId))],
    },
    ...viewNotificationItem(row.request),
    row.status === "AWAITING_SHARES" && {
      type: "item",
      icon: "arrow-counter-clockwise",
      label: { key: "options.reverse-exercise" },
      onSelect: [startReverseExercise(row.exercise, row.holderName, row.program)],
    },
  ];
  const items = compact(candidates);
  return items.length === 0 ? [] : [["context-menu", items]];
};

export type ReverseExerciseState = {
  exerciseId: string;
  holderName: string;
  program: TMessage;
  count: number;
  remarks: StringFieldModel;
  submitResult?: Result<TMessage, TMessage>;
};

export const startReverseExercise = (
  exercise: TrancheExercise,
  holderName: string,
  program: TMessage,
): DCEvent =>
  updateOptionsState(() => ({
    reverseExercise: {
      exerciseId: exercise.exerciseId,
      holderName,
      program,
      count: exercise.count,
      remarks: new StringFieldModel(),
    },
  }));

export const closeReverseExerciseDialog = (): DCEvent =>
  updateOptionsState(() => ({ reverseExercise: undefined }));

const updateReverseExercise = (
  f: (state: ReverseExerciseState) => Partial<ReverseExerciseState>,
): DCEvent =>
  updateOptionsState((prev) =>
    prev?.reverseExercise === undefined
      ? {}
      : { reverseExercise: { ...prev.reverseExercise, ...f(prev.reverseExercise) } },
  );

export const reverseExerciseCommand = (state: ReverseExerciseState): ReverseExerciseCommand => ({
  commandType: "REVERSE_EXERCISE",
  exerciseId: state.exerciseId,
  remarks: state.remarks.inputValue.trim() || undefined,
});

export const submitReverseExercise = (orgNo: string, state: ReverseExerciseState): DCEvent[] => [
  updateReverseExercise(() => ({ submitResult: pending() })),
  instrumentCommandApiClient.send(orgNo, reverseExerciseCommand(state), {
    onSuccess: () => [
      closeReverseExerciseDialog(),
      showAlert({ key: "options.exercise-reversed" }, "success"),
      loadGrants(orgNo),
    ],
    onFailure: (err) => [
      updateReverseExercise(() => ({ submitResult: failure(commandFailureMessage(err)) })),
    ],
  }),
];

export type ExerciseRequestQuantity = "ALL" | "SPECIFY";

export type ExerciseRequestFormState = {
  requestId?: string;
  isPreselected: boolean;
  trancheId?: string;
  requestDate: LocalDateFieldModel;
  quantity: RadioFieldModel<ExerciseRequestQuantity>;
  count: NumberFieldModel;
  submitResult?: Result<TMessage, TMessage>;
};

export type ExerciseRequestCandidate = {
  grantId: string;
  tranche: OptionTranche;
  plan?: OptionPlan;
  holderId: string;
  holderName: string;
  program: TMessage;
};

export type ExerciseRequestSummary = {
  candidate: ExerciseRequestCandidate;
  requestDate?: string;
  available: number;
  count: number;
  cost: number;
  window?: ExerciseWindow;
};

const isOpenRequest = (request: TrancheExerciseRequest): boolean =>
  request.status === "REQUESTED" || request.status === "ACKNOWLEDGED";

export const requestableOptions = (
  tranche: OptionTranche,
  date: string,
  excludingRequestId?: string,
): number => {
  const openRequested = (tranche.exerciseRequests ?? [])
    .filter((request) => isOpenRequest(request) && request.requestId !== excludingRequestId)
    .reduce((acc, request) => acc + request.count, 0);
  return Math.max(0, exercisableOn(tranche, date) - openRequested);
};

export const exerciseRequestCandidates = (
  grants: OptionGrantDetails[],
  plans: OptionPlan[],
  holders: InstrumentHolder[],
): ExerciseRequestCandidate[] => {
  const planById = new Map(plans.map((p) => [p.optionPlanId, p]));
  const holderNameById = new Map(holders.map((h) => [h.instrumentHolderId, h.name]));
  return grants
    .flatMap(({ grant, tranches }) =>
      tranches.map((tranche) => ({
        grantId: grant.optionGrantId,
        tranche,
        plan: planById.get(grant.planId),
        holderId: grant.holderId,
        holderName: holderNameById.get(grant.holderId) ?? "",
        program: programLabel(planById.get(grant.planId)?.name ?? "–", tranche.name),
      })),
    )
    .sort(
      (a, b) =>
        a.holderName.localeCompare(b.holderName) ||
        (a.plan?.name ?? "").localeCompare(b.plan?.name ?? ""),
    );
};

export const selectableRequestCandidates = (
  candidates: ExerciseRequestCandidate[],
  today: string,
): ExerciseRequestCandidate[] =>
  candidates.filter((candidate) => requestableOptions(candidate.tranche, today) > 0);

const newExerciseRequestForm = (
  today: string,
  init: Partial<Pick<ExerciseRequestFormState, "requestId" | "trancheId">> & {
    requestDate?: string;
    count?: number;
  } = {},
): ExerciseRequestFormState => ({
  requestId: init.requestId,
  isPreselected: init.trancheId !== undefined,
  trancheId: init.trancheId,
  requestDate: new LocalDateFieldModel({ required: true, value: init.requestDate ?? today }),
  quantity: new RadioFieldModel<ExerciseRequestQuantity>({
    required: true,
    value: init.count === undefined ? "ALL" : "SPECIFY",
    options: ["ALL", "SPECIFY"],
    idFn: (quantity) => quantity,
    labelFn: (quantity) => ({
      key: quantity === "ALL" ? "options.all-available-options" : "options.specify-quantity",
    }),
  }),
  count: new NumberFieldModel({ isInteger: true, isPositive: true, value: init.count }),
});

export const startExerciseRequest = (today: string, trancheId?: string): DCEvent =>
  updateOptionsState(() => ({ exerciseRequestForm: newExerciseRequestForm(today, { trancheId }) }));

export const startEditExerciseRequest = (view: ExerciseRequestView): DCEvent =>
  updateOptionsState(() => ({
    exerciseRequestForm: newExerciseRequestForm(view.request.requestDate, {
      requestId: view.request.requestId,
      trancheId: view.trancheId,
      requestDate: view.request.requestDate,
      count: view.request.count,
    }),
  }));

export const closeExerciseRequestForm = (): DCEvent =>
  updateOptionsState(() => ({ exerciseRequestForm: undefined }));

const updateExerciseRequestForm = (
  f: (form: ExerciseRequestFormState) => Partial<ExerciseRequestFormState>,
): DCEvent =>
  updateOptionsState((state) =>
    state?.exerciseRequestForm === undefined
      ? {}
      : { exerciseRequestForm: { ...state.exerciseRequestForm, ...f(state.exerciseRequestForm) } },
  );

export const setExerciseRequestTranche = (trancheId: string): DCEvent =>
  updateExerciseRequestForm(() => ({ trancheId, submitResult: undefined }));

export const prepExerciseRequestSummary = (
  form: ExerciseRequestFormState,
  candidates: ExerciseRequestCandidate[],
): ExerciseRequestSummary | undefined => {
  const candidate = candidates.find((c) => c.tranche.optionTrancheId === form.trancheId);
  if (candidate === undefined) {
    return undefined;
  }
  const requestDate = form.requestDate.validatedValue;
  const available =
    requestDate === undefined
      ? 0
      : requestableOptions(candidate.tranche, requestDate, form.requestId);
  const count = form.quantity.value === "ALL" ? available : (form.count.parseInputValue() ?? 0);
  return {
    candidate,
    requestDate,
    available,
    count,
    cost: count * candidate.tranche.strikePrice,
    window: requestDate === undefined ? undefined : exerciseWindowOn(candidate.plan, requestDate),
  };
};

export const exerciseRequestIssues = (
  summary: ExerciseRequestSummary | undefined,
  quantity: ExerciseRequestQuantity | undefined,
  today: string,
): TMessage[] => {
  const requestDate = summary?.requestDate;
  if (summary === undefined || requestDate === undefined) {
    return [];
  }
  const { tranche, plan } = summary.candidate;
  const windows = activeExerciseWindows(plan);
  return compact<TMessage>([
    requestDate > today && { key: "options.exercise-request-in-future" },
    requestDate < tranche.grantDate && { key: "options.exercise-request-before-grant" },
    requestDate > tranche.expirationDate && { key: "options.exercise-request-after-expiry" },
    windows.length > 0 &&
      summary.window === undefined && { key: "options.exercise-request-outside-window" },
    summary.available === 0 && { key: "options.exercise-request-nothing-available" },
    quantity === "SPECIFY" &&
      summary.count > summary.available &&
      summary.available > 0 && {
        key: "options.exercise-request-above-available",
        params: { available: { type: "number", value: summary.available } },
      },
  ]);
};

export const exerciseRequestFormComplete = (
  form: ExerciseRequestFormState,
  summary: ExerciseRequestSummary | undefined,
  today: string,
): boolean =>
  summary !== undefined &&
  summary.requestDate !== undefined &&
  summary.count > 0 &&
  Number.isInteger(summary.count) &&
  exerciseRequestIssues(summary, form.quantity.value, today).length === 0;

export const exerciseRequestFormCommand = (
  form: ExerciseRequestFormState,
  summary: ExerciseRequestSummary,
  requestDate: string,
): RequestExerciseCommand | EditExerciseRequestCommand => {
  const target = {
    grantId: summary.candidate.grantId,
    trancheId: summary.candidate.tranche.optionTrancheId,
    requestDate,
    count: summary.count,
  };
  return form.requestId === undefined
    ? { commandType: "REQUEST_EXERCISE", ...target }
    : { commandType: "EDIT_EXERCISE_REQUEST", requestId: form.requestId, ...target };
};

export const submitExerciseRequestForm = (
  orgNo: string,
  form: ExerciseRequestFormState,
  candidates: ExerciseRequestCandidate[],
  today: string,
): DCEvent[] => {
  const summary = prepExerciseRequestSummary(form, candidates);
  const requestDate = summary?.requestDate;
  if (
    summary === undefined ||
    requestDate === undefined ||
    !exerciseRequestFormComplete(form, summary, today)
  ) {
    return [];
  }
  return [
    updateExerciseRequestForm(() => ({ submitResult: pending() })),
    instrumentCommandApiClient.send(orgNo, exerciseRequestFormCommand(form, summary, requestDate), {
      onSuccess: () => [
        closeExerciseRequestForm(),
        showAlert(
          {
            key:
              form.requestId === undefined
                ? "options.exercise-request-registered"
                : "options.exercise-request-updated",
          },
          "success",
        ),
        loadGrants(orgNo),
      ],
      onFailure: (err) => [
        updateExerciseRequestForm(() => ({ submitResult: failure(commandFailureMessage(err)) })),
      ],
    }),
  ];
};

export type HolderExerciseRequestScope = {
  orgNo: string;
  shareholderId: number;
  onRequested: () => void;
};

export const exerciseRequestsOpenOn = (plan: OptionPlan | undefined, date: string): boolean =>
  plan?.exerciseRequestPolicy === "ANY_TIME" ||
  (plan?.exerciseRequestPolicy === "WINDOWS_ONLY" && exerciseWindowOn(plan, date) !== undefined);

export const nextScheduledVesting = (
  tranche: OptionTranche,
  today: string,
): VestingScheduleEntry | undefined =>
  trancheVestingEntries(tranche)
    .filter(
      (entry) => entry.date > today && entry.date <= tranche.expirationDate && entry.options > 0,
    )
    .sort((a, b) => a.date.localeCompare(b.date))[0];

export type HolderRequestButton = { isDisabled: boolean; tooltip: TMessage[]; notice?: TMessage };

export const holderRequestButton = (
  tranche: OptionTranche,
  grants: OptionGrantDetails[],
  plans: OptionPlan[],
  today: string,
): HolderRequestButton | undefined => {
  const planId = grants.find((details) => details.grant.optionGrantId === tranche.grantId)?.grant
    .planId;
  const plan = plans.find((p) => p.optionPlanId === planId);
  if (!exerciseRequestsOpenOn(plan, today)) {
    return undefined;
  }
  if (requestableOptions(tranche, today) > 0) {
    return {
      isDisabled: false,
      tooltip: [],
      notice: exerciseWindowNotice(activeExerciseWindows(plan), today),
    };
  }
  const next = nextScheduledVesting(tranche, today);
  const allExercised =
    exercisableOn(tranche, today) === 0 && trancheNumbers(tranche, today).exercised > 0;
  return {
    isDisabled: true,
    tooltip: compact<TMessage>([
      { key: allExercised ? "options.all-vested-options-exercised" : "options.no-vested-options" },
      next !== undefined && {
        key: "options.next-scheduled-vesting",
        params: {
          date: { type: "date", value: next.date },
          options: { type: "number", value: next.options },
        },
      },
    ]),
  };
};

export const submitHolderExerciseRequest = (
  scope: HolderExerciseRequestScope,
  form: ExerciseRequestFormState,
  candidates: ExerciseRequestCandidate[],
  today: string,
): DCEvent[] => {
  const summary = prepExerciseRequestSummary(form, candidates);
  if (summary === undefined || !exerciseRequestFormComplete(form, summary, today)) {
    return [];
  }
  const request = {
    grantId: summary.candidate.grantId,
    trancheId: summary.candidate.tranche.optionTrancheId,
    count: summary.count,
  };
  const callbacks = (message: TMessageKey) => ({
    onSuccess: () => [
      closeExerciseRequestForm(),
      showAlert({ key: message }, "success"),
      thunk(() => scope.onRequested()),
    ],
    onFailure: (err: unknown) => [
      updateExerciseRequestForm(() => ({ submitResult: failure(commandFailureMessage(err)) })),
    ],
  });
  return [
    updateExerciseRequestForm(() => ({ submitResult: pending() })),
    form.requestId === undefined
      ? shareholderViewApiClient.requestOptionExercise(
          scope.orgNo,
          scope.shareholderId,
          request,
          callbacks("options.exercise-request-sent"),
        )
      : shareholderViewApiClient.editOptionExerciseRequest(
          scope.orgNo,
          scope.shareholderId,
          form.requestId,
          request,
          callbacks("options.exercise-request-updated"),
        ),
  ];
};

const updateDeleteExerciseRequest = (
  f: (state: DeleteExerciseRequestState) => Partial<DeleteExerciseRequestState>,
): DCEvent =>
  updateOptionsState((prev) =>
    prev?.deleteExerciseRequest === undefined
      ? {}
      : {
          deleteExerciseRequest: {
            ...prev.deleteExerciseRequest,
            ...f(prev.deleteExerciseRequest),
          },
        },
  );

export const submitHolderWithdrawRequest = (
  scope: HolderExerciseRequestScope,
  state: DeleteExerciseRequestState,
): DCEvent[] => [
  updateDeleteExerciseRequest(() => ({ submitResult: pending() })),
  shareholderViewApiClient.withdrawOptionExerciseRequest(
    scope.orgNo,
    scope.shareholderId,
    state.view.request.requestId,
    { grantId: state.view.grantId, trancheId: state.view.trancheId },
    {
      onSuccess: () => [
        closeDeleteExerciseRequest(),
        showAlert({ key: "options.exercise-request-deleted" }, "success"),
        thunk(() => scope.onRequested()),
      ],
      onFailure: (err) => [
        updateDeleteExerciseRequest(() => ({ submitResult: failure(commandFailureMessage(err)) })),
      ],
    },
  ),
];

export type HolderRequestPage = {
  view: ExerciseRequestView;
  number: number;
  total: number;
  previous?: DCEvent;
  next?: DCEvent;
};

export const showHolderRequest = (holderId: string, index: number): DCEvent =>
  updateOptionsState((state) => ({
    holderRequestIndex: { ...state?.holderRequestIndex, [holderId]: index },
  }));

export const holderRequestsCollapsed = (
  state: OptionsPageState | undefined,
  isCollapsedByDefault: boolean,
): boolean => state?.holderRequestsCollapsed ?? isCollapsedByDefault;

export const setHolderRequestsCollapsed = (isCollapsed: boolean): DCEvent =>
  updateOptionsState(() => ({ holderRequestsCollapsed: isCollapsed }));

export const holderRequestPage = (
  views: ExerciseRequestView[],
  indexByHolder: Record<string, number> | undefined,
): HolderRequestPage | undefined => {
  const newestFirst = [...views].sort((a, b) =>
    b.request.requestDate.localeCompare(a.request.requestDate),
  );
  const first = newestFirst[0];
  if (first === undefined) {
    return undefined;
  }
  const { holderId } = first;
  const shown = Math.min(Math.max(indexByHolder?.[holderId] ?? 0, 0), newestFirst.length - 1);
  return {
    view: newestFirst[shown],
    number: shown + 1,
    total: newestFirst.length,
    previous: shown > 0 ? showHolderRequest(holderId, shown - 1) : undefined,
    next: shown < newestFirst.length - 1 ? showHolderRequest(holderId, shown + 1) : undefined,
  };
};

export const holderRequestMenuItems = (view: ExerciseRequestView): DropdownMenuItems =>
  view.request.status === "REQUESTED"
    ? [
        {
          type: "item",
          icon: "edit",
          label: { key: "options.edit-exercise-request" },
          onSelect: [startEditExerciseRequest(view)],
        },
        {
          type: "item",
          icon: "trash",
          color: "error",
          label: { key: "options.delete-exercise-request" },
          onSelect: [startDeleteExerciseRequest(view)],
        },
      ]
    : [];

export type ExerciseRequestDecision = "ACKNOWLEDGED" | "REJECTED";

export type ExerciseRequestReviewState = {
  view: ExerciseRequestView;
  decision: RadioFieldModel<ExerciseRequestDecision>;
  message: StringFieldModel;
  sendCopy: BooleanFieldModel;
  copyTo: StringFieldModel;
  copyToAdministrators: BooleanFieldModel;
  submitResult?: Result<TMessage, TMessage>;
};

export const startExerciseRequestReview = (view: ExerciseRequestView): DCEvent =>
  updateOptionsState(() => ({
    exerciseRequestReview: {
      view,
      decision: new RadioFieldModel<ExerciseRequestDecision>({
        required: true,
        value: "ACKNOWLEDGED",
        options: ["ACKNOWLEDGED", "REJECTED"],
        idFn: (decision) => decision,
        labelFn: (decision) => ({
          key:
            decision === "ACKNOWLEDGED" ? "options.acknowledge-request" : "options.decline-request",
        }),
      }),
      message: new StringFieldModel(),
      sendCopy: new BooleanFieldModel({ value: false }),
      copyTo: createEmailField(),
      copyToAdministrators: new BooleanFieldModel({ value: false }),
    },
  }));

export const closeExerciseRequestReview = (): DCEvent =>
  updateOptionsState(() => ({ exerciseRequestReview: undefined }));

const updateExerciseRequestReview = (
  f: (review: ExerciseRequestReviewState) => Partial<ExerciseRequestReviewState>,
): DCEvent =>
  updateOptionsState((state) =>
    state?.exerciseRequestReview === undefined
      ? {}
      : {
          exerciseRequestReview: {
            ...state.exerciseRequestReview,
            ...f(state.exerciseRequestReview),
          },
        },
  );

export type ExerciseRequestNoticePreview = {
  greeting: TMessage;
  decision: TMessage;
  note: TMessage;
};

export const exerciseRequestNoticePreview = (
  view: ExerciseRequestView,
  decision: ExerciseRequestDecision,
  companyName: string,
): ExerciseRequestNoticePreview => {
  const isDeclined = decision === "REJECTED";
  return {
    greeting: { key: "options.exercise-request-email-greeting", params: { name: view.holderName } },
    decision: {
      key: isDeclined
        ? "options.exercise-request-email-declined"
        : "options.exercise-request-email-acknowledged",
      params: {
        options: { type: "number", value: view.request.count },
        program: view.planName,
        company: companyName,
      },
    },
    note: {
      key: isDeclined
        ? "options.exercise-request-email-declined-note"
        : "options.exercise-request-email-acknowledged-note",
    },
  };
};

export const reviewNotifiesSomeone = (review: ExerciseRequestReviewState): boolean =>
  review.view.holderEmail !== undefined ||
  Boolean(review.sendCopy.value) ||
  Boolean(review.copyToAdministrators.value);

export const exerciseRequestReviewComplete = (review: ExerciseRequestReviewState): boolean =>
  (review.decision.value !== "REJECTED" || review.message.inputValue.trim() !== "") &&
  (!review.sendCopy.value || (review.copyTo.inputValue.trim() !== "" && review.copyTo.isValid));

export const exerciseRequestReviewCommand = (
  review: ExerciseRequestReviewState,
  language: Language,
): UpdateExerciseRequestCommand => ({
  commandType: "UPDATE_EXERCISE_REQUEST",
  grantId: review.view.grantId,
  trancheId: review.view.trancheId,
  requestId: review.view.request.requestId,
  status: review.decision.value === "REJECTED" ? "REJECTED" : "ACKNOWLEDGED",
  remarks: review.message.inputValue.trim() || undefined,
  notify: {
    language,
    copyTo: review.sendCopy.value ? review.copyTo.inputValue.trim() : undefined,
    copyToAdministrators: Boolean(review.copyToAdministrators.value),
  },
});

const reviewSuccessMessage = (review: ExerciseRequestReviewState): TMessage => {
  if (review.view.holderEmail !== undefined) {
    return { key: "options.notification-sent-to", params: { name: review.view.holderName } };
  }
  return {
    key:
      review.decision.value === "REJECTED"
        ? "options.exercise-request-declined"
        : "options.exercise-request-acknowledged",
  };
};

export const submitExerciseRequestReview = (
  orgNo: string,
  review: ExerciseRequestReviewState,
  language: Language,
): DCEvent[] =>
  exerciseRequestReviewComplete(review)
    ? [
        updateExerciseRequestReview(() => ({ submitResult: pending() })),
        instrumentCommandApiClient.send(orgNo, exerciseRequestReviewCommand(review, language), {
          onSuccess: () => [
            closeExerciseRequestReview(),
            showAlert(reviewSuccessMessage(review), "success"),
            loadGrants(orgNo),
          ],
          onFailure: (err) => [
            updateExerciseRequestReview(() => ({
              submitResult: failure(commandFailureMessage(err)),
            })),
          ],
        }),
      ]
    : [];

export type DeleteExerciseRequestState = {
  view: ExerciseRequestView;
  submitResult?: Result<TMessage, TMessage>;
};

export const startDeleteExerciseRequest = (view: ExerciseRequestView): DCEvent =>
  updateOptionsState(() => ({ deleteExerciseRequest: { view } }));

export const closeDeleteExerciseRequest = (): DCEvent =>
  updateOptionsState(() => ({ deleteExerciseRequest: undefined }));

export const submitDeleteExerciseRequest = (
  orgNo: string,
  state: DeleteExerciseRequestState,
): DCEvent[] => [
  updateOptionsState((prev) =>
    prev?.deleteExerciseRequest === undefined
      ? {}
      : { deleteExerciseRequest: { ...prev.deleteExerciseRequest, submitResult: pending() } },
  ),
  instrumentCommandApiClient.send(
    orgNo,
    {
      commandType: "UPDATE_EXERCISE_REQUEST",
      grantId: state.view.grantId,
      trancheId: state.view.trancheId,
      requestId: state.view.request.requestId,
      status: "DELETED",
    } satisfies UpdateExerciseRequestCommand,
    {
      onSuccess: () => [
        closeDeleteExerciseRequest(),
        showAlert({ key: "options.exercise-request-deleted" }, "success"),
        loadGrants(orgNo),
      ],
      onFailure: (err) => [
        updateOptionsState((prev) =>
          prev?.deleteExerciseRequest === undefined
            ? {}
            : {
                deleteExerciseRequest: {
                  ...prev.deleteExerciseRequest,
                  submitResult: failure(commandFailureMessage(err)),
                },
              },
        ),
      ],
    },
  ),
];

export type SentNotificationState = {
  notification: ExerciseRequestNotification;
  sentAt: string;
};

export const openSentNotification = (
  notification: ExerciseRequestNotification,
  sentAt: string,
): DCEvent => updateOptionsState(() => ({ sentNotification: { notification, sentAt } }));

export const closeSentNotification = (): DCEvent =>
  updateOptionsState(() => ({ sentNotification: undefined }));

export const startExerciseForRequest = (
  view: ExerciseRequestView,
  scope: ExerciseActionScope,
): DCEvent[] => {
  const rows = exerciseRows(
    scope.grants,
    scope.plans,
    scope.holders,
    scope.today,
    view.trancheId,
  ).map((row) => ({
    ...withExerciseCount(row, view.request.count),
    isSelected: true,
    requestId: view.request.requestId,
    requestedCount: view.request.count,
  }));
  if (rows.length === 0) {
    return [];
  }
  return [
    updateOptionsState(() => ({ exercise: newExerciseState(rows, true, scope.today) })),
    loadExerciseHoldingCompanies(scope.orgNo, [rows[0].holderId]),
  ];
};

const declinedRequestShownDays = 14;

export const declinedRequestDaysLeft = (
  view: ExerciseRequestView,
  today: string,
): number | undefined => {
  const decision = view.decision;
  if (view.request.status !== "REJECTED" || decision === undefined) {
    return undefined;
  }
  const declinedOn = toLocalDateISOString(new Date(decision.changedAt));
  return declinedRequestShownDays - differenceInCalendarDays(toDate(today), toDate(declinedOn));
};

export const requestDisplayStatus = (status: ExerciseRequestStatus): ExerciseRequestStatus =>
  status === "EXERCISED" ? "ACKNOWLEDGED" : status;

const isShownOnHolderPage = (view: ExerciseRequestView, today: string): boolean => {
  switch (view.request.status) {
    case "REQUESTED":
    case "ACKNOWLEDGED":
      return true;
    case "EXERCISED":
      return view.exercise === undefined || exerciseStatus(view.exercise) !== "SHARES_ISSUED";
    case "REJECTED":
      return (declinedRequestDaysLeft(view, today) ?? 0) > 0;
    case "WITHDRAWN":
    case "DELETED":
      return false;
  }
};

export const holderExerciseRequests = (
  holderId: string,
  grants: OptionGrantDetails[],
  plans: OptionPlan[],
  holders: InstrumentHolder[],
  today: string,
): ExerciseRequestView[] =>
  exerciseRequestViews(grants, plans, holders).filter(
    (view) => view.holderId === holderId && isShownOnHolderPage(view, today),
  );

export const sumNumbers = (rows: OptionsRowNumbers[]): OptionsRowNumbers => ({
  granted: rows.reduce((acc, r) => acc + r.granted, 0),
  exercised: rows.reduce((acc, r) => acc + r.exercised, 0),
  cancelled: rows.reduce((acc, r) => acc + r.cancelled, 0),
  expired: rows.reduce((acc, r) => acc + r.expired, 0),
  outstanding: rows.reduce((acc, r) => acc + r.outstanding, 0),
  vested: rows.reduce((acc, r) => acc + r.vested, 0),
});

const poolChangeOn = (plan: OptionPlan, date: string): OptionPoolChange | undefined =>
  (plan.poolChanges ?? [])
    .filter((change) => change.effectiveDate <= date)
    .reduce<OptionPoolChange | undefined>(
      (latest, change) =>
        latest === undefined || change.effectiveDate >= latest.effectiveDate ? change : latest,
      undefined,
    );

export const poolSizeOn = (plan: OptionPlan, date: string): number | undefined =>
  poolChangeOn(plan, date)?.poolSize;

const optionsTakenFromPool = (
  planId: string,
  grants: OptionGrantDetails[],
  today: string,
  excludedTrancheId?: string,
): number =>
  grants
    .filter((details) => details.grant.planId === planId)
    .flatMap((details) => details.tranches)
    .filter((tranche) => tranche.optionTrancheId !== excludedTrancheId)
    .map((tranche) => trancheNumbers(tranche, today))
    .reduce((acc, numbers) => acc + numbers.outstanding + numbers.exercised, 0);

export type ProgramPoolRow = {
  plan: OptionPlan;
  poolSize?: number;
  taken: number;
  unallocated?: number;
};

export const prepProgramPoolRows = (
  plans: OptionPlan[],
  grants: OptionGrantDetails[],
  today: string,
): ProgramPoolRow[] =>
  plans.map((plan) => {
    const poolSize = poolSizeOn(plan, today);
    const taken = optionsTakenFromPool(plan.optionPlanId, grants, today);
    return {
      plan,
      poolSize,
      taken,
      unallocated: poolSize === undefined ? undefined : poolSize - taken,
    };
  });

export const programsAbovePool = (rows: ProgramPoolRow[]): number =>
  rows.filter((row) => row.unallocated !== undefined && row.unallocated < 0).length;

const countValue = (value: number | undefined): TMessageParam | TMessage =>
  value === undefined ? { translated: "–" } : { type: "number", value };

export const prepProgramCards = (
  plan: OptionPlan,
  grants: OptionGrantDetails[],
  today: string,
): DataCardProps[] => {
  const { poolSize, taken, unallocated } = prepProgramPoolRows([plan], grants, today)[0];
  const totals = sumNumbers(
    grants
      .filter((details) => details.grant.planId === plan.optionPlanId)
      .flatMap((details) => details.tranches)
      .map((tranche) => trancheNumbers(tranche, today)),
  );
  const countSubtitle: TMessage = { key: "options.card-count-subtitle" };
  return [
    {
      title: { key: "options.pool-size" },
      subtitle: countSubtitle,
      infoPopover: { content: { key: "options.pool-size-info" } },
      value: countValue(poolSize),
    },
    {
      title: { key: "options.pool-used" },
      subtitle: countSubtitle,
      infoPopover: { content: { key: "options.pool-used-help" } },
      value: countValue(taken),
      extraInfo:
        poolSize !== undefined && poolSize > 0
          ? {
              key: "options.of-pool",
              params: { percent: { type: "percent", value: taken / poolSize } },
            }
          : undefined,
    },
    {
      title: { key: "options.pool-unallocated" },
      subtitle: countSubtitle,
      value: countValue(unallocated === undefined ? undefined : Math.max(0, unallocated)),
      extraInfo:
        unallocated !== undefined && unallocated < 0
          ? {
              key: "options.pool-above",
              params: { count: { type: "number", value: -unallocated } },
            }
          : undefined,
    },
    {
      title: { key: "options.vested" },
      subtitle: countSubtitle,
      infoPopover: { content: { key: "options.vested-help" } },
      value: countValue(totals.vested),
      extraInfo:
        totals.granted > 0
          ? {
              key: "options.vested-of-total",
              params: { percent: { type: "percent", value: totals.vested / totals.granted } },
            }
          : undefined,
    },
    {
      title: { key: "instrument.expiration-date" },
      infoPopover: { content: { key: "options.program-expiration-date-info" } },
      value:
        plan.expirationDate === undefined
          ? { translated: "–" }
          : { type: "date", value: plan.expirationDate },
    },
  ];
};

export type ExerciseWindowStatus = "closed" | "open" | "upcoming";

export const exerciseWindowStatus = (
  exerciseWindow: ExerciseWindow,
  today: string,
): ExerciseWindowStatus =>
  exerciseWindow.toDate < today ? "closed" : exerciseWindow.fromDate > today ? "upcoming" : "open";

export type ExerciseWindowBar = ExerciseWindow & { status: ExerciseWindowStatus };

export const chartExerciseWindows = (
  exerciseWindows: ExerciseWindow[],
  chartStart: string,
  today: string,
): ExerciseWindowBar[] =>
  exerciseWindows
    .filter((exerciseWindow) => exerciseWindow.toDate >= chartStart)
    .map((exerciseWindow) => ({
      ...exerciseWindow,
      status: exerciseWindowStatus(exerciseWindow, today),
    }));

export const exerciseWindowNotice = (
  exerciseWindows: ExerciseWindow[],
  today: string,
): TMessage | undefined => {
  const open = exerciseWindows.find((w) => exerciseWindowStatus(w, today) === "open");
  if (open !== undefined) {
    return {
      key: "options.exercise-window-open-until",
      params: { date: { type: "date", value: open.toDate } },
    };
  }
  const nextFromDate = exerciseWindows
    .filter((w) => exerciseWindowStatus(w, today) === "upcoming")
    .map((w) => w.fromDate)
    .sort()[0];
  return nextFromDate === undefined
    ? undefined
    : {
        key: "options.exercise-window-opens-in",
        params: { count: differenceInCalendarDays(toDate(nextFromDate), toDate(today)) },
      };
};

export const exerciseWindowsTooltip = (exerciseWindows: ExerciseWindow[]): TMessageParam => ({
  type: "list",
  value: exerciseWindows.map(
    (exerciseWindow): TMessage => ({
      key: "options.exercise-window-period",
      params: {
        from: { type: "date", value: exerciseWindow.fromDate },
        to: { type: "date", value: exerciseWindow.toDate },
      },
    }),
  ),
  separator: "\n",
});

export const prepProgramGrantRows = ({
  plan,
  holders,
  grants,
  today,
}: {
  plan: OptionPlan;
  holders: InstrumentHolder[];
  grants: OptionGrantDetails[];
  today: string;
}): OptionsTableRow[] => {
  const holderById = new Map(holders.map((h) => [h.instrumentHolderId, h]));
  return grants
    .filter((details) => details.grant.planId === plan.optionPlanId)
    .flatMap((details) => {
      const holder = holderById.get(details.grant.holderId);
      return holder === undefined
        ? []
        : details.tranches.map((tranche) => ({
            kind: "tranche" as const,
            holderName: holder.name,
            holderIcon: holderIcon(holder.name),
            expandable: false,
            ...trancheRow(holder, { tranche, planName: plan.name }, today),
          }));
    })
    .sort(
      (a, b) => a.holderName.localeCompare(b.holderName) || a.grantDate.localeCompare(b.grantDate),
    );
};

export const optionsAbovePoolAfterGrant = ({
  plan,
  numberOfOptions,
  grants,
  today,
  editedTrancheId,
}: {
  plan?: OptionPlan;
  numberOfOptions?: number;
  grants: OptionGrantDetails[];
  today: string;
  editedTrancheId?: string;
}): number => {
  const poolSize = plan === undefined ? undefined : poolSizeOn(plan, today);
  if (plan === undefined || poolSize === undefined || numberOfOptions === undefined) {
    return 0;
  }
  const taken = optionsTakenFromPool(plan.optionPlanId, grants, today, editedTrancheId);
  return Math.max(0, taken + numberOfOptions - poolSize);
};

export const programLabel = (planName: string, trancheName?: string): TMessage => ({
  translated: trancheName ? `${planName} · ${trancheName}` : planName,
});

export const vestingTypeLabel = (vesting: VestingConfig): TMessage => {
  switch (vesting.vestingType) {
    case "PERIODIC":
      return { key: "options.time-vested" };
    case "SPECIFIC_DATES":
      return { key: "options.frequency-specific-dates" };
    case "PERFORMANCE":
      return { key: "options.performance-vested" };
  }
};

export type TrancheWithPlan = {
  tranche: OptionTranche;
  planName: string;
  exerciseWindows?: ExerciseWindow[];
};

export const holderTranches = (
  holderId: string,
  grants: OptionGrantDetails[],
  plans: OptionPlan[],
): TrancheWithPlan[] => {
  const planById = new Map(plans.map((p) => [p.optionPlanId, p]));
  return grants
    .filter((details) => details.grant.holderId === holderId)
    .flatMap((details) =>
      details.tranches.map((tranche) => ({
        tranche,
        planName: planById.get(details.grant.planId)?.name ?? "–",
        exerciseWindows: activeExerciseWindows(planById.get(details.grant.planId)),
      })),
    );
};

// Every tranche in the company paired with its plan name — used where the view
// spans holders (e.g. the cancellation dialog's grant picker and schedule).
export const allTranchesWithPlan = (
  grants: OptionGrantDetails[],
  plans: OptionPlan[],
): TrancheWithPlan[] => {
  const planById = new Map(plans.map((p) => [p.optionPlanId, p]));
  return grants.flatMap((details) =>
    details.tranches.map((tranche) => ({
      tranche,
      planName: planById.get(details.grant.planId)?.name ?? "–",
      exerciseWindows: activeExerciseWindows(planById.get(details.grant.planId)),
    })),
  );
};

export const findTranche = (
  grants: OptionGrantDetails[],
  trancheId: string,
): OptionTranche | undefined =>
  grants.flatMap((details) => details.tranches).find((t) => t.optionTrancheId === trancheId);

export type ProgramGrantGroup = {
  planName: string;
  tranches: TrancheWithPlan[];
};

export const groupTranchesByProgram = (tranches: TrancheWithPlan[]): ProgramGrantGroup[] => {
  const byProgram = new Map<string, TrancheWithPlan[]>();
  tranches.forEach((twp) => {
    const list = byProgram.get(twp.planName) ?? [];
    list.push(twp);
    byProgram.set(twp.planName, list);
  });
  return [...byProgram].map(([planName, list]) => ({ planName, tranches: list }));
};

const matchesQuery = (holder: InstrumentHolder, tranches: TrancheWithPlan[], query: string) => {
  const q = query.trim().toLowerCase();
  if (q === "") return true;
  return (
    holder.name.toLowerCase().includes(q) ||
    tranches.some((t) => t.planName.toLowerCase().includes(q))
  );
};

const trancheRow = (
  holder: InstrumentHolder,
  { tranche, planName }: TrancheWithPlan,
  today: string,
) => ({
  id: tranche.optionTrancheId,
  trancheId: tranche.optionTrancheId,
  holderId: holder.instrumentHolderId,
  shareholderId: holder.shareholderId,
  expanded: false,
  program: programLabel(planName, tranche.name),
  isMultipleProgram: false,
  shareTypeId: tranche.shareTypeId,
  strike: tranche.strikePrice,
  grantDate: tranche.grantDate,
  expiryDate: tranche.expirationDate,
  ...trancheNumbers(tranche, today),
});

const holderRows = (
  holder: InstrumentHolder,
  tranches: TrancheWithPlan[],
  expandedHolderIds: string[],
  today: string,
): OptionsTableRow[] => {
  if (tranches.length <= 1) {
    const single = tranches[0];
    return [
      {
        kind: "holder",
        holderName: holder.name,
        holderIcon: holderIcon(holder.name),
        shareholderId: holder.shareholderId,
        expandable: false,
        ...(single
          ? trancheRow(holder, single, today)
          : {
              id: holder.instrumentHolderId,
              holderId: holder.instrumentHolderId,
              expanded: false,
              program: { translated: "–" },
              isMultipleProgram: false,
              ...sumNumbers([]),
            }),
      },
    ];
  }

  const expanded = expandedHolderIds.includes(holder.instrumentHolderId);
  const planNames = [...new Set(tranches.map((t) => t.planName))];
  const shareTypeIds = [...new Set(tranches.map((t) => t.tranche.shareTypeId))];
  const parent: OptionsTableRow = {
    id: holder.instrumentHolderId,
    kind: "holder",
    holderId: holder.instrumentHolderId,
    holderName: holder.name,
    shareholderId: holder.shareholderId,
    expandable: true,
    expanded,
    program:
      planNames.length === 1
        ? { translated: planNames[0] }
        : { key: "options.multiple-programs", params: { count: tranches.length } },
    isMultipleProgram: planNames.length > 1,
    shareTypeId: shareTypeIds.length === 1 ? shareTypeIds[0] : undefined,
    ...sumNumbers(tranches.map((t) => trancheNumbers(t.tranche, today))),
  };
  if (!expanded) return [parent];
  return [
    parent,
    ...tranches.map((t) => ({
      kind: "tranche" as const,
      expandable: false,
      ...trancheRow(holder, t, today),
    })),
  ];
};

export const prepOptionsRows = ({
  holders,
  grants,
  plans,
  expandedHolderIds,
  query,
  today,
}: {
  holders: InstrumentHolder[];
  grants: OptionGrantDetails[];
  plans: OptionPlan[];
  expandedHolderIds: string[];
  query: string;
  today: string;
}): OptionsTableRow[] =>
  [...holders]
    .sort((a, b) => a.name.localeCompare(b.name))
    .map((holder) => ({
      holder,
      tranches: holderTranches(holder.instrumentHolderId, grants, plans),
    }))
    .filter(({ holder, tranches }) => matchesQuery(holder, tranches, query))
    .flatMap(({ holder, tranches }) => holderRows(holder, tranches, expandedHolderIds, today));

export type GrantActionScope = {
  orgNo: string;
  tranche: OptionTranche;
  holderName: string;
  program: TMessage;
  grants: OptionGrantDetails[];
  plans: OptionPlan[];
  holders: InstrumentHolder[];
  shareTypes: ShareType[];
  lang: Language;
  today: string;
  t: TFunction;
};

const createOptionsProject = (
  orgNo: string,
  projectName: string,
  decisionData: OptionGrantLetter | OptionExercise,
  {
    isGeneratedName = true,
    onSuccess = [],
    onFailure = [],
  }: { isGeneratedName?: boolean; onSuccess?: DCEvent[]; onFailure?: DCEvent[] } = {},
): DCEvent =>
  projectApi.createProject(
    {
      orgNo,
      projectName,
      isGeneratedName,
      decisions: [{ decisionType: decisionData.type, decisionData }],
    },
    {
      onSuccess: (res) => [
        ...onSuccess,
        navigate(adminProject({ orgNo, projectId: res.data.projectId }), {
          state: { type: "project-navigation-state", from: "create-new" },
        }),
      ],
      onFailure: () => [showGeneralError(), ...onFailure],
    },
  );

const setCreatingGrantLetter = (isCreatingGrantLetter: boolean): DCEvent =>
  updateOptionsState(() => ({ isCreatingGrantLetter }));

const createGrantLetterProject = ({
  orgNo,
  planId,
  holderId,
  projectName,
}: {
  orgNo: string;
  planId: string;
  holderId: string;
  projectName: string;
}): DCEvent[] => [
  setCreatingGrantLetter(true),
  createOptionsProject(
    orgNo,
    projectName,
    {
      type: DecisionType.optionGrantLetter,
      programs: [{ planId, holderIds: [holderId] }],
    },
    {
      onSuccess: [setCreatingGrantLetter(false)],
      onFailure: [setCreatingGrantLetter(false)],
    },
  ),
];

const sendGrantLetterItem = ({
  orgNo,
  tranche,
  holderName,
  grants,
  holders,
  today,
  t,
}: GrantActionScope): DropdownMenuItems[number] => {
  const grant = grants.find((details) => details.grant.optionGrantId === tranche.grantId)?.grant;
  const holder = holders.find((h) => h.instrumentHolderId === grant?.holderId);
  const canSend = grant !== undefined && holder?.shareholderId !== undefined;
  return {
    type: "item",
    icon: "envelope",
    label: { key: "options.send-grant-letter" },
    isDisabled: !canSend,
    tooltip: canSend ? undefined : { key: "decisions.optionGrantLetter.no-shareholder" },
    onSelect:
      grant === undefined
        ? []
        : createGrantLetterProject({
            orgNo,
            planId: grant.planId,
            holderId: grant.holderId,
            projectName: `${today} ${t("decisionType.optionGrantLetter.action")} – ${holderName}`,
          }),
  };
};

// Shared by the register's row menu and the grant view's Edit menu, so the two
// never drift apart.
export const grantActionItems = (scope: GrantActionScope): DropdownMenuItems => {
  const { orgNo, tranche, holderName, program, grants, plans, holders, shareTypes, lang, today } =
    scope;
  const isExercisable = exercisableOn(tranche, today) > 0;
  const isRequestable = requestableOptions(tranche, today) > 0;
  return [
    {
      type: "item",
      icon: "seal-check",
      label: { key: "options.edit-grant" },
      onSelect: [startEditGrant(tranche, holderName, grants, plans, shareTypes, lang)],
    },
    {
      type: "item",
      icon: "steps",
      label: { key: "options.edit-vesting-schedule" },
      onSelect: [startEditVesting(tranche, holderName, grants, plans, shareTypes, lang)],
    },
    {
      type: "item",
      icon: "fast-forward",
      label: { key: "options.acceleration" },
      onSelect: [startAcceleration(tranche, grants, plans, holders, today)],
    },
    {
      type: "item",
      icon: "file-minus",
      label: { key: "options.cancellation" },
      onSelect: [startCancellation(tranche, grants, plans, holders, today)],
    },
    {
      type: "item",
      icon: "check-circle",
      label: { key: "options.exercise" },
      isDisabled: !isExercisable,
      tooltip: isExercisable ? undefined : { key: "options.nothing-exercisable" },
      onSelect: startExercise(orgNo, tranche, grants, plans, holders, today),
    },
    {
      type: "item",
      icon: "paper-plane",
      label: { key: "options.register-exercise-request" },
      isDisabled: !isRequestable,
      tooltip: isRequestable ? undefined : { key: "options.nothing-exercisable" },
      onSelect: [startExerciseRequest(today, tranche.optionTrancheId)],
    },
    sendGrantLetterItem(scope),
    { type: "separator" },
    {
      type: "item",
      icon: "trash",
      color: "error",
      label: { key: "options.delete-grant" },
      onSelect: [startDeleteGrant(tranche, holderName, program)],
    },
  ];
};

export const optionsRowActions = ({
  orgNo,
  row,
  grants,
  plans,
  holders,
  shareTypes,
  lang,
  today,
  t,
}: {
  orgNo: string;
  row: OptionsTableRow;
  grants: OptionGrantDetails[];
  plans: OptionPlan[];
  holders: InstrumentHolder[];
  shareTypes: ShareType[];
  lang: Language;
  today: string;
  t: TFunction;
}): RowAction[] => {
  const tranche = row.trancheId === undefined ? undefined : findTranche(grants, row.trancheId);
  const holderName = holders.find((h) => h.instrumentHolderId === row.holderId)?.name;
  if (tranche === undefined || holderName === undefined) {
    return [];
  }
  return [
    [
      "context-menu",
      grantActionItems({
        orgNo,
        tranche,
        holderName,
        program: row.program,
        grants,
        plans,
        holders,
        shareTypes,
        lang,
        today,
        t,
      }),
    ],
  ];
};

export type OptionsEmptyState = "programs" | "exercises" | "options";

export const optionsEmptyState = (
  tab: OptionsTab,
  plans: OptionPlan[],
  grants: OptionGrantDetails[],
): OptionsEmptyState | undefined => {
  switch (tab) {
    case "programs":
      return plans.length === 0 ? "programs" : undefined;
    case "exercises":
      return grants.length === 0 ? "exercises" : undefined;
    case "holders":
      return grants.length === 0 ? "options" : undefined;
  }
};

export const registerMenuItems = ({
  tab,
  orgNo,
  grants,
  plans,
  holders,
  today,
}: {
  tab: OptionsTab;
  orgNo: string;
  grants: OptionGrantDetails[];
  plans: OptionPlan[];
  holders: InstrumentHolder[];
  today: string;
}): DropdownMenuItems => {
  if (tab === "programs") {
    return [
      {
        type: "item",
        icon: "folder-plus",
        label: { key: "options.new-program" },
        onSelect: [openCreatePlanDialog()],
      },
    ];
  }
  const canExercise = hasExercisableOptions(grants, today);
  return [
    {
      type: "item",
      icon: "file-plus",
      label: { key: "options.new-grant" },
      onSelect: openRegisterGrantDialog(orgNo),
    },
    {
      type: "item",
      icon: "fast-forward",
      label: { key: "options.acceleration" },
      onSelect: [startAccelerationForCompany(grants, plans, holders, today)],
    },
    {
      type: "item",
      icon: "file-minus",
      label: { key: "options.cancellation" },
      onSelect: [startCancellationForCompany(grants, plans, holders, today)],
    },
    {
      type: "item",
      icon: "check-circle",
      label: { key: "options.exercise" },
      isDisabled: !canExercise,
      tooltip: canExercise ? undefined : { key: "options.nothing-exercisable" },
      onSelect: startExerciseForCompany(orgNo, grants, plans, holders, today),
    },
  ];
};

export const isLoadingOptions = (state?: OptionsPageState): boolean =>
  state?.orgNo === undefined ||
  isPending(state.holdersResult) ||
  isPending(state.plansResult) ||
  isPending(state.grantsResult);

export const optionsPageData = (state?: OptionsPageState) => {
  const holders = isOk(state?.holdersResult) ? state.holdersResult.data : undefined;
  const plans = isOk(state?.plansResult) ? state.plansResult.data : undefined;
  const grants = isOk(state?.grantsResult) ? state.grantsResult.data : undefined;
  if (holders === undefined || plans === undefined || grants === undefined) return undefined;
  return { holders, plans, grants };
};

export const optionHolderSince = (
  holder: InstrumentHolder,
  grants: OptionGrantDetails[],
): string | undefined =>
  holder.joinedDate ??
  grants
    .filter((details) => details.grant.holderId === holder.instrumentHolderId)
    .map((details) => details.grant.registeredDate)
    .sort()[0];

// Holder scope sums every tranche and labels the cards as totals ("Total
// options", "# Shares"); tranche scope describes a single tranche ("Options",
// "# Count"). The computations are identical, except that the holder's ownership
// also counts the shares the holder owns.
export const prepHolderOptionCards = ({
  tranches,
  shareTypes,
  fullyDiluted,
  lang,
  today,
  scope,
}: {
  tranches: TrancheWithPlan[];
  shareTypes: ShareType[];
  fullyDiluted?: FullyDilutedBasis;
  lang: Language;
  today: string;
  scope: "holder" | "tranche";
}): DataCardProps[] => {
  const isHolder = scope === "holder";
  const countSubtitle: TMessage = {
    key: isHolder ? "options.card-shares-subtitle" : "options.card-count-subtitle",
  };
  const numbers = tranches.map((t) => trancheNumbers(t.tranche, today));
  const totals = sumNumbers(numbers);
  const exerciseCost = tranches.reduce(
    (acc, t) => acc + trancheNumbers(t.tranche, today).vested * t.tranche.strikePrice,
    0,
  );
  const shareClassNames = [
    ...new Set(
      tranches
        .map((t) => t.tranche.shareTypeId)
        .filter((id) => id !== undefined)
        .map((id) => lookupShareTypeName(id, shareTypes, lang)),
    ),
  ];
  const cards: DataCardProps[] = [
    {
      title: { key: isHolder ? "options.total-options" : "options.options-header" },
      subtitle: countSubtitle,
      infoPopover: { content: { key: "options.total-options-help" } },
      value: { type: "number", value: totals.granted },
      extraInfo:
        shareClassNames.length > 0 ? { translated: shareClassNames.join(", ") } : undefined,
    },
    {
      title: { key: isHolder ? "options.total-vested" : "options.vested" },
      subtitle: countSubtitle,
      infoPopover: { content: { key: "options.vested-help" } },
      value: { type: "number", value: totals.vested },
      extraInfo:
        totals.granted > 0
          ? {
              key: "options.vested-of-total",
              params: { percent: { type: "percent", value: totals.vested / totals.granted } },
            }
          : undefined,
    },
    {
      title: { key: isHolder ? "options.total-exercise-cost" : "options.exercise-cost" },
      subtitle: { translated: "NOK" },
      infoPopover: { content: { key: "options.exercise-cost-help" } },
      value: { type: "number", value: exerciseCost },
      extraInfo: { key: "options.exercise-cost-footer" },
    },
  ];
  if (fullyDiluted === undefined) {
    return cards;
  }
  const held = (isHolder ? fullyDiluted.sharesHeld : 0) + totals.outstanding;
  return [
    ...cards,
    {
      title: {
        key: isHolder ? "options.total-ownership-if-exercised" : "options.ownership-if-exercised",
      },
      subtitle: { key: isHolder ? "options.fully-diluted-total" : "options.fully-diluted" },
      infoPopover: { content: { key: "options.ownership-if-exercised-help" } },
      value: { type: "percent", value: held / fullyDiluted.total },
      extraInfo: {
        key: "options.if-outstanding-exercised",
        params: { count: { type: "number", value: totals.outstanding } },
      },
    },
  ];
};

export type HolderGrantRow = OptionsRowNumbers & {
  id: string;
  program: TMessage;
  shareTypeId?: number;
  strike: number;
  grantDate: string;
  expirationDate: string;
};

export const prepHolderGrantRows = (tranches: TrancheWithPlan[], today: string): HolderGrantRow[] =>
  tranches.map(({ tranche, planName }) => ({
    id: tranche.optionTrancheId,
    program: programLabel(planName, tranche.name),
    shareTypeId: tranche.shareTypeId,
    strike: tranche.strikePrice,
    grantDate: tranche.grantDate,
    expirationDate: tranche.expirationDate,
    ...trancheNumbers(tranche, today),
  }));

export type VestingChartExercise = { date: string; count: number };

export type VestingChartSeries = {
  label: string;
  startDate: string;
  entries: VestingScheduleEntry[];
  cliffDate?: string;
  accelerationDates?: string[];
  cancellationDates?: string[];
  exercises?: VestingChartExercise[];
};

export const prepHolderVestingSeries = (tranches: TrancheWithPlan[]): VestingChartSeries[] =>
  tranches
    .map(({ tranche, planName }) => ({
      label: tranche.name ? `${planName} · ${tranche.name}` : planName,
      startDate: tranche.grantDate,
      entries: cancelVestingEntries(
        accelerateVestingEntries(trancheVestingEntries(tranche), tranche.accelerations ?? []),
        tranche.cancellations ?? [],
      ),
      cliffDate:
        tranche.vesting.vestingType === "PERIODIC" && tranche.vesting.cliffMonths > 0
          ? plusVestingMonths(tranche.grantDate, tranche.vesting.cliffMonths)
          : undefined,
      accelerationDates: (tranche.accelerations ?? []).map((accel) => accel.effectiveDate),
      cancellationDates: (tranche.cancellations ?? []).map((cancel) => cancel.effectiveDate),
      exercises: activeExercises(tranche)
        .map((exercise) => ({ date: exercise.exerciseDate, count: exercise.count }))
        .sort((a, b) => a.date.localeCompare(b.date)),
    }))
    .filter((series) => series.entries.length > 0);

const vestedOn = (series: VestingChartSeries, date: string): number =>
  series.entries
    .filter((entry) => entry.date <= date)
    .reduce((acc, entry) => acc + entry.options, 0);

const exercisedOn = (series: VestingChartSeries, date: string): number =>
  (series.exercises ?? [])
    .filter((exercise) => exercise.date <= date)
    .reduce((acc, exercise) => acc + exercise.count, 0);

export type VestingProgressPoint = { date: string; vested: number; exercised: number };

export const vestingProgressPoints = (
  series: VestingChartSeries,
  endDate: string,
): VestingProgressPoint[] =>
  [
    ...new Set([
      series.startDate,
      ...series.entries.map((entry) => entry.date),
      ...(series.exercises ?? []).map((exercise) => exercise.date),
      endDate,
    ]),
  ]
    .filter((date) => date >= series.startDate && date <= endDate)
    .sort()
    .map((date) => {
      const vested = vestedOn(series, date);
      return { date, vested, exercised: Math.min(exercisedOn(series, date), vested) };
    });

export type VestingExerciseMarker = { date: string; count: number; vested: number };

export const vestingExerciseMarkers = (series: VestingChartSeries): VestingExerciseMarker[] =>
  Object.entries(groupBy(series.exercises ?? [], (exercise) => exercise.date))
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([date, exercises]) => ({
      date,
      count: exercises.reduce((acc, exercise) => acc + exercise.count, 0),
      vested: vestedOn(series, date),
    }));

export type VestingChartSummary = { date: string; vested: number; remaining: number };

export const vestingChartSummary = (
  series: VestingChartSeries,
): VestingChartSummary | undefined => {
  const last = series.entries[series.entries.length - 1];
  if (last === undefined) {
    return undefined;
  }
  const vested = vestedOn(series, last.date);
  const exercised = (series.exercises ?? []).reduce((acc, exercise) => acc + exercise.count, 0);
  return { date: last.date, vested, remaining: Math.max(0, vested - exercised) };
};

export type VestingChartPoint = {
  date: string;
  vested: { seriesIndex: number; label: string; cumulative: number }[];
};

export const vestingChartPoints = (series: VestingChartSeries[]): VestingChartPoint[] => {
  const byDate = new Map<string, VestingChartPoint["vested"]>();
  series.forEach((s, seriesIndex) => {
    let cumulative = 0;
    for (const entry of s.entries) {
      cumulative += entry.options;
      byDate.set(entry.date, [
        ...(byDate.get(entry.date) ?? []).filter((v) => v.seriesIndex !== seriesIndex),
        { seriesIndex, label: s.label, cumulative },
      ]);
    }
  });
  return [...byDate.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([date, vested]) => ({ date, vested }));
};

export type OptionValuePoint = { date: string; amount: Amount; companyValue: Amount };

const fairValueOn = (
  fairValues: ShareValuationInformation[],
  shareTypeCode: ShareTypeCode | undefined,
  date: string,
): ShareValuationInformation | undefined =>
  fairValues
    .filter((fairValue) => fairValue.shareTypeCode === shareTypeCode && fairValue.date <= date)
    .reduce<ShareValuationInformation | undefined>(
      (latest, fairValue) =>
        latest === undefined || fairValue.date >= latest.date ? fairValue : latest,
      undefined,
    );

const companyValueOn = (
  fairValues: ShareValuationInformation[],
  date: string,
  currency: string,
): Amount => ({
  currency,
  value: [...new Set(fairValues.map((fairValue) => fairValue.shareTypeCode))]
    .map(
      (shareTypeCode) => fairValueOn(fairValues, shareTypeCode, date)?.companyTotalValue.value ?? 0,
    )
    .reduce((acc, value) => acc + value, 0),
});

export const prepHolderOptionValues = ({
  tranches,
  fairValues,
  shareTypes,
  today,
}: {
  tranches: TrancheWithPlan[];
  fairValues: ShareValuationInformation[];
  shareTypes: ShareType[];
  today: string;
}): OptionValuePoint[] => {
  const shareTypeCodeById = new Map(shareTypes.map((s) => [s.sharetypeId, s.shareTypeCode]));
  const valued = tranches.flatMap(({ tranche }) => {
    const shareTypeCode =
      tranche.shareTypeId === undefined ? undefined : shareTypeCodeById.get(tranche.shareTypeId);
    return shareTypeCode === undefined ? [] : [{ tranche, shareTypeCode }];
  });
  const dates = [
    ...new Set([
      ...valued.flatMap(({ tranche }) => [
        tranche.grantDate,
        ...(tranche.cancellations ?? []).map((c) => c.effectiveDate),
      ]),
      ...fairValues.map((fairValue) => fairValue.date),
      today,
    ]),
  ]
    .filter((date) => date <= today)
    .sort();
  return dates.flatMap((date) => {
    const amounts = valued.flatMap(({ tranche, shareTypeCode }) => {
      const fairValue =
        tranche.grantDate <= date ? fairValueOn(fairValues, shareTypeCode, date)?.value : undefined;
      return fairValue === undefined
        ? []
        : [{ ...fairValue, value: trancheNumbers(tranche, date).outstanding * fairValue.value }];
    });
    return amounts.length === 0
      ? []
      : [
          {
            date,
            amount: {
              currency: amounts[0].currency,
              value: amounts.reduce((acc, a) => acc + a.value, 0),
            },
            companyValue: companyValueOn(fairValues, date, amounts[0].currency),
          },
        ];
  });
};

export type HolderMilestone = VestingMilestone & { fractionOfTotal: number };

export type HolderMilestoneGroup = {
  id: string;
  program: TMessage;
  milestones: HolderMilestone[];
};

// Performance tranches have no time schedule; their milestones are listed
// separately from the vesting chart in the holder detail view.
export const prepHolderMilestones = (tranches: TrancheWithPlan[]): HolderMilestoneGroup[] =>
  tranches.flatMap(({ tranche, planName }) =>
    tranche.vesting.vestingType === "PERFORMANCE"
      ? [
          {
            id: tranche.optionTrancheId,
            program: programLabel(planName, tranche.name),
            milestones: tranche.vesting.milestones.map((milestone) => ({
              ...milestone,
              fractionOfTotal:
                tranche.totalOptions > 0 ? milestone.optionsAmount / tranche.totalOptions : 0,
            })),
          },
        ]
      : [],
  );

export type VestingTemplateSummary = {
  details: { label: TMessage; value: TMessage }[];
  dates: VestingScheduleEntry[];
  milestones?: HolderMilestoneGroup;
};

const durationLabel = (months: number): TMessage =>
  months > 0 && months % 12 === 0
    ? { key: "options.duration-years", params: { count: months / 12 } }
    : { key: "options.duration-months", params: { count: months } };

const fractionOf = (amount: number, total: number): number => (total > 0 ? amount / total : 0);

export const prepVestingTemplateSummary = (
  plan: OptionPlan,
): VestingTemplateSummary | undefined => {
  const vesting = plan.vestingTemplate;
  if (vesting === undefined) {
    return undefined;
  }
  const vestingType: VestingTemplateSummary["details"][number] = {
    label: { key: "options.vesting-type" },
    value: vestingTypeLabel(vesting),
  };
  const total = vestingTotal(vesting) ?? 0;
  switch (vesting.vestingType) {
    case "PERIODIC":
      return {
        details: [
          vestingType,
          { label: { key: "options.period" }, value: durationLabel(vesting.periodMonths) },
          {
            label: { key: "options.frequency" },
            value: { key: frequencyLabelKeys[vesting.frequency] },
          },
          { label: { key: "options.cliff" }, value: durationLabel(vesting.cliffMonths) },
        ],
        dates: [],
      };
    case "SPECIFIC_DATES":
      return {
        details: [vestingType],
        dates: vesting.entries
          .map((entry) => ({
            date: entry.vestingDate,
            options: entry.optionsAmount,
            fractionOfTotal: fractionOf(entry.optionsAmount, total),
          }))
          .sort((a, b) => a.date.localeCompare(b.date)),
      };
    case "PERFORMANCE":
      return {
        details: [vestingType],
        dates: [],
        milestones: {
          id: plan.optionPlanId,
          program: { translated: plan.name },
          milestones: vesting.milestones.map((milestone) => ({
            ...milestone,
            fractionOfTotal: fractionOf(milestone.optionsAmount, total),
          })),
        },
      };
  }
};

type GrantTerms = {
  planId: string;
  shareTypeId?: number;
  totalOptions: number;
  strikePrice: number;
  grantDate: string;
  expirationDate: string;
};

const amount = (value: number): TMessageParam => ({ type: "amount", value, currency: "NOK" });

// One clause per changed grant term, formatted as "old → new"; unchanged terms
// are omitted so the row only carries what the modification actually touched.
const grantChangeClauses = (
  prev: GrantTerms,
  next: GrantTerms,
  planNameById: Map<string, string>,
  shareTypeById: Map<number, ShareType>,
): TMessage[] => {
  const clauses: TMessage[] = [];
  if (prev.totalOptions !== next.totalOptions) {
    clauses.push({
      key: "options.history-change-options",
      params: {
        change: fromTo(
          { type: "number", value: prev.totalOptions },
          {
            type: "number",
            value: next.totalOptions,
          },
        ),
      },
    });
  }
  if (prev.strikePrice !== next.strikePrice) {
    clauses.push({
      key: "options.history-change-strike",
      params: { change: fromTo(amount(prev.strikePrice), amount(next.strikePrice)) },
    });
  }
  if (prev.grantDate !== next.grantDate) {
    clauses.push({
      key: "options.history-change-grant-date",
      params: {
        change: fromTo(
          { type: "date", value: prev.grantDate },
          { type: "date", value: next.grantDate },
        ),
      },
    });
  }
  if (prev.expirationDate !== next.expirationDate) {
    clauses.push({
      key: "options.history-change-expiry",
      params: {
        change: fromTo(
          { type: "date", value: prev.expirationDate },
          { type: "date", value: next.expirationDate },
        ),
      },
    });
  }
  if (prev.shareTypeId !== next.shareTypeId) {
    clauses.push({
      key: "options.history-change-share-type",
      params: {
        change: fromTo(
          shareTypeClause(prev.shareTypeId, shareTypeById),
          shareTypeClause(next.shareTypeId, shareTypeById),
        ),
      },
    });
  }
  if (prev.planId !== next.planId) {
    clauses.push({
      key: "options.history-change-program",
      params: {
        change: fromTo(
          { translated: planNameById.get(prev.planId) ?? "–" },
          { translated: planNameById.get(next.planId) ?? "–" },
        ),
      },
    });
  }
  return clauses;
};

const registeredDescription = (
  payload: Extract<InstrumentEventPayload, { payloadType: "GRANT_REGISTERED" }>,
  program: string,
  shareTypeById: Map<number, ShareType>,
): TMessage => {
  const details: (TMessage | TMessageParam | string)[] = [program];
  const shareType =
    payload.shareTypeId === undefined ? undefined : shareTypeById.get(payload.shareTypeId);
  if (shareType) {
    details.push({ type: "shareType", value: shareType });
  }
  details.push({ key: "options.history-strike", params: { strike: amount(payload.strikePrice) } });
  return {
    key: "options.history-grant-description",
    params: {
      count: { type: "number", value: payload.totalOptions },
      details: { type: "list", value: details, separator: " · " },
    },
  };
};

const modificationDescription = (changes: TMessage[], program: string): TMessage =>
  changes.length === 0
    ? { key: "options.history-modification-generic", params: { program } }
    : {
        key: "options.history-modification-description",
        params: { program, changes: { type: "list", value: changes, separator: " · " } },
      };

const requestStatusHistory: Record<ExerciseRequestStatus, { icon: IconName; key: TMessageKey }> = {
  REQUESTED: { icon: "paper-plane", key: "options.history-exercise-requested" },
  ACKNOWLEDGED: { icon: "check-square", key: "options.history-exercise-request-acknowledged" },
  REJECTED: { icon: "x-circle", key: "options.history-exercise-request-rejected" },
  WITHDRAWN: { icon: "x-circle", key: "options.history-exercise-request-withdrawn" },
  DELETED: { icon: "trash", key: "options.history-exercise-request-deleted" },
  EXERCISED: { icon: "check-circle", key: "options.history-exercise" },
};

const historyRowForEvent = (
  event: InstrumentEvent,
  program: string,
  prevTerms: GrantTerms | undefined,
  planNameById: Map<string, string>,
  shareTypeById: Map<number, ShareType>,
): HistoryRow<GrantTerms> => {
  const base = { id: event.instrumentEventId, date: event.recordedAt };
  const payload = event.payload;
  switch (payload.payloadType) {
    case "GRANT_REGISTERED":
      return {
        entry: {
          ...base,
          icon: "file-plus",
          event: { key: "options.history-grant-registered" },
          description: registeredDescription(payload, program, shareTypeById),
        },
        terms: payload,
      };
    case "GRANT_MODIFIED": {
      const changes = prevTerms
        ? grantChangeClauses(prevTerms, payload, planNameById, shareTypeById)
        : [];
      return {
        entry: {
          ...base,
          icon: "edit",
          event: { key: "options.history-modification" },
          description: modificationDescription(changes, program),
        },
        terms: payload,
      };
    }
    case "VESTING_MODIFIED":
      return {
        entry: {
          ...base,
          icon: "edit",
          event: { key: "options.history-modification" },
          description: {
            key: "options.history-vesting-modified-description",
            params: { program, schedule: vestingTypeLabel(payload.vesting) },
          },
        },
        terms: prevTerms,
      };
    case "CANCELLATION_REGISTERED":
      return {
        entry: {
          ...base,
          icon: "file-minus",
          event: { key: "options.history-cancellation" },
          description: {
            key: "options.history-cancellation-description",
            params: {
              program,
              count: { type: "number", value: payload.vestedCancelled + payload.unvestedCancelled },
              reason: { key: cancellationReasonLabelKeys[payload.reason] },
            },
          },
        },
        terms: prevTerms,
      };
    case "ACCELERATION_REGISTERED":
      return {
        entry: {
          ...base,
          icon: "fast-forward",
          event: { key: "options.history-acceleration" },
          description: {
            key: "options.history-acceleration-description",
            params: {
              program,
              count: { type: "number", value: payload.unvestedAccelerated },
            },
          },
        },
        terms: prevTerms,
      };
    case "EXERCISE_REGISTERED":
      return {
        entry: {
          ...base,
          icon: "check-circle",
          event: { key: "options.history-exercise" },
          description: {
            key: "options.history-exercise-description",
            params: {
              program,
              count: { type: "number", value: payload.count },
              receiver: payload.receiverName ?? "–",
            },
          },
        },
        terms: prevTerms,
      };
    case "EXERCISE_REVERSED":
      return {
        entry: {
          ...base,
          icon: "arrow-counter-clockwise",
          event: { key: "options.history-exercise-reversed" },
          description: {
            key: "options.history-exercise-reversed-description",
            params: { program },
          },
        },
        terms: prevTerms,
      };
    case "EXERCISE_REQUESTED":
      return {
        entry: {
          ...base,
          icon: requestStatusHistory.REQUESTED.icon,
          event: { key: requestStatusHistory.REQUESTED.key },
          description: {
            key: "options.history-exercise-requested-description",
            params: { program, count: { type: "number", value: payload.count } },
          },
        },
        terms: prevTerms,
      };
    case "EXERCISE_REQUEST_MODIFIED":
      return {
        entry: {
          ...base,
          icon: "edit",
          event: { key: "options.history-exercise-request-modified" },
          description: {
            key: "options.history-exercise-requested-description",
            params: { program, count: { type: "number", value: payload.count } },
          },
        },
        terms: prevTerms,
      };
    case "EXERCISE_REQUEST_STATUS_CHANGED":
      return {
        entry: {
          ...base,
          icon: requestStatusHistory[payload.status].icon,
          event: { key: requestStatusHistory[payload.status].key },
          description: { translated: program },
        },
        terms: prevTerms,
      };
  }
};

// History reads the append-only ledger: one row per event. Per tranche, events
// are folded in append order so each modification can be diffed against the
// terms established by the preceding event; rows then sort newest-first.
export const prepHolderHistory = (
  holderId: string,
  grants: OptionGrantDetails[],
  plans: OptionPlan[],
  shareTypes: ShareType[],
): HolderHistoryEntry[] => {
  const planNameById = new Map(plans.map((p) => [p.optionPlanId, p.name]));
  const shareTypeById = new Map(shareTypes.map((s) => [s.sharetypeId, s]));
  return grants
    .filter((details) => details.grant.holderId === holderId)
    .flatMap((details) => {
      const program = planNameById.get(details.grant.planId) ?? "–";
      const byTranche = groupBy(
        (details.events ?? []).filter((event) => event.trancheId !== undefined),
        (event) => event.trancheId,
      );
      return Object.values(byTranche).flatMap((trancheEvents) =>
        foldHistory<InstrumentEvent, GrantTerms>(trancheEvents, (event, terms) =>
          historyRowForEvent(event, program, terms, planNameById, shareTypeById),
        ),
      );
    })
    .sort((a, b) => b.date.localeCompare(a.date));
};
